import React, { useState, useEffect, useCallback, useMemo } from "react";

const API = process.env.REACT_APP_API || "http://localhost:3001";

/* ══════════════════════════════════════════════
   UTILS
   ══════════════════════════════════════════════ */

const getToken = () => sessionStorage.getItem("controla_admin_token");
const getSession = () => {
  try { return JSON.parse(sessionStorage.getItem("controla_admin_session")); } catch { return null; }
};
const clearSession = () => { sessionStorage.removeItem("controla_admin_token"); sessionStorage.removeItem("controla_admin_session"); };

async function apiFetch(path, opts = {}) {
  const token = getToken();
  const res = await fetch(API + path, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    method: opts.method || "GET",
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) { clearSession(); window.location.reload(); throw new Error("Sessão expirada"); }
    throw new Error(data.error || "Erro na requisição");
  }
  return data;
}

const fmt = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v);
  const d = s.includes("T") ? s.split("T")[0] : s;
  if (d.length === 10 && d.includes("-")) { const [y, m, day] = d.split("-"); return day + "/" + m + "/" + y; }
  return s;
};

// ─── Consulta CNPJ via BrasilAPI (gratuita) ─────────────────
async function buscarCNPJ(cnpj) {
  const doc = (cnpj || "").replace(/\D/g, "");
  if (doc.length !== 14) throw new Error("CNPJ deve ter 14 dígitos");
  const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${doc}`);
  if (!res.ok) throw new Error("CNPJ não encontrado");
  const d = await res.json();
  return {
    razao_social: d.razao_social || "",
    nome_fantasia: d.nome_fantasia || "",
    endereco: [d.logradouro, d.numero, d.complemento].filter(Boolean).join(", "),
    bairro: d.bairro || "",
    cidade: d.municipio || "",
    uf: d.uf || "",
    cep: d.cep || "",
    telefone: d.ddd_telefone_1 || "",
    email: d.email || "",
    situacao: d.descricao_situacao_cadastral || "",
  };
}

const statusColors = { ativo: "#2ecc71", trial: "#3498db", suspenso: "#f39c12", cancelado: "#e74c3c" };
const statusLabels = { ativo: "Ativo", trial: "Trial", suspenso: "Suspenso", cancelado: "Cancelado" };
const planoLabels = { basico: "Básico", controla: "Controla", controla_plus: "Controla Plus", base: "Básico", operacao: "Controla", escala: "Controla Plus" };
const planoPrecos = { basico: "R$ 120/mês", controla: "R$ 200/mês", controla_plus: "R$ 300/mês", base: "R$ 120/mês", operacao: "R$ 200/mês", escala: "R$ 300/mês" };
const planoStorage = { basico: 0, controla: 2147483648, controla_plus: 2147483648, base: 0, operacao: 2147483648, escala: 2147483648 };
const planoMaxLoc = { basico: 70, controla: 200, controla_plus: 0, base: 70, operacao: 200, escala: 0 };
const planoFeatures = {
  basico: { anexos: false, rota: true, maxLoc: 70, appMot: false, maxMot: 0 },
  controla: { anexos: true, rota: true, maxLoc: 200, appMot: true, maxMot: 5 },
  controla_plus: { anexos: true, rota: true, maxLoc: 0, appMot: true, maxMot: 15 },
  base: { anexos: false, rota: true, maxLoc: 70, appMot: false, maxMot: 0 },
  operacao: { anexos: true, rota: true, maxLoc: 200, appMot: true, maxMot: 5 },
  escala: { anexos: true, rota: true, maxLoc: 0, appMot: true, maxMot: 15 },
};
const fmtStorage = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return gb.toFixed(gb >= 10 ? 0 : 1) + " GB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return mb.toFixed(0) + " MB";
  const kb = bytes / 1024;
  return kb.toFixed(0) + " KB";
};

/* ══════════════════════════════════════════════
   GLOBAL STYLES
   ══════════════════════════════════════════════ */

const GlobalStyles = () => (
  <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', -apple-system, sans-serif; background: #f0f2f5; color: #333; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #1e2330; }
    ::-webkit-scrollbar-thumb { background: #4a4a6a; border-radius: 3px; }
    input, select, textarea {
      background: #f5f7fa; border: 1px solid #dde1e6; color: #333;
      padding: 10px 14px; border-radius: 8px; font-size: 14px; width: 100%;
      transition: border-color .2s; font-family: inherit;
    }
    input:focus, select:focus, textarea:focus { outline: none; border-color: #1aae9e; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: scale(1); } }

    /* ═══ MOBILE — painel admin estilo app (≤768px). Desktop intocado. ═══ */
    @media (max-width: 768px) {
      html, body { -webkit-tap-highlight-color: transparent; }

      /* shell: sidebar vira drawer, hamburguer aparece */
      .adm-sidebar { display: none !important; }
      .adm-sidebar.open { display: flex !important; width: 80vw !important; max-width: 300px; z-index: 9990 !important; box-shadow: 4px 0 28px rgba(0,0,0,.4); border-radius: 0 18px 18px 0; animation: admDrawer .22s ease; overflow-y: auto; }
      .adm-overlay { display: block !important; position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 9989; }
      .adm-burger { display: flex !important; }
      .adm-main { margin-left: 0 !important; padding: 64px 12px calc(24px + env(safe-area-inset-bottom)) 12px !important; }
      @keyframes admDrawer { from { transform: translateX(-35%); opacity: .4; } to { transform: none; opacity: 1; } }

      /* toque */
      input, select, textarea { font-size: 16px !important; min-height: 44px; }

      /* login tela cheia */
      .adm-login-box { width: 100% !important; min-height: 100vh; min-height: 100dvh; border-radius: 0 !important; padding: 40px 26px !important; display: flex; flex-direction: column; justify-content: center; box-sizing: border-box; }
      .adm-login-box img { width: 190px !important; }
      .adm-login-box button { min-height: 48px; border-radius: 12px !important; }

      /* modal = bottom sheet */
      .adm-modal-overlay { align-items: flex-end !important; padding: 0 !important; }
      .adm-modal-box { width: 100% !important; max-width: 100vw !important; border-radius: 18px 18px 0 0 !important; max-height: 92vh !important; max-height: 92dvh !important; animation: admSheet .24s ease !important; }
      .adm-modal-box::before { content: ""; display: block; width: 42px; height: 4px; border-radius: 2px; background: #ddd; margin: 8px auto 0; }
      .adm-modal-head { padding: 10px 16px !important; }
      .adm-modal-body { padding: 16px 14px calc(16px + env(safe-area-inset-bottom)) !important; }
      .adm-modal-body div[style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
      .adm-modal-body button { min-height: 44px; }
      @keyframes admSheet { from { transform: translateY(48px); opacity: .5; } to { transform: none; opacity: 1; } }

      /* grids de KPI/conteúdo empilham */
      .adm-main div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
      .adm-main div[style*="grid-template-columns: repeat(4"], .adm-main div[style*="grid-template-columns: repeat(5"] { grid-template-columns: 1fr 1fr !important; }

      /* tabelas viram cards */
      .mob-cards { min-width: 0 !important; }
      .mob-cards, .mob-cards tbody { display: block; }
      .mob-cards thead { display: none !important; }
      .mob-cards tbody tr { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 2px 12px; position: relative; padding: 12px 14px !important; margin: 0 0 10px; background: #fff !important; border: 1px solid #ececec !important; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
      .mob-cards td { display: block; border: none !important; padding: 2px 0 !important; font-size: 13px !important; max-width: none !important; white-space: normal !important; text-align: left !important; }
      .mob-cards .mob-title { order: -2; width: 100%; font-size: 14px !important; }
      .mob-cards .mob-check { order: -3; width: auto; padding-right: 8px !important; }
      .mob-cards .mob-hide { display: none !important; }
      .mob-cards td[data-label] { display: flex; flex-direction: column; width: calc(50% - 8px); margin-top: 6px; }
      .mob-cards td[data-label]::before { content: attr(data-label); font-size: 9px; font-weight: 800; color: #9aa0a6; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 1px; }
      .mob-cards .mob-actions { width: 100% !important; margin-top: 10px; }
      .mob-cards .mob-actions > div { display: flex !important; gap: 8px !important; }
      .mob-cards .mob-actions button { flex: 1; min-height: 40px; border-radius: 10px !important; font-size: 13px !important; }
      .mob-cards td[colspan] { position: static; width: 100%; }

      /* wrappers de tabela com min-width inline deixam de travar */
      .adm-main table[style*="min-width"], .adm-main table[style*="minWidth"] { min-width: 0 !important; }
      .adm-main div[style*="overflow-x"] { overflow-x: visible !important; }
    }
  `}</style>
);

/* ══════════════════════════════════════════════
   LOGIN PAGE
   ══════════════════════════════════════════════ */

function LoginPage({ onLogin }) {
  // modo: "login" | "forgot" | "reset"
  const [modo, setModo] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("reset") ? "reset" : "login";
  });
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmSenha, setConfirmSenha] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [resposta, setResposta] = useState("");

  const resetToken = new URLSearchParams(window.location.search).get("reset") || "";

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro(""); setLoading(true);
    try {
      const data = await apiFetch("/api/admin/login", { method: "POST", body: { email, senha } });
      sessionStorage.setItem("controla_admin_token", data.token);
      sessionStorage.setItem("controla_admin_session", JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setErro(""); setSucesso(""); setLoading(true);
    try {
      await apiFetch("/api/admin/forgot-password", { method: "POST", body: { email, resposta } });
      setSucesso("Se o email estiver cadastrado, você receberá um link para redefinir sua senha.");
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setErro(""); setSucesso(""); setLoading(true);
    if (novaSenha !== confirmSenha) { setErro("As senhas não conferem"); setLoading(false); return; }
    if (novaSenha.length < 6) { setErro("Senha deve ter no mínimo 6 caracteres"); setLoading(false); return; }
    try {
      const data = await apiFetch("/api/admin/reset-password", { method: "POST", body: { token: resetToken, novaSenha } });
      setSucesso(data.msg || "Senha redefinida com sucesso!");
      // Limpar token da URL
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => { setModo("login"); setSucesso(""); }, 3000);
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const goTo = (m) => { setModo(m); setErro(""); setSucesso(""); };

  const inputStyle = { background: "#f5f7fa", border: "1px solid #dde1e6", color: "#333", padding: "12px 14px", borderRadius: 10, fontSize: 14, width: "100%" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666", marginBottom: 6, textTransform: "uppercase" };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#1a1a2e", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "url(/painel.png)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* ── LOGIN ── */}
      {modo === "login" && (
        <form onSubmit={handleLogin} className="adm-login-box" style={{ position: "relative", zIndex: 2, background: "#fff", borderRadius: 20, padding: "50px 40px", width: 420, boxShadow: "0 20px 60px rgba(0,0,0,.3)", animation: "slideIn .4s ease" }}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <img src="/logo-full.png" alt="Logo" style={{ width: 220, margin: "0 auto 20px", objectFit: "contain", display: "block" }} />
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#222", margin: "6px 0 4px" }}>Painel de Administração</h1>
            <p style={{ color: "#888", fontSize: 13 }}>Gestão de Empresas</p>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email</label>
            <input type="email" placeholder="Digite seu email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14, position: "relative" }}>
            <label style={labelStyle}>Senha</label>
            <input type={showPw ? "text" : "password"} placeholder="Digite sua senha" value={senha} onChange={e => setSenha(e.target.value)} required style={inputStyle} />
            <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 12, top: 36, background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 16 }}>{showPw ? "🙈" : "👁️"}</button>
          </div>
          <div style={{ textAlign: "right", marginBottom: 20 }}>
            <button type="button" onClick={() => goTo("forgot")} style={{ background: "none", border: "none", color: "#1aae9e", fontSize: 13, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Esqueceu a senha?</button>
          </div>
          {erro && <div style={{ background: "rgba(231,76,60,.1)", border: "1px solid rgba(231,76,60,.2)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{erro}</div>}
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #1aae9e, #158f82)", color: "#fff", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", opacity: loading ? .7 : 1, fontFamily: "inherit" }}>
            {loading ? "Entrando..." : "Entrar no Painel"}
          </button>
        </form>
      )}

      {/* ── FORGOT PASSWORD ── */}
      {modo === "forgot" && (
        <form onSubmit={handleForgot} style={{ position: "relative", zIndex: 2, background: "#fff", borderRadius: 20, padding: "50px 40px", width: 420, boxShadow: "0 20px 60px rgba(0,0,0,.3)", animation: "slideIn .4s ease" }}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg, #f0fdfa, #e6fffa)", border: "2px solid #1aae9e", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>🔐</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#222", margin: "6px 0 4px" }}>Esqueceu a senha?</h1>
            <p style={{ color: "#888", fontSize: 13, lineHeight: 1.5 }}>Informe seu email e enviaremos um link para redefinir sua senha.</p>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email</label>
            <input type="email" placeholder="Digite seu email de admin" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Pergunta de Segurança</label>
            <p style={{ color: "#666", fontSize: 12, marginBottom: 8, lineHeight: 1.4 }}>Qual o nome do servidor de SAMP que mais jogamos?</p>
            <input type="text" placeholder="Resposta" value={resposta} onChange={e => setResposta(e.target.value)} required style={inputStyle} autoComplete="off" />
          </div>
          {erro && <div style={{ background: "rgba(231,76,60,.1)", border: "1px solid rgba(231,76,60,.2)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{erro}</div>}
          {sucesso && <div style={{ background: "rgba(46,204,113,.1)", border: "1px solid rgba(46,204,113,.2)", color: "#059669", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center", lineHeight: 1.5 }}>{sucesso}</div>}
          <button type="submit" disabled={loading || !!sucesso} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #1aae9e, #158f82)", color: "#fff", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", opacity: (loading || sucesso) ? .7 : 1, fontFamily: "inherit", marginBottom: 14 }}>
            {loading ? "Enviando..." : "Enviar Link de Recuperação"}
          </button>
          <div style={{ textAlign: "center" }}>
            <button type="button" onClick={() => goTo("login")} style={{ background: "none", border: "none", color: "#1aae9e", fontSize: 13, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Voltar ao login</button>
          </div>
        </form>
      )}

      {/* ── RESET PASSWORD ── */}
      {modo === "reset" && (
        <form onSubmit={handleReset} style={{ position: "relative", zIndex: 2, background: "#fff", borderRadius: 20, padding: "50px 40px", width: 420, boxShadow: "0 20px 60px rgba(0,0,0,.3)", animation: "slideIn .4s ease" }}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg, #f0fdfa, #e6fffa)", border: "2px solid #1aae9e", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>🔑</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#222", margin: "6px 0 4px" }}>Nova Senha</h1>
            <p style={{ color: "#888", fontSize: 13 }}>Crie uma nova senha para sua conta admin.</p>
          </div>
          <div style={{ marginBottom: 18, position: "relative" }}>
            <label style={labelStyle}>Nova Senha</label>
            <input type={showPw ? "text" : "password"} placeholder="Mínimo 6 caracteres" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} required minLength={6} style={inputStyle} />
            <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 12, top: 36, background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 16 }}>{showPw ? "🙈" : "👁️"}</button>
          </div>
          <div style={{ marginBottom: 24, position: "relative" }}>
            <label style={labelStyle}>Confirmar Senha</label>
            <input type={showPw2 ? "text" : "password"} placeholder="Repita a nova senha" value={confirmSenha} onChange={e => setConfirmSenha(e.target.value)} required minLength={6} style={inputStyle} />
            <button type="button" onClick={() => setShowPw2(!showPw2)} style={{ position: "absolute", right: 12, top: 36, background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 16 }}>{showPw2 ? "🙈" : "👁️"}</button>
          </div>
          {erro && <div style={{ background: "rgba(231,76,60,.1)", border: "1px solid rgba(231,76,60,.2)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{erro}</div>}
          {sucesso && <div style={{ background: "rgba(46,204,113,.1)", border: "1px solid rgba(46,204,113,.2)", color: "#059669", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{sucesso}</div>}
          <button type="submit" disabled={loading || !!sucesso} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #1aae9e, #158f82)", color: "#fff", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", opacity: (loading || sucesso) ? .7 : 1, fontFamily: "inherit", marginBottom: 14 }}>
            {loading ? "Salvando..." : "Redefinir Senha"}
          </button>
          <div style={{ textAlign: "center" }}>
            <button type="button" onClick={() => { window.history.replaceState({}, "", window.location.pathname); goTo("login"); }} style={{ background: "none", border: "none", color: "#1aae9e", fontSize: 13, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Voltar ao login</button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   TOAST
   ══════════════════════════════════════════════ */

function ToastContainer({ toasts }) {
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999, display: "flex", flexDirection: "column", gap: 10 }}>
      {toasts.map(t => (
        <div key={t.id} style={{ background: "#1a1a2e", border: `1px solid ${t.color || "#1aae9e"}44`, borderRadius: 12, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 30px rgba(0,0,0,.4)", animation: "fadeIn .3s ease", minWidth: 280 }}>
          <span style={{ fontSize: 22 }}>{t.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>{t.title}</div>
            {t.sub && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{t.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SIDEBAR
   ══════════════════════════════════════════════ */

function Sidebar({ page, setPage, admin, onLogout, mobileOpen }) {
  const items = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "reunioes", icon: "📅", label: "Reuniões" },
    { id: "empresas", icon: "🏢", label: "Empresas" },
    { id: "usuarios", icon: "👤", label: "Usuários" },
    { id: "contratos", icon: "📄", label: "Contratos" },
    { id: "assinaturas", icon: "💰", label: "Assinaturas" },
    { id: "calendario", icon: "🗓️", label: "Calendário" },
    { id: "propostas", icon: "📋", label: "Propostas" },
    { id: "financeiro", icon: "💹", label: "Financeiro" },
    { id: "mapa", icon: "🗺️", label: "Mapa Operacional" },
    { id: "monitoramento", icon: "👁️", label: "Monitoramento" },
    { id: "modulos", icon: "🧩", label: "Módulos Adicionais" },
    { id: "ia", icon: "🤖", label: "Perguntas IA" },
    { id: "emails", icon: "📧", label: "Emails Enviados" },
  ];

  return (
    <div className={"adm-sidebar" + (mobileOpen ? " open" : "")} style={{ width: 214, background: "#1e2330", display: "flex", flexDirection: "column", height: "100vh", position: "fixed", left: 0, top: 0, zIndex: 100 }}>
      <div style={{ height: 56, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 12px", borderBottom: "1px solid #2a3040", background: "#fff", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <img src="/logo-icon.png" alt="CC" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#1e2d4f", fontWeight: 800, fontSize: 13, lineHeight: 1.2, whiteSpace: "nowrap" }}>{"Controla"} <span style={{ color: "#1aae9e" }}>{"Caçambas"}</span></div>
            <div style={{ color: "#9ca3af", fontSize: 9, fontWeight: 500, lineHeight: 1.2, marginTop: 1 }}>Painel de Administração</div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, padding: "12px 10px" }}>
        {items.map(item => (
          <button key={item.id} onClick={() => setPage(item.id)} style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 14px",
            background: page === item.id ? "rgba(26,174,158,.18)" : "transparent",
            border: page === item.id ? "1px solid rgba(26,174,158,.3)" : "1px solid transparent",
            borderRadius: 10, cursor: "pointer", color: page === item.id ? "#1aae9e" : "#8888aa",
            fontSize: 14, fontWeight: page === item.id ? 700 : 500, marginBottom: 4, transition: "all .2s", fontFamily: "inherit",
          }}>
            <span style={{ fontSize: 18 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
      <div style={{ padding: "16px", borderTop: "1px solid #2a3040" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1aae9e", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, color: "#fff" }}>
            {(admin?.nome?.[0] || "A").toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#fff" }}>{admin?.nome || "Admin"}</div>
            <div style={{ fontSize: 11, color: "#888" }}>Super Admin</div>
          </div>
        </div>
        <button onClick={onLogout} style={{ width: "100%", padding: "8px", background: "rgba(26,174,158,.1)", border: "1px solid rgba(26,174,158,.2)", borderRadius: 8, color: "#1aae9e", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Sair
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   COMPONENTS
   ══════════════════════════════════════════════ */

function StatusBadge({ status }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${statusColors[status] || "#666"}22`, border: `1px solid ${statusColors[status] || "#666"}44`, color: statusColors[status] || "#666", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColors[status] || "#666" }} />
      {statusLabels[status] || status}
    </span>
  );
}

/* ──── Popup de Sucesso com Animação ──── */
function SuccessPopup({ titulo, subtitulo, detalhes, onClose }) {
  const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 50); }, []);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>
      <div style={{ background:"#fff", borderRadius:16, width:420, maxWidth:"92vw", padding:"40px 32px 32px", textAlign:"center", transform: show ? "scale(1)" : "scale(.7)", opacity: show ? 1 : 0, transition:"all .4s cubic-bezier(.34,1.56,.64,1)", boxShadow:"0 20px 60px rgba(0,0,0,.2)" }}>
        {/* Checkmark animado */}
        <div style={{ width:72, height:72, borderRadius:"50%", background:"linear-gradient(135deg, #2ecc71, #27ae60)", margin:"0 auto 20px", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 8px 24px rgba(46,204,113,.35)", animation: show ? "pulseSuccess 1.2s ease" : "none" }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ strokeDasharray:30, strokeDashoffset: show ? 0 : 30, transition:"stroke-dashoffset .6s ease .3s" }}>
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 style={{ margin:"0 0 6px", fontSize:20, fontWeight:800, color:"#1a1a1a" }}>{titulo}</h2>
        <p style={{ margin:"0 0 20px", fontSize:13, color:"#888", lineHeight:1.5 }}>{subtitulo}</p>
        {detalhes && (
          <div style={{ background:"#f0faf8", border:"1px solid #d1fae5", borderRadius:10, padding:"14px 18px", marginBottom:20, textAlign:"left" }}>
            {detalhes.map((d, i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom: i < detalhes.length - 1 ? "1px solid #e6f7f1" : "none" }}>
                <span style={{ fontSize:12, color:"#666", fontWeight:600 }}>{d.label}</span>
                <span style={{ fontSize:12, color:"#111", fontWeight:700 }}>{d.value}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:20 }}>
          <div style={{ fontSize:12, color:"#92400e", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:15 }}>📧</span> Email de boas-vindas enviado com sucesso!
          </div>
        </div>
        <button onClick={onClose} style={{ background:"linear-gradient(135deg, #1aae9e, #158f82)", color:"#fff", border:"none", borderRadius:10, padding:"12px 40px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>
          OK
        </button>
      </div>
      <style>{`
        @keyframes pulseSuccess { 0% { transform:scale(.5); opacity:0; } 50% { transform:scale(1.15); } 100% { transform:scale(1); opacity:1; } }
      `}</style>
    </div>
  );
}

function Modal({ title, onClose, children, width }) {
  return (
    <div onClick={onClose} className="adm-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} className="adm-modal-box" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, width: width || 520, maxWidth: "calc(100vw - 40px)", maxHeight: "90vh", overflow: "auto", animation: "slideIn .3s ease" }}>
        <div className="adm-modal-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#222" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#999", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div className="adm-modal-body" style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 6, textTransform: "uppercase" };
const fieldStyle = { marginBottom: 14 };

/* ══════════════════════════════════════════════
   DASHBOARD
   ══════════════════════════════════════════════ */

function Dashboard({ stats, loading: statsLoading }) {
  const [analytics, setAnalytics] = useState(null);
  const [periodo, setPeriodo] = useState("30d");
  const [loadingA, setLoadingA] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthDetailBucket, setHealthDetailBucket] = useState(null); // null | "atencao" | "risco" | "critico"

  useEffect(() => {
    setLoadingA(true);
    apiFetch("/api/admin/analytics?periodo=" + periodo)
      .then(setAnalytics)
      .catch(() => {})
      .finally(() => setLoadingA(false));
  }, [periodo]);

  // Health score (não depende do filtro de período — é estado atual)
  useEffect(() => {
    apiFetch("/api/admin/analytics/health")
      .then(setHealth)
      .catch(() => {});
  }, []);

  // Reengajamento via WhatsApp — mensagem adaptada ao bucket
  const reengajarWhatsApp = (tenant, bucket) => {
    const numero = telToWhatsApp(tenant.tel || "");
    if (!numero) {
      alert("Esta empresa não tem telefone cadastrado.\nAtualize na ficha de Cobrança antes de enviar pelo WhatsApp.");
      return;
    }
    const primeiroNome = (tenant.nome || "").split(" ")[0] || tenant.nome || "";
    const sd = saudacaoAgora();
    const dias = tenant.dias_sem_login;
    const nuncaLogou = tenant.nunca_logou;

    let mensagem = "";
    if (nuncaLogou) {
      // Nunca logou — onboarding falhou
      mensagem = [
        `${sd}, ${primeiroNome}!`,
        ``,
        `Vi aqui que sua empresa está cadastrada no *Controla Caçambas*, mas você ainda não acessou o sistema.`,
        ``,
        `Posso te ajudar com o primeiro acesso? Em 15 minutos te mostro como cadastrar caçambas, criar locação e usar o mapa operacional.`,
        ``,
        `Me avisa um horário que prefere e eu te ligo. Vamos juntos!`,
      ].join("\n");
    } else if (bucket === "atencao") {
      // 8-14 dias — leve, informal
      mensagem = [
        `${sd}, ${primeiroNome}! Tudo bem?`,
        ``,
        `Notei que você ficou alguns dias sem acessar o *Controla Caçambas*. Tá tudo certo por aí?`,
        ``,
        `Se precisar de ajuda em algo do sistema — relatório, cobrança, mapa — me chama que eu resolvo rapidinho.`,
      ].join("\n");
    } else if (bucket === "risco") {
      // 15-30 dias — preocupado mas acolhedor
      mensagem = [
        `${sd}, ${primeiroNome}!`,
        ``,
        `Faz cerca de ${dias} dias que ninguém da sua empresa acessa o *Controla Caçambas* e fiquei preocupado se está faltando algo.`,
        ``,
        `Tem alguma dificuldade no sistema que eu posso resolver? Posso fazer um treinamento rápido com sua equipe sem custo, ou te ajudar a configurar algo específico.`,
        ``,
        `Me responde aqui que a gente conversa.`,
      ].join("\n");
    } else {
      // crítico (31+ dias)
      mensagem = [
        `${sd}, ${primeiroNome}!`,
        ``,
        `Faz mais de um mês que sua empresa não acessa o *Controla Caçambas*. Antes de qualquer coisa, queria entender se está tudo certo aí.`,
        ``,
        `Se o sistema não está atendendo bem, ou se mudaram a forma de trabalhar, me conta. Posso ajustar algo, fazer um novo treinamento ou conversar sobre o plano.`,
        ``,
        `O que você prefere?`,
      ].join("\n");
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const loading = statsLoading || loadingA;
  if (loading && !analytics) return <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div>;

  const s = stats || {};
  const a = analytics || {};

  const periodos = [
    { key: "hoje", label: "Hoje" },
    { key: "7d", label: "7 dias" },
    { key: "30d", label: "30 dias" },
    { key: "90d", label: "90 dias" },
  ];

  // Cards principais
  const cards1 = [
    { icon: "📦", label: "Locações Hoje", value: a.locacoesHoje || 0, color: "#1aae9e" },
    { icon: "💰", label: "Faturamento Hoje", value: fmt(a.faturamentoHoje || 0), color: "#2ecc71" },
    { icon: "🟢", label: "Usuários Online", value: a.usuariosAtivos || 0, color: "#3498db" },
    { icon: "💵", label: "MRR Estimado", value: fmt(a.mrr || 0), color: "#9b59b6" },
  ];
  const cards2 = [
    { icon: "🏢", label: "Empresas Ativas", value: s.tenants?.ativos || 0, color: "#2ecc71" },
    { icon: "🧪", label: "Em Trial", value: s.tenants?.trial || 0, color: "#3498db" },
    { icon: "⏸️", label: "Suspensos", value: s.tenants?.suspensos || 0, color: "#f39c12" },
    { icon: "📦", label: "Total Locações", value: s.locacoes || 0, color: "#9b59b6" },
    { icon: "👥", label: "Total Usuários", value: s.usuarios || 0, color: "#1abc9c" },
    { icon: "💰", label: "Faturamento Global", value: fmt(s.faturamento || 0), color: "#f1c40f" },
    { icon: "🆕", label: "Novos no Período", value: a.novosTenantsPeriodo || 0, color: "#e67e22" },
    { icon: "❌", label: "Cancelados", value: s.tenants?.cancelados || 0, color: "#e74c3c" },
  ];

  const cardStyle = (color) => ({
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
    padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)",
    borderLeft: `4px solid ${color}`,
  });

  // Chart: locações por dia
  const chartData = a.locacoesPorDia || [];
  const maxChart = Math.max(...chartData.map(d => d.total), 1);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#222", marginBottom: 4 }}>Dashboard</h1>
          <p style={{ color: "#888", fontSize: 14 }}>Visão geral do sistema Controla Caçambas</p>
        </div>
        <div style={{ display: "flex", gap: 6, background: "#f0f2f5", borderRadius: 10, padding: 4 }}>
          {periodos.map(p => (
            <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
              padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
              background: periodo === p.key ? "#1aae9e" : "transparent",
              color: periodo === p.key ? "#fff" : "#666",
              fontSize: 13, fontWeight: periodo === p.key ? 700 : 500, fontFamily: "inherit",
              transition: "all .2s",
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Alertas */}
      {(a.alertas || []).length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {a.alertas.map((al, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: `${al.cor}12`, border: `1px solid ${al.cor}33`, borderRadius: 10, fontSize: 13, color: al.cor, fontWeight: 600 }}>
              <span style={{ fontSize: 16 }}>{al.tipo === "warning" ? "⚠️" : "ℹ️"}</span>
              {al.msg}
            </div>
          ))}
        </div>
      )}

      {/* Cards destaques */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        {cards1.map((c, i) => (
          <div key={i} style={cardStyle(c.color)}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>{c.icon}</span>
              <span style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>{c.label}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#222" }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Cards gerais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        {cards2.map((c, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${c.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{c.icon}</div>
              <span style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{c.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#222" }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Gráfico + Rankings */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Gráfico locações por dia */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16 }}>Locações por Dia</h3>
          {chartData.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: "#999", fontSize: 13 }}>Sem dados no período</div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 180, padding: "0 0 18px" }}>
              {chartData.slice(-30).map((d, i) => {
                const h = Math.max((d.total / maxChart) * 140, 4);
                const dStr = String(d.dia).includes("T") ? String(d.dia).split("T")[0] : String(d.dia);
                const parts = dStr.split("-");
                const diaLabel = parts.length >= 3 ? parts[2] + "/" + parts[1] : dStr;
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 9, color: "#888", fontWeight: 600 }}>{d.total || ""}</span>
                    <div style={{ width: "100%", height: h, background: "linear-gradient(180deg, #1aae9e, #0d9488)", borderRadius: "4px 4px 0 0", minWidth: 6, transition: "height .3s", cursor: "pointer" }} title={`${diaLabel}: ${d.total} locações\n${fmt(d.faturamento)}`} />
                    <span style={{ fontSize: 8, color: "#aaa", whiteSpace: "nowrap", transform: chartData.length > 15 ? "rotate(-45deg)" : "none", transformOrigin: "center", marginTop: chartData.length > 15 ? 4 : 0 }}>{diaLabel}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Distribuição de planos */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16 }}>Distribuição de Planos</h3>
          {Object.keys(a.planosDistribuicao || {}).length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: "#999", fontSize: 13 }}>Sem dados</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Object.entries(a.planosDistribuicao || {}).map(([plano, total]) => {
                const totalAll = Object.values(a.planosDistribuicao).reduce((a, b) => a + b, 0);
                const pct = totalAll > 0 ? (total / totalAll * 100) : 0;
                const planoColors = { basico: "#3498db", controla: "#1aae9e", controla_plus: "#9b59b6", base: "#3498db", operacao: "#1aae9e", escala: "#9b59b6" };
                const pLabels = { basico: "Básico", controla: "Controla", controla_plus: "Controla Plus", base: "Básico", operacao: "Controla", escala: "Controla Plus" };
                return (
                  <div key={plano}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{pLabels[plano] || plano}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: planoColors[plano] || "#666" }}>{total} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div style={{ height: 8, background: "#f0f2f5", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: planoColors[plano] || "#888", borderRadius: 4, transition: "width .5s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Locações por Status */}
          {Object.keys(a.locacoesPorStatus || {}).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: "#888", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Locações por Status</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries(a.locacoesPorStatus).map(([st, total]) => {
                  const stColors = { "Para Colocar": "#1a9e8f", "Colocada": "#2ecc71", "Para Retirar": "#9b59b6", "Para Trocar": "#f39c12", "Para Cobrar": "#f59e0b", "Encerrada": "#888", "Cancelada": "#e74c3c" };
                  return (
                    <span key={st} style={{ padding: "4px 10px", background: `${stColors[st] || "#666"}15`, border: `1px solid ${stColors[st] || "#666"}33`, borderRadius: 8, fontSize: 11, fontWeight: 600, color: stColors[st] || "#666" }}>{st}: {total}</span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════ HEALTH SCORE DOS TENANTS ATIVOS ═══════ */}
      {health && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>💚</span> Health Score — Tenants Ativos
              </h3>
              <div style={{ fontSize: 12, color: "#888" }}>
                {health.total_ativos} ativos · score geral <span style={{ fontWeight: 800, color: health.score_geral >= 80 ? "#16a34a" : health.score_geral >= 60 ? "#f39c12" : "#e74c3c" }}>{health.score_geral}%</span>
                {health.media_dias_sem_login !== null && ` · média ${health.media_dias_sem_login}d sem login`}
                {health.nunca_logaram > 0 && ` · ${health.nunca_logaram} nunca logaram`}
              </div>
            </div>
          </div>

          {/* 5 buckets em barra */}
          {(() => {
            const total = health.total_ativos || 1;
            const items = [
              { key: "saudavel", label: "Saudável",  sub: "≤ 3 dias",      cor: "#16a34a", count: health.buckets.saudavel },
              { key: "ok",       label: "OK",        sub: "4-7 dias",      cor: "#22c55e", count: health.buckets.ok },
              { key: "atencao",  label: "Atenção",   sub: "8-14 dias",     cor: "#f59e0b", count: health.buckets.atencao },
              { key: "risco",    label: "Risco",     sub: "15-30 dias",    cor: "#f97316", count: health.buckets.risco },
              { key: "critico",  label: "Crítico",   sub: "31+ dias",      cor: "#dc2626", count: health.buckets.critico },
            ];
            const clickable = (k) => ["atencao","risco","critico"].includes(k) && health[k === "atencao" ? "em_atencao" : k === "risco" ? "em_risco" : "criticos"]?.length > 0;
            return (
              <>
                {/* Barra empilhada (visualização proporção) */}
                <div style={{ display: "flex", height: 14, borderRadius: 8, overflow: "hidden", marginBottom: 14, background: "#f0f2f5" }}>
                  {items.map(it => it.count > 0 && (
                    <div
                      key={it.key}
                      title={`${it.label}: ${it.count} (${(it.count/total*100).toFixed(1)}%)`}
                      style={{ width: `${it.count/total*100}%`, background: it.cor, transition: "width .5s" }}
                    />
                  ))}
                </div>
                {/* 5 cards de bucket */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                  {items.map(it => {
                    const pct = total ? (it.count / total * 100) : 0;
                    const ativo = healthDetailBucket === it.key;
                    return (
                      <div
                        key={it.key}
                        onClick={() => clickable(it.key) ? setHealthDetailBucket(ativo ? null : it.key) : null}
                        style={{
                          padding: "12px 14px", borderRadius: 10,
                          background: ativo ? `${it.cor}15` : "#fafbfc",
                          border: `1px solid ${ativo ? it.cor : "#e5e7eb"}`,
                          cursor: clickable(it.key) ? "pointer" : "default",
                          transition: "all .15s",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: it.cor, display: "inline-block" }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1e2d4f", textTransform: "uppercase", letterSpacing: 0.3 }}>{it.label}</span>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: it.cor }}>{it.count}</div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{pct.toFixed(0)}% · {it.sub}</div>
                        {clickable(it.key) && <div style={{ fontSize: 10, color: it.cor, fontWeight: 700, marginTop: 6 }}>{ativo ? "▼ ocultar" : "▶ ver lista"}</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Detalhamento — lista de tenants do bucket selecionado */}
          {healthDetailBucket && (() => {
            const lista = healthDetailBucket === "atencao" ? health.em_atencao
                        : healthDetailBucket === "risco"   ? health.em_risco
                        : health.criticos;
            const cor   = healthDetailBucket === "atencao" ? "#f59e0b"
                        : healthDetailBucket === "risco"   ? "#f97316"
                        : "#dc2626";
            const titulo = healthDetailBucket === "atencao" ? "Em atenção (8-14 dias sem login)"
                         : healthDetailBucket === "risco"   ? "Em risco (15-30 dias sem login)"
                         : "Críticos (31+ dias ou nunca logaram)";
            return (
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px dashed #e5e7eb" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: cor }}>{titulo}</span>
                  <span style={{ fontSize: 11, color: "#888" }}>{lista.length} tenant(s)</span>
                </div>
                <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #f0f2f5", borderRadius: 8 }}>
                  <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Empresa</th>
                        <th style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Sem login</th>
                        <th style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Plano</th>
                        <th style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Valor/mês</th>
                        <th style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Cliente desde</th>
                        <th style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map(t => (
                        <tr key={t.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                          <td className="mob-title" style={{ padding: "8px 12px" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f" }}>{t.nome}</div>
                            <div style={{ fontSize: 10, color: "#888" }}>{t.email || "—"}{t.tel ? ` · ${t.tel}` : ""}</div>
                          </td>
                          <td data-label="Sem login" style={{ padding: "8px 12px", textAlign: "center" }}>
                            {t.nunca_logou ? (
                              <span style={{ fontSize: 11, fontWeight: 800, color: "#dc2626", background: "#dc262615", padding: "3px 10px", borderRadius: 8 }}>Nunca logou</span>
                            ) : (
                              <span style={{ fontSize: 13, fontWeight: 800, color: cor }}>{t.dias_sem_login}d</span>
                            )}
                          </td>
                          <td data-label="Plano" style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "#666" }}>{t.plano || "—"}</td>
                          <td data-label="Valor/mês" style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#1e2d4f" }}>{fmt(t.valor_plano)}</td>
                          <td data-label="Cliente desde" style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "#888" }}>
                            {t.tenant_criado ? new Date(t.tenant_criado).toLocaleDateString("pt-BR") : "—"}
                            {t.dias_como_cliente ? <div style={{ fontSize: 10, color: "#aaa" }}>{t.dias_como_cliente}d</div> : null}
                          </td>
                          <td className="mob-actions" style={{ padding: "8px 12px", textAlign: "center" }}>
                            <button
                              onClick={() => reengajarWhatsApp(t, healthDetailBucket)}
                              disabled={!t.tel}
                              title={t.tel ? "Enviar mensagem de reengajamento via WhatsApp" : "Tenant sem telefone cadastrado"}
                              style={{
                                background: t.tel ? "#25d366" : "#e5e7eb",
                                color: t.tel ? "#fff" : "#aaa",
                                border: "none", borderRadius: 6, padding: "6px 12px",
                                fontSize: 11, fontWeight: 700, cursor: t.tel ? "pointer" : "not-allowed",
                                fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5,
                                whiteSpace: "nowrap"
                              }}
                            >
                              💬 Precisa de ajuda?
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Rankings */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Top Empresas por Faturamento */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#222", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>🏆</span> Top Faturamento</h3>
          {(a.topFaturamento || []).filter(t => t.faturamento > 0).slice(0, 5).map((t, i) => (
            <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 4 ? "1px solid #f0f2f5" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? "#f1c40f" : i === 1 ? "#bdc3c7" : i === 2 ? "#cd7f32" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: i < 3 ? "#fff" : "#888" }}>{i + 1}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{t.nome}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#2ecc71" }}>{fmt(t.faturamento)}</span>
            </div>
          ))}
          {(a.topFaturamento || []).filter(t => t.faturamento > 0).length === 0 && (
            <div style={{ textAlign: "center", padding: 20, color: "#999", fontSize: 12 }}>Sem dados</div>
          )}
        </div>

        {/* Top Empresas por Locações */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#222", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>📦</span> Top Locações</h3>
          {(a.topLocacoes || []).filter(t => t.total_locacoes > 0).slice(0, 5).map((t, i) => (
            <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 4 ? "1px solid #f0f2f5" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? "#f1c40f" : i === 1 ? "#bdc3c7" : i === 2 ? "#cd7f32" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: i < 3 ? "#fff" : "#888" }}>{i + 1}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{t.nome}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#9b59b6" }}>{t.total_locacoes}</span>
            </div>
          ))}
          {(a.topLocacoes || []).filter(t => t.total_locacoes > 0).length === 0 && (
            <div style={{ textAlign: "center", padding: 20, color: "#999", fontSize: 12 }}>Sem dados</div>
          )}
        </div>

        {/* Top Usuários Ativos */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#222", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>👤</span> Top Usuários</h3>
          {(a.topUsuarios || []).filter(t => t.total_logins > 0).slice(0, 5).map((t, i) => (
            <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 4 ? "1px solid #f0f2f5" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? "#f1c40f" : i === 1 ? "#bdc3c7" : i === 2 ? "#cd7f32" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: i < 3 ? "#fff" : "#888", flexShrink: 0 }}>{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.nome}</div>
                  <div style={{ fontSize: 10, color: "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.tenant_nome}</div>
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#3498db", flexShrink: 0, marginLeft: 8 }}>{t.total_logins} logins</span>
            </div>
          ))}
          {(a.topUsuarios || []).filter(t => t.total_logins > 0).length === 0 && (
            <div style={{ textAlign: "center", padding: 20, color: "#999", fontSize: 12 }}>Sem dados</div>
          )}
        </div>
      </div>

      {/* Locações por Região */}
      {(a.locacoesPorRegiao || []).length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>📍</span> Locações por Região
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Barras horizontais */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(() => {
                const regioes = (a.locacoesPorRegiao || []).filter(r => r.regiao !== "Não informado");
                const maxR = Math.max(...regioes.map(r => r.total), 1);
                return regioes.slice(0, 10).map((r, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{r.regiao}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1aae9e", whiteSpace: "nowrap" }}>{r.total} loc. • {fmt(r.faturamento)}</span>
                    </div>
                    <div style={{ height: 8, background: "#f0f2f5", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: (r.total / maxR * 100) + "%", background: `hsl(${168 - i * 12}, 70%, ${45 + i * 3}%)`, borderRadius: 4, transition: "width .5s" }} />
                    </div>
                  </div>
                ));
              })()}
            </div>
            {/* Lista resumida */}
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "6px 16px", fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: "#888", borderBottom: "1px solid #e5e7eb", paddingBottom: 6 }}>Região</div>
                <div style={{ fontWeight: 700, color: "#888", borderBottom: "1px solid #e5e7eb", paddingBottom: 6, textAlign: "right" }}>Locações</div>
                <div style={{ fontWeight: 700, color: "#888", borderBottom: "1px solid #e5e7eb", paddingBottom: 6, textAlign: "right" }}>Faturamento</div>
                {(a.locacoesPorRegiao || []).filter(r => r.regiao !== "Não informado").slice(0, 10).map((r, i) => (
                  <React.Fragment key={i}>
                    <div style={{ color: "#333", fontWeight: 500, padding: "4px 0", borderBottom: "1px solid #f0f2f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i + 1}. {r.regiao}</div>
                    <div style={{ color: "#1aae9e", fontWeight: 700, textAlign: "right", padding: "4px 0", borderBottom: "1px solid #f0f2f5" }}>{r.total}</div>
                    <div style={{ color: "#2ecc71", fontWeight: 600, textAlign: "right", padding: "4px 0", borderBottom: "1px solid #f0f2f5" }}>{fmt(r.faturamento)}</div>
                  </React.Fragment>
                ))}
              </div>
              {(() => {
                const totalGeral = (a.locacoesPorRegiao || []).reduce((s, r) => s + r.total, 0);
                const fatGeral = (a.locacoesPorRegiao || []).reduce((s, r) => s + (r.faturamento || 0), 0);
                return (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, padding: "8px 0", borderTop: "2px solid #1aae9e" }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#222" }}>Total Geral</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#1aae9e" }}>{totalGeral} loc. • {fmt(fatGeral)}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Trials expirando + Empresas inativas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {(a.trialsExpirando || []).length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #f39c1244", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "#f39c12", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
              <span>⚠️</span> Trials Expirando ({a.trialsExpirando.length})
            </h3>
            {a.trialsExpirando.map(t => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: t.dias_restantes <= 3 ? "rgba(231,76,60,.06)" : "#f8f9fa", borderRadius: 8, marginBottom: 6, border: t.dias_restantes <= 3 ? "1px solid rgba(231,76,60,.15)" : "1px solid #e5e7eb" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#222", fontSize: 13 }}>{t.nome}</span>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{t.email}</div>
                </div>
                <span style={{ color: t.dias_restantes <= 3 ? "#e74c3c" : "#f39c12", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                  {t.dias_restantes <= 0 ? "Expirado!" : t.dias_restantes + " dia(s)"}
                </span>
              </div>
            ))}
          </div>
        )}

        {(a.empresasInativas || []).length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #e67e2244", borderRadius: 14, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "#e67e22", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
              <span>💤</span> Empresas Inativas ({a.empresasInativas.length})
            </h3>
            {a.empresasInativas.slice(0, 8).map(t => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f8f9fa", borderRadius: 8, marginBottom: 6, border: "1px solid #e5e7eb" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#222", fontSize: 13 }}>{t.nome}</span>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{t.plano} • {t.status}</div>
                </div>
                <span style={{ color: "#e67e22", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                  {t.dias_inativo ? t.dias_inativo + " dias" : "Nunca acessou"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   NOVO EMPRESA MODAL
   ══════════════════════════════════════════════ */

// Senha aleatória legível (sem 0/O/1/l/I)
function gerarSenhaAleatoria(len = 10) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

function NovaEmpresaModal({ onClose, onSave }) {
  const [form, setForm] = useState({ nome: "", email: "", plano: "basico", trialDias: 7, nomeUsuario: "", loginUsuario: "", senhaUsuario: "", emailUsuario: "" });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const planosLabel = { basico: "Básico", controla: "Controla", controla_plus: "Controla Plus" };

  const handleSave = async () => {
    if (!form.nome || !form.email) return setErro("Nome e email são obrigatórios");
    if (!form.nomeUsuario || !form.loginUsuario || !form.senhaUsuario) return setErro("Dados do primeiro usuário são obrigatórios");
    setLoading(true); setErro("");
    try { await onSave(form); setShowSuccess(true); } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  if (showSuccess) {
    return <SuccessPopup
      titulo="Empresa Criada!"
      subtitulo={`A empresa "${form.nome}" foi criada com sucesso e já está pronta para uso.`}
      detalhes={[
        { label: "Empresa", value: form.nome },
        { label: "Plano", value: planosLabel[form.plano] || form.plano },
        { label: "Trial", value: form.trialDias + " dias" },
        { label: "Usuário", value: form.nomeUsuario },
        { label: "Login", value: form.loginUsuario },
        { label: "Email", value: form.emailUsuario || form.email },
      ]}
      onClose={onClose}
    />;
  }

  return (
    <Modal title="Nova Empresa" onClose={onClose} width={560}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Nome da Empresa</label>
          <input value={form.nome} onChange={e => upd("nome", e.target.value)} placeholder="Ex: Caçambas São Paulo LTDA" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input type="email" value={form.email} onChange={e => upd("email", e.target.value)} placeholder="contato@empresa.com" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Plano</label>
          <select value={form.plano} onChange={e => upd("plano", e.target.value)}>
            <option value="basico">Básico — R$ 120/mês</option>
            <option value="controla">Controla — R$ 200/mês</option>
            <option value="controla_plus">Controla Plus — R$ 300/mês</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Telefone</label>
          <input type="tel" value={form.tel || ""} onChange={e => upd("tel", e.target.value)} placeholder="(11) 99999-9999" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Dias de Trial</label>
          <input type="number" value={form.trialDias} onChange={e => upd("trialDias", e.target.value)} min={1} max={90} />
        </div>
        <div style={{ gridColumn: "1 / -1", margin: "10px 0 14px", padding: "12px 0 0", borderTop: "1px solid #2a2a4a" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1aae9e" }}>Primeiro Usuário (Admin da Empresa)</span>
        </div>
        <div style={fieldStyle}><label style={labelStyle}>Nome</label><input value={form.nomeUsuario} onChange={e => upd("nomeUsuario", e.target.value)} placeholder="Nome completo" /></div>
        <div style={fieldStyle}><label style={labelStyle}>Login</label><input value={form.loginUsuario} onChange={e => upd("loginUsuario", e.target.value)} placeholder="login de acesso" /></div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Senha</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" value={form.senhaUsuario} onChange={e => upd("senhaUsuario", e.target.value)} placeholder="Senha inicial" style={{ flex: 1, minWidth: 0 }} />
            <button type="button" onClick={() => upd("senhaUsuario", gerarSenhaAleatoria(10))} title="Gerar senha aleatória" style={{ background: "rgba(26,174,158,.15)", border: "1px solid rgba(26,174,158,.3)", borderRadius: 8, color: "#1aae9e", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 12px", whiteSpace: "nowrap", fontFamily: "inherit" }}>🎲 Gerar</button>
          </div>
        </div>
        <div style={fieldStyle}><label style={labelStyle}>Email do Usuário</label><input type="email" value={form.emailUsuario} onChange={e => upd("emailUsuario", e.target.value)} placeholder="email@usuario.com" /></div>
      </div>
      {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "10px 24px", background: "#f0f2f5", border: "1px solid #dde1e6", borderRadius: 8, color: "#888", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
        <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: loading ? .7 : 1, fontFamily: "inherit" }}>
          {loading ? "Criando..." : "Criar Empresa"}
        </button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   DETALHE EMPRESA MODAL (completo)
   ══════════════════════════════════════════════ */

function DetalheEmpresaModal({ tenant, onClose, onSave, addToast, onReload }) {
  const [tab, setTab] = useState("dados");
  const [form, setForm] = useState({ nome: tenant.nome, email: tenant.email, plano: tenant.plano, status: tenant.status, trial_expira: tenant.trial_expira ? String(tenant.trial_expira).split("T")[0] : "" });
  const [usuarios, setUsuarios] = useState([]);
  const [detalhes, setDetalhes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [novoUser, setNovoUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [appMotData, setAppMotData] = useState(null);
  const [editMot, setEditMot] = useState(null);
  const [maxMotEdit, setMaxMotEdit] = useState(null);

  // ─── Assinatura & Cobrança (lazy, carrega ao abrir a aba) ──
  const [cobLoaded, setCobLoaded] = useState(false);
  const [cobLoading, setCobLoading] = useState(false);
  const [cobForm, setCobForm] = useState(null);
  const [cobSaving, setCobSaving] = useState(false);
  const [assinaturaInfo, setAssinaturaInfo] = useState(null);

  // ─── Propostas vinculadas (lazy) ──
  const [propostasTenant, setPropostasTenant] = useState(null);

  // ─── Relatório Mensal por Email ──
  const [relData, setRelData] = useState(null);
  const [relSaving, setRelSaving] = useState(false);
  const [relEmailTeste, setRelEmailTeste] = useState("");
  const [relEnviando, setRelEnviando] = useState(false);
  const [relPreviewHtml, setRelPreviewHtml] = useState(null);
  const [relLoadingPreview, setRelLoadingPreview] = useState(false);
  const _hoje = new Date();
  const _prev = new Date(_hoje.getFullYear(), _hoje.getMonth() - 1, 1);
  const [relMes, setRelMes] = useState(_prev.getMonth() + 1);
  const [relAno, setRelAno] = useState(_prev.getFullYear());

  const loadRel = () => {
    apiFetch("/api/admin/tenants/" + tenant.id + "/relatorio-email").then(setRelData).catch(() => {});
  };

  useEffect(() => {
    apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios").then(setUsuarios).catch(() => {});
    apiFetch("/api/admin/tenants/" + tenant.id + "/detalhes").then(setDetalhes).catch(() => {});
    apiFetch("/api/admin/tenants/" + tenant.id + "/motoristas-app").then(d => { setAppMotData(d); setMaxMotEdit(d.max); }).catch(() => {});
    loadRel();
  }, [tenant.id]);

  // Fetch lazy das abas Assinatura e Propostas
  useEffect(() => {
    if (tab === "assinatura" && !cobLoaded && !cobLoading) {
      setCobLoading(true);
      Promise.all([
        apiFetch("/api/admin/cobranca/tenants/" + tenant.id).catch(() => null),
        apiFetch("/api/admin/cobranca/tenants/" + tenant.id + "/assinatura").catch(() => null),
      ]).then(([cob, ass]) => {
        const t = cob?.tenant || null;
        setAssinaturaInfo(ass);
        if (t) {
          setCobForm({
            razao_social: t.razao_social || t.nome || "",
            cpf_cnpj_cobranca: t.cpf_cnpj_cobranca || t.cnpj || "",
            tel: t.tel || "",
            endereco_cobranca: t.endereco_cobranca || "",
            bairro_cobranca: t.bairro_cobranca || "",
            cidade_cobranca: t.cidade_cobranca || "",
            uf_cobranca: t.uf_cobranca || "",
            cep_cobranca: t.cep_cobranca || "",
            dia_vencimento: t.dia_vencimento || 10,
            forma_pagamento: t.forma_pagamento || "boleto",
            valor_plano: t.valor_plano || 0,
            prox_cobranca: t.prox_cobranca ? String(t.prox_cobranca).split("T")[0] : "",
            ciclo_cobranca: t.ciclo_cobranca || "mensal",
            plano: t.plano || "",
          });
        }
        setCobLoaded(true);
        setCobLoading(false);
      });
    }
    if (tab === "propostas" && propostasTenant === null) {
      apiFetch("/api/admin/propostas?tenant_id=" + tenant.id)
        .then(d => setPropostasTenant(d || []))
        .catch(() => setPropostasTenant([]));
    }
  }, [tab, tenant.id, cobLoaded, cobLoading, propostasTenant]);

  const updCob = (k, v) => setCobForm(f => ({ ...f, [k]: v }));

  const handleSaveCobranca = async () => {
    if (!cobForm) return;
    if (!cobForm.cpf_cnpj_cobranca) return addToast("Erro", "CPF/CNPJ é obrigatório", "⚠️", "#e74c3c");
    const doc = String(cobForm.cpf_cnpj_cobranca).replace(/\D/g, "");
    if (doc.length !== 11 && doc.length !== 14) return addToast("Erro", "CPF deve ter 11 dígitos ou CNPJ 14 dígitos", "⚠️", "#e74c3c");
    setCobSaving(true);
    try {
      await apiFetch("/api/admin/cobranca/tenants/" + tenant.id, { method: "PUT", body: cobForm });
      addToast("Cobrança atualizada!", tenant.nome, "💰", "#2ecc71");
    } catch (err) { addToast("Erro", err.message, "⚠️", "#e74c3c"); }
    setCobSaving(false);
  };

  const togglePrefRel = async () => {
    if (!relData) return;
    setRelSaving(true);
    try {
      const novo = !relData.email_relatorio_mensal;
      await apiFetch("/api/admin/tenants/" + tenant.id + "/relatorio-email/preferencia", {
        method: "POST", body: { email_relatorio_mensal: novo }
      });
      setRelData(d => ({ ...d, email_relatorio_mensal: novo }));
      addToast(novo ? "Relatório ativado" : "Relatório desativado", relData.email || "", "📬", "#1aae9e");
    } catch (err) { addToast("Erro", err.message, "⚠️", "#e74c3c"); }
    setRelSaving(false);
  };

  const verPreviewRel = async () => {
    setRelLoadingPreview(true);
    try {
      const token = getToken();
      const res = await fetch(API + "/api/admin/tenants/" + tenant.id + "/relatorio-email/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ mes: relMes, ano: relAno }),
      });
      if (!res.ok) throw new Error("Falha ao gerar preview");
      const html = await res.text();
      setRelPreviewHtml(html);
    } catch (err) { addToast("Erro", err.message, "⚠️", "#e74c3c"); }
    setRelLoadingPreview(false);
  };

  const enviarTesteRel = async () => {
    if (!relEmailTeste || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(relEmailTeste)) {
      return addToast("Email inválido", relEmailTeste, "⚠️", "#e74c3c");
    }
    setRelEnviando(true);
    try {
      await apiFetch("/api/admin/tenants/" + tenant.id + "/relatorio-email/enviar-teste", {
        method: "POST", body: { email: relEmailTeste, mes: relMes, ano: relAno }
      });
      addToast("Teste enviado!", relEmailTeste, "✉️", "#2ecc71");
      loadRel();
    } catch (err) { addToast("Erro", err.message, "⚠️", "#e74c3c"); }
    setRelEnviando(false);
  };

  const handleSave = async () => {
    setLoading(true); setErro("");
    try { await onSave(tenant.id, form); onClose(); } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleAddUser = async () => {
    if (!novoUser?.nome || !novoUser?.login || !novoUser?.senha) return setErro("Preencha nome, login e senha");
    setLoading(true); setErro("");
    try {
      await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios", { method: "POST", body: novoUser });
      const usu = await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios");
      setUsuarios(usu); setNovoUser(null);
      addToast("Usuário criado!", novoUser.nome, "👤", "#2ecc71");
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleEditUser = async () => {
    if (!editUser?.nome || !editUser?.login) return setErro("Nome e login são obrigatórios");
    setLoading(true); setErro("");
    try {
      await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios/" + editUser.id, { method: "PUT", body: editUser });
      const usu = await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios");
      setUsuarios(usu); setEditUser(null);
      addToast("Usuário atualizado!", editUser.nome, "✏️", "#3498db");
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm("Excluir o usuário \"" + u.nome + "\"? Essa ação não pode ser desfeita.")) return;
    try {
      await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios/" + u.id, { method: "DELETE" });
      setUsuarios(prev => prev.filter(x => x.id !== u.id));
      addToast("Usuário excluído!", u.nome, "🗑️", "#e74c3c");
    } catch (err) { setErro(err.message); }
  };

  const tabs = [
    { key: "dados", label: "Dados", icon: "🏢" },
    { key: "numeros", label: "Números", icon: "📊" },
    { key: "usuarios", label: "Usuários (" + usuarios.length + ")", icon: "👥" },
    { key: "app", label: "App Motorista" + (appMotData ? " (" + appMotData.ativos + "/" + (appMotData.max||0) + ")" : ""), icon: "📱" },
    { key: "relatorio", label: "Relatório" + (relData && !relData.email_relatorio_mensal ? " (off)" : ""), icon: "📬" },
    { key: "assinatura", label: "Assinatura", icon: "💰" },
    { key: "propostas", label: "Propostas", icon: "📋" },
  ];

  const tabBtnStyle = (active) => ({
    padding: "8px 16px", fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
    background: active ? "rgba(26,174,158,.15)" : "transparent",
    border: active ? "1px solid rgba(26,174,158,.3)" : "1px solid transparent",
    borderRadius: 8, color: active ? "#1aae9e" : "#8888aa", fontFamily: "inherit",
    transition: "all .2s", display: "flex", alignItems: "center", gap: 6,
  });

  const statCard = (label, value, color) => (
    <div style={{ background: "#f8f9fa", borderRadius: 10, padding: "16px", textAlign: "center", border: "1px solid #e5e7eb" }}>
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#222" }}>{value}</div>
    </div>
  );

  return (
    <Modal title={tenant.nome} onClose={onClose} width={1040}>
      {/* Status badge no topo */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <StatusBadge status={tenant.status} />
        <span style={{ fontSize: 12, color: "#888" }}>{planoLabels[tenant.plano] || tenant.plano}</span>
        <span style={{ fontSize: 12, color: "#888" }}>•</span>
        <span style={{ fontSize: 12, color: "#888" }}>{tenant.email}</span>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabBtnStyle(tab === t.key)}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ─── TAB: Dados ─── */}
      {tab === "dados" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}><label style={labelStyle}>Nome</label><input value={form.nome} onChange={e => upd("nome", e.target.value)} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={e => upd("email", e.target.value)} /></div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Plano</label>
              <select value={form.plano} onChange={e => upd("plano", e.target.value)}>
                <option value="basico">Básico — R$ 120/mês</option><option value="controla">Controla — R$ 200/mês</option><option value="controla_plus">Controla Plus — R$ 300/mês</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Status</label>
              <select value={form.status} onChange={e => upd("status", e.target.value)}>
                <option value="ativo">Ativo</option><option value="trial">Trial</option><option value="suspenso">Suspenso</option><option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div style={fieldStyle}><label style={labelStyle}>Trial Expira</label><input type="date" value={form.trial_expira} onChange={e => upd("trial_expira", e.target.value)} /></div>
          </div>
          {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ padding: "10px 24px", background: "#f0f2f5", border: "1px solid #dde1e6", borderRadius: 8, color: "#888", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
            <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: loading ? .7 : 1, fontFamily: "inherit" }}>
              {loading ? "Salvando..." : "Salvar Alterações"}
            </button>
          </div>
        </>
      )}

      {/* ─── TAB: Números ─── */}
      {tab === "numeros" && (
        <div>
          {/* Info do Plano */}
          <div style={{ background: "rgba(26,174,158,.06)", border: "1px solid rgba(26,174,158,.2)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1aae9e" }}>Plano {planoLabels[tenant.plano] || tenant.plano}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>{planoPrecos[tenant.plano] || ""}</span>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {(() => {
                const feat = planoFeatures[tenant.plano] || planoFeatures.base;
                const limit = Number(tenant.storage_limit) || planoStorage[tenant.plano] || 0;
                const used = Number(tenant.storage_used) || 0;
                return (
                  <>
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: feat.anexos ? "rgba(46,204,113,.1)" : "rgba(231,76,60,.08)", color: feat.anexos ? "#2ecc71" : "#e74c3c", border: `1px solid ${feat.anexos ? "rgba(46,204,113,.2)" : "rgba(231,76,60,.15)"}` }}>{feat.anexos ? "Anexos" : "Sem Anexos"}</span>
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: feat.rota ? "rgba(46,204,113,.1)" : "rgba(231,76,60,.08)", color: feat.rota ? "#2ecc71" : "#e74c3c", border: `1px solid ${feat.rota ? "rgba(46,204,113,.2)" : "rgba(231,76,60,.15)"}` }}>{feat.rota ? "Rota Inteligente" : "Sem Rota"}</span>
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: "rgba(52,152,219,.1)", color: "#3498db", border: "1px solid rgba(52,152,219,.2)" }}>{feat.maxLoc === 0 ? "Caçambas Ilimitadas" : "Até " + feat.maxLoc + " na rua"}</span>
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: feat.appMot ? "rgba(26,174,158,.1)" : "rgba(231,76,60,.08)", color: feat.appMot ? "#1aae9e" : "#e74c3c", border: `1px solid ${feat.appMot ? "rgba(26,174,158,.2)" : "rgba(231,76,60,.15)"}` }}>{feat.appMot ? "📱 App Mot. (" + feat.maxMot + ")" : "Sem App Mot."}</span>
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: "rgba(155,89,182,.1)", color: "#9b59b6", border: "1px solid rgba(155,89,182,.2)" }}>{limit === 0 ? "Sem armazenamento" : fmtStorage(used) + " / " + fmtStorage(limit)}</span>
                  </>
                );
              })()}
            </div>
            {(() => {
              const limit = Number(tenant.storage_limit) || planoStorage[tenant.plano] || 0;
              if (limit === 0) return null;
              const used = Number(tenant.storage_used) || 0;
              const pct = Math.min((used / limit) * 100, 100);
              const color = pct > 90 ? "#e74c3c" : pct > 70 ? "#f39c12" : "#1aae9e";
              return (
                <div style={{ marginTop: 10 }}>
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: pct + "%", background: color, borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{pct.toFixed(1)}% utilizado</div>
                </div>
              );
            })()}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            {statCard("Locações", tenant.total_locacoes || 0, "#9b59b6")}
            {statCard("Clientes", detalhes?.clientes || 0, "#3498db")}
            {statCard("Caçambas", detalhes?.cacambas || 0, "#1abc9c")}
            {statCard("Motoristas", detalhes?.motoristas || 0, "#f39c12")}
            {statCard("Faturamento", fmt(tenant.faturamento || 0), "#2ecc71")}
            {statCard("Média Preço", fmt(detalhes?.mediaPreco || 0), "#e67e22")}
          </div>
          {detalhes?.locacoesPorStatus && Object.keys(detalhes.locacoesPorStatus).length > 0 && (
            <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 16, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#888", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Locações por Status</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Object.entries(detalhes.locacoesPorStatus).map(([status, total]) => (
                  <div key={status} style={{ background: "#fff", padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: 12, color: "#888" }}>{status}:</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#222", marginLeft: 6 }}>{total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: Usuários ─── */}
      {tab === "usuarios" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{usuarios.length} usuário(s)</span>
            {!novoUser && !editUser && (
              <button onClick={() => setNovoUser({ nome: "", login: "", senha: "", email: "", role: "administrador" })} style={{ padding: "6px 14px", background: "rgba(26,174,158,.15)", border: "1px solid rgba(26,174,158,.3)", borderRadius: 8, color: "#1aae9e", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Novo Usuário</button>
            )}
          </div>

          {/* Lista de usuários */}
          {usuarios.map(u => (
            <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#f8f9fa", borderRadius: 10, marginBottom: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600, color: "#222", fontSize: 14 }}>{u.nome}</span>
                  <span style={{ padding: "2px 8px", background: u.role === "administrador" ? "rgba(26,174,158,.15)" : u.role === "financeiro" ? "rgba(34,197,94,.15)" : "rgba(243,156,18,.15)", color: u.role === "administrador" ? "#1aae9e" : u.role === "financeiro" ? "#22c55e" : "#f39c12", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{u.role === "administrador" ? "Administrador" : u.role === "operacional" ? "Operacional" : u.role === "financeiro" ? "Financeiro" : u.role}</span>
                  <span style={{ fontSize: 11, color: u.status === "ativo" ? "#2ecc71" : "#e74c3c", fontWeight: 600 }}>● {u.status}</span>
                </div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
                  Login: {u.login} {u.email ? " • " + u.email : ""} {u.ultimo_acesso ? " • Último acesso: " + fmtDate(u.ultimo_acesso) : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setEditUser({ ...u, senha: "" })} style={{ padding: "5px 10px", background: "rgba(52,152,219,.15)", border: "1px solid rgba(52,152,219,.3)", borderRadius: 6, color: "#3498db", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Editar</button>
                <button onClick={() => handleDeleteUser(u)} style={{ padding: "5px 10px", background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", borderRadius: 6, color: "#e74c3c", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Excluir</button>
              </div>
            </div>
          ))}

          {/* Form novo usuário */}
          {novoUser && (
            <div style={{ background: "#16213e", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#2ecc71", marginBottom: 12 }}>Novo Usuário</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input placeholder="Nome" value={novoUser.nome} onChange={e => setNovoUser(f => ({ ...f, nome: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Login" value={novoUser.login} onChange={e => setNovoUser(f => ({ ...f, login: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Senha" value={novoUser.senha} onChange={e => setNovoUser(f => ({ ...f, senha: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Email" value={novoUser.email} onChange={e => setNovoUser(f => ({ ...f, email: e.target.value }))} style={{ fontSize: 13 }} />
                <select value={novoUser.role} onChange={e => setNovoUser(f => ({ ...f, role: e.target.value }))} style={{ fontSize: 13 }}>
                  <option value="administrador">Administrador</option>
                  <option value="operacional">Operacional</option>
                  <option value="financeiro">Financeiro (Plano de Contas)</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <button onClick={() => setNovoUser(null)} style={{ padding: "6px 14px", background: "none", border: "1px solid #e5e7eb", borderRadius: 6, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
                <button onClick={handleAddUser} disabled={loading} style={{ padding: "6px 14px", background: "#2ecc71", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Adicionar</button>
              </div>
            </div>
          )}

          {/* Form editar usuário */}
          {editUser && (
            <div style={{ background: "#16213e", border: "1px solid #3498db44", borderRadius: 10, padding: 16, marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#3498db", marginBottom: 12 }}>Editando: {editUser.nome}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input placeholder="Nome" value={editUser.nome} onChange={e => setEditUser(f => ({ ...f, nome: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Login" value={editUser.login} onChange={e => setEditUser(f => ({ ...f, login: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Nova senha (vazio = não alterar)" value={editUser.senha} onChange={e => setEditUser(f => ({ ...f, senha: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Email" value={editUser.email || ""} onChange={e => setEditUser(f => ({ ...f, email: e.target.value }))} style={{ fontSize: 13 }} />
                <select value={editUser.role} onChange={e => setEditUser(f => ({ ...f, role: e.target.value }))} style={{ fontSize: 13 }}>
                  <option value="administrador">Administrador</option>
                  <option value="operacional">Operacional</option>
                  <option value="financeiro">Financeiro (Plano de Contas)</option>
                </select>
                <select value={editUser.status} onChange={e => setEditUser(f => ({ ...f, status: e.target.value }))} style={{ fontSize: 13 }}>
                  <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <button onClick={() => setEditUser(null)} style={{ padding: "6px 14px", background: "none", border: "1px solid #e5e7eb", borderRadius: 6, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
                <button onClick={handleEditUser} disabled={loading} style={{ padding: "6px 14px", background: "#3498db", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Salvar</button>
              </div>
            </div>
          )}

          {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}
        </div>
      )}

      {/* ─── TAB: App Motorista ─── */}
      {tab === "app" && (
        <div>
          {/* Controle de limite */}
          <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 16, border: "1px solid #e5e7eb", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>Limite de Motoristas com App</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                  {appMotData ? `${appMotData.ativos} ativos de ${appMotData.max} liberados · R$ ${(appMotData.valorPorMotorista||10).toFixed(2)}/mot/mês` : "Carregando..."}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="number" min="0" value={maxMotEdit ?? ""} onChange={e => setMaxMotEdit(parseInt(e.target.value) || 0)}
                  style={{ width: 60, fontSize: 14, fontWeight: 700, textAlign: "center", padding: "6px 8px", border: "1px solid #ddd", borderRadius: 6 }} />
                <button onClick={async () => {
                  try {
                    await apiFetch("/api/admin/tenants/" + tenant.id + "/max-motoristas-app", { method: "PATCH", body: { max_motoristas_app: maxMotEdit } });
                    setAppMotData(prev => prev ? { ...prev, max: maxMotEdit } : prev);
                    addToast("Limite atualizado!", maxMotEdit + " motoristas", "📱", "#1aae9e");
                  } catch(err) { setErro(err.message); }
                }} style={{ padding: "6px 14px", background: "#1aae9e", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Salvar Limite
                </button>
              </div>
            </div>
            {/* Barra de uso */}
            {appMotData && appMotData.max > 0 && (() => {
              const pct = Math.min((appMotData.ativos / appMotData.max) * 100, 100);
              const cor = pct > 90 ? "#e74c3c" : pct > 70 ? "#f39c12" : "#1aae9e";
              return (
                <div>
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: pct + "%", background: cor, borderRadius: 4, transition: "width .3s" }} />
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{pct.toFixed(0)}% das vagas utilizadas</div>
                </div>
              );
            })()}
          </div>

          {/* Lista de motoristas */}
          <div style={{ fontSize: 14, fontWeight: 700, color: "#222", marginBottom: 10 }}>Motoristas</div>
          {!appMotData ? (
            <div style={{ textAlign: "center", padding: 20, color: "#888", fontSize: 13 }}>Carregando...</div>
          ) : appMotData.motoristas.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#888", fontSize: 13 }}>Nenhum motorista cadastrado nesta empresa.</div>
          ) : appMotData.motoristas.map(m => (
            <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: m.ativo_app ? "rgba(26,174,158,.06)" : "#f8f9fa", borderRadius: 10, marginBottom: 6, border: "1px solid " + (m.ativo_app ? "rgba(26,174,158,.2)" : "#e5e7eb") }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600, color: "#222", fontSize: 13 }}>{m.nome}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: m.ativo_app ? "rgba(26,174,158,.15)" : "rgba(136,136,136,.1)", color: m.ativo_app ? "#1aae9e" : "#888" }}>
                    {m.ativo_app ? "📱 App Ativo" : "Sem App"}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  {m.email_app ? "Login: " + m.email_app : "Sem email de app"}
                  {m.ultimo_login_app ? " · Último acesso: " + fmtDate(m.ultimo_login_app) : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {editMot?.id === m.id ? (<>
                  {/* Form inline de edição */}
                </>) : (<>
                  <button onClick={() => setEditMot({ id: m.id, email_app: m.email_app || "", senha_app: "", ativo_app: m.ativo_app })}
                    style={{ padding: "5px 10px", background: "rgba(52,152,219,.15)", border: "1px solid rgba(52,152,219,.3)", borderRadius: 6, color: "#3498db", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
                    Configurar
                  </button>
                  <button onClick={async () => {
                    const novoAtivo = !m.ativo_app;
                    if (novoAtivo && appMotData.max > 0 && appMotData.ativos >= appMotData.max) {
                      setErro("Limite de " + appMotData.max + " motoristas atingido.");
                      return;
                    }
                    if (novoAtivo && !m.email_app) { setEditMot({ id: m.id, email_app: "", senha_app: "", ativo_app: true }); return; }
                    try {
                      const upd = await apiFetch("/api/admin/tenants/" + tenant.id + "/motoristas/" + m.id + "/app", { method: "PATCH", body: { ativo_app: novoAtivo } });
                      setAppMotData(prev => ({ ...prev, motoristas: prev.motoristas.map(x => x.id === upd.id ? upd : x), ativos: prev.ativos + (novoAtivo ? 1 : -1) }));
                      addToast(novoAtivo ? "App ativado!" : "App desativado", m.nome, "📱", novoAtivo ? "#1aae9e" : "#888");
                    } catch(err) { setErro(err.message); }
                  }} style={{ padding: "5px 10px", background: m.ativo_app ? "rgba(231,76,60,.15)" : "rgba(46,204,113,.15)", border: "1px solid " + (m.ativo_app ? "rgba(231,76,60,.3)" : "rgba(46,204,113,.3)"), borderRadius: 6, color: m.ativo_app ? "#e74c3c" : "#2ecc71", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
                    {m.ativo_app ? "Desativar" : "Ativar"}
                  </button>
                </>)}
              </div>
            </div>
          ))}

          {/* Modal inline de configuração de credenciais */}
          {editMot && (
            <div style={{ background: "#16213e", border: "1px solid rgba(26,174,158,.3)", borderRadius: 10, padding: 16, marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1aae9e", marginBottom: 12 }}>Configurar App — {appMotData?.motoristas.find(m=>m.id===editMot.id)?.nome || ""}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input placeholder="Email de acesso" value={editMot.email_app} onChange={e => setEditMot(f => ({ ...f, email_app: e.target.value }))} style={{ fontSize: 13 }} />
                <input placeholder="Senha" type="password" value={editMot.senha_app} onChange={e => setEditMot(f => ({ ...f, senha_app: e.target.value }))} style={{ fontSize: 13 }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <button onClick={() => setEditMot(null)} style={{ padding: "6px 14px", background: "none", border: "1px solid #e5e7eb", borderRadius: 6, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
                <button onClick={async () => {
                  try {
                    setLoading(true);
                    const body = {};
                    if (editMot.email_app) body.email_app = editMot.email_app;
                    if (editMot.senha_app) body.senha_app = editMot.senha_app;
                    if (editMot.ativo_app && !appMotData.motoristas.find(m=>m.id===editMot.id)?.ativo_app) body.ativo_app = true;
                    const upd = await apiFetch("/api/admin/tenants/" + tenant.id + "/motoristas/" + editMot.id + "/app", { method: "PATCH", body });
                    const wasActive = appMotData.motoristas.find(m=>m.id===editMot.id)?.ativo_app;
                    setAppMotData(prev => ({ ...prev, motoristas: prev.motoristas.map(x => x.id === upd.id ? upd : x), ativos: prev.ativos + (upd.ativo_app && !wasActive ? 1 : 0) }));
                    setEditMot(null);
                    addToast("Credenciais salvas!", "", "📱", "#1aae9e");
                  } catch(err) { setErro(err.message); }
                  setLoading(false);
                }} disabled={loading} style={{ padding: "6px 14px", background: "#1aae9e", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {editMot.ativo_app ? "Salvar e Ativar" : "Salvar"}
                </button>
              </div>
            </div>
          )}

          {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}
        </div>
      )}

      {/* ─── TAB: Relatório Mensal por Email ─── */}
      {tab === "relatorio" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Status + destino */}
          <div style={{ background: "#f8f9fa", border: "1px solid #e5e7eb", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 4 }}>Relatório mensal automático</div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Enviado no dia 1 de cada mês para o email cadastrado nesta empresa.
                </div>
              </div>
              <button
                onClick={togglePrefRel}
                disabled={relSaving || !relData}
                style={{
                  padding: "8px 18px", borderRadius: 20, border: "none",
                  background: relData?.email_relatorio_mensal ? "#2ecc71" : "#bbb",
                  color: "#fff", fontSize: 12, fontWeight: 700, cursor: relSaving ? "wait" : "pointer", fontFamily: "inherit",
                  transition: "background .2s",
                }}
              >
                {relData?.email_relatorio_mensal ? "✓ Ativo" : "Desativado"}
              </button>
            </div>

            <div style={{ marginTop: 14, padding: "10px 12px", background: "#fff", borderRadius: 8, border: "1px dashed #d1d5db" }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>Destinatário</div>
              <div style={{ fontSize: 14, color: "#222", fontWeight: 700 }}>
                {relData?.email || <span style={{ color: "#e74c3c", fontWeight: 600 }}>⚠ Email não cadastrado na aba Dados</span>}
              </div>
            </div>
          </div>

          {/* Período + preview/teste */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>Período de referência</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select value={relMes} onChange={e => setRelMes(Number(e.target.value))} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit" }}>
                {["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"].map((m,i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
              <input type="number" value={relAno} onChange={e => setRelAno(Number(e.target.value))} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, width: 90, fontFamily: "inherit" }} />
              <button
                onClick={verPreviewRel}
                disabled={relLoadingPreview}
                style={{ padding: "8px 16px", background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
              >
                {relLoadingPreview ? "Gerando…" : "👁 Ver preview"}
              </button>
            </div>

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px dashed #e5e7eb" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>Enviar teste</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="email"
                  placeholder="destino@exemplo.com"
                  value={relEmailTeste}
                  onChange={e => setRelEmailTeste(e.target.value)}
                  style={{ flex: 1, minWidth: 220, padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit" }}
                />
                <button
                  onClick={enviarTesteRel}
                  disabled={relEnviando}
                  style={{ padding: "8px 18px", background: "#2ecc71", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                >
                  {relEnviando ? "Enviando…" : "✉ Enviar"}
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
                O teste não registra no histórico de envios.
              </div>
            </div>
          </div>

          {/* Histórico */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>
              Histórico de envios ({relData?.historico?.length || 0})
            </div>
            {(!relData?.historico || relData.historico.length === 0) ? (
              <div style={{ fontSize: 13, color: "#888", padding: 12, textAlign: "center" }}>
                Nenhum relatório enviado ainda.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 240, overflowY: "auto" }}>
                {relData.historico.map(h => (
                  <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "#f8f9fa", borderRadius: 6, fontSize: 12 }}>
                    <div>
                      <span style={{ fontWeight: 700, color: "#222" }}>{String(h.referencia_mes).padStart(2,"0")}/{h.referencia_ano}</span>
                      <span style={{ color: "#888", marginLeft: 8 }}>{fmtDate(h.enviado_em)}</span>
                    </div>
                    <div style={{ fontSize: 11 }}>
                      {h.status === "enviado" ? (
                        <span style={{ color: "#2ecc71", fontWeight: 700 }}>✓ {h.destinatarios}</span>
                      ) : (
                        <span style={{ color: "#e74c3c", fontWeight: 700 }} title={h.erro || ""}>✗ Erro</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TAB: Assinatura ─── */}
      {tab === "assinatura" && (
        <div>
          {cobLoading && <div style={{ padding: 30, textAlign: "center", color: "#aaa", fontSize: 13 }}>Carregando dados de cobrança...</div>}
          {!cobLoading && cobLoaded && (
            <>
              {/* Bloco Assinatura Asaas */}
              <div style={{ background: "rgba(26,174,158,.06)", border: "1px solid rgba(26,174,158,.2)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1aae9e", marginBottom: 10 }}>🔁 Assinatura Asaas</div>
                {!assinaturaInfo || !assinaturaInfo.ativa ? (
                  <div style={{ fontSize: 13, color: "#888" }}>Nenhuma assinatura Asaas vinculada a esta empresa.</div>
                ) : (() => {
                  const sub = assinaturaInfo.subscription || null;
                  const cicloMap = { MONTHLY: "Mensal", SEMIANNUALLY: "Semestral", YEARLY: "Anual" };
                  const stMap = { ACTIVE: ["Ativa", "#2ecc71"], INACTIVE: ["Inativa", "#e74c3c"], EXPIRED: ["Expirada", "#e74c3c"] };
                  const [stLabel, stColor] = stMap[sub?.status] || [sub?.status || "—", "#888"];
                  const badge = (label, value, color) => (
                    <div style={{ background: "#fff", padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                      <span style={{ fontSize: 11, color: "#888" }}>{label}: </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: color || "#222" }}>{value}</span>
                    </div>
                  );
                  return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {badge("Plano", planoLabels[cobForm?.plano] || cobForm?.plano || "—")}
                      {badge("Status", stLabel, stColor)}
                      {badge("Valor", fmt(sub?.value ?? cobForm?.valor_plano ?? 0), "#1aae9e")}
                      {badge("Ciclo", cicloMap[sub?.cycle] || sub?.cycle || cobForm?.ciclo_cobranca || "—")}
                      {badge("Próx. cobrança", sub?.nextDueDate ? fmtDate(sub.nextDueDate) : (cobForm?.prox_cobranca ? fmtDate(cobForm.prox_cobranca) : "—"))}
                      {badge("Gateway", "Asaas")}
                      {badge("ID", assinaturaInfo.subscriptionId || "—", "#8e44ad")}
                      {badge("Início", sub?.dateCreated ? fmtDate(sub.dateCreated) : "—")}
                      {badge("Fim", sub?.endDate ? fmtDate(sub.endDate) : "—")}
                    </div>
                  );
                })()}
              </div>

              {/* Form de cobrança */}
              {!cobForm ? (
                <div style={{ padding: 20, textAlign: "center", color: "#aaa", fontSize: 13 }}>Não foi possível carregar os dados de cobrança.</div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div style={{ gridColumn: "1 / -1", fontSize: 12, fontWeight: 700, color: "#1e2d4f", borderBottom: "1px solid #eee", paddingBottom: 6 }}>Dados da Empresa</div>
                    <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Razão Social / Nome</label><input value={cobForm.razao_social} onChange={e => updCob("razao_social", e.target.value)} /></div>
                    <div><label style={labelStyle}>CPF/CNPJ Cobrança</label><input value={cobForm.cpf_cnpj_cobranca} onChange={e => updCob("cpf_cnpj_cobranca", e.target.value)} placeholder="00.000.000/0000-00" /></div>
                    <div><label style={labelStyle}>Plano</label>
                      <select value={cobForm.plano} onChange={e => updCob("plano", e.target.value)}>
                        <option value="basico">Básico</option><option value="controla">Controla</option><option value="controla_plus">Controla Plus</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelStyle}>📱 Telefone / WhatsApp <span style={{ fontWeight: 400, color: "#888", fontSize: 11 }}>· usado para envio das mensagens de cobrança</span></label>
                      <input value={cobForm.tel} onChange={e => updCob("tel", e.target.value)} placeholder="(11) 98765-4321" />
                    </div>

                    <div style={{ gridColumn: "1 / -1", fontSize: 12, fontWeight: 700, color: "#1e2d4f", borderBottom: "1px solid #eee", paddingBottom: 6, marginTop: 8 }}>Endereço de Cobrança</div>
                    <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Endereço</label><input value={cobForm.endereco_cobranca} onChange={e => updCob("endereco_cobranca", e.target.value)} /></div>
                    <div><label style={labelStyle}>Bairro</label><input value={cobForm.bairro_cobranca} onChange={e => updCob("bairro_cobranca", e.target.value)} /></div>
                    <div><label style={labelStyle}>Cidade</label><input value={cobForm.cidade_cobranca} onChange={e => updCob("cidade_cobranca", e.target.value)} /></div>
                    <div><label style={labelStyle}>UF</label><input value={cobForm.uf_cobranca} onChange={e => updCob("uf_cobranca", e.target.value)} maxLength={2} /></div>
                    <div><label style={labelStyle}>CEP</label><input value={cobForm.cep_cobranca} onChange={e => updCob("cep_cobranca", e.target.value)} /></div>

                    <div style={{ gridColumn: "1 / -1", fontSize: 12, fontWeight: 700, color: "#1e2d4f", borderBottom: "1px solid #eee", paddingBottom: 6, marginTop: 8 }}>Configuração de Cobrança</div>
                    <div><label style={labelStyle}>Valor (R$)</label><input type="number" step="0.01" value={cobForm.valor_plano} onChange={e => updCob("valor_plano", e.target.value)} /></div>
                    <div><label style={labelStyle}>Ciclo de Cobrança</label>
                      <select value={cobForm.ciclo_cobranca} onChange={e => updCob("ciclo_cobranca", e.target.value)}>
                        <option value="mensal">Mensal</option><option value="semestral">Semestral</option><option value="anual">Anual</option>
                      </select>
                    </div>
                    <div><label style={labelStyle}>Forma de Pagamento</label>
                      <select value={cobForm.forma_pagamento} onChange={e => updCob("forma_pagamento", e.target.value)}>
                        <option value="boleto">Boleto</option><option value="pix">PIX</option><option value="cartao">Cartão</option><option value="manual">Manual</option>
                      </select>
                    </div>
                    <div><label style={labelStyle}>Dia de Vencimento</label><input type="number" min={1} max={28} value={cobForm.dia_vencimento} onChange={e => updCob("dia_vencimento", e.target.value)} /></div>
                    <div><label style={labelStyle}>Próximo Vencimento</label><input type="date" value={cobForm.prox_cobranca} onChange={e => updCob("prox_cobranca", e.target.value)} /></div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                    <button onClick={handleSaveCobranca} disabled={cobSaving} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: cobSaving ? .7 : 1, fontFamily: "inherit" }}>
                      {cobSaving ? "Salvando..." : "Salvar Cobrança"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── TAB: Propostas ─── */}
      {tab === "propostas" && (
        <div>
          {propostasTenant === null ? (
            <div style={{ padding: 30, textAlign: "center", color: "#aaa", fontSize: 13 }}>Carregando propostas...</div>
          ) : propostasTenant.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#aaa", fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
              Nenhuma proposta vinculada
            </div>
          ) : (() => {
            const stColors = { rascunho: "#888", enviada: "#3498db", aceita: "#2ecc71", recusada: "#e74c3c", expirada: "#f39c12" };
            const stLabels = { rascunho: "Rascunho", enviada: "Enviada", aceita: "Aceita", recusada: "Recusada", expirada: "Expirada" };
            const thP = { textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #e5e7eb" };
            return (
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr>
                    <th style={thP}>Código</th><th style={thP}>Plano</th><th style={thP}>Valor</th><th style={thP}>Status</th><th style={thP}>Criada</th><th style={thP}>Views</th><th style={thP}></th>
                  </tr></thead>
                  <tbody>
                    {propostasTenant.map(p => (
                      <tr key={p.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
                        <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1aae9e", fontSize: 12 }}>{p.codigo}</td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "#555" }}>{p.plano || "—"}</td>
                        <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1e2d4f" }}>{fmt(p.valor_final || 0)}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: stColors[p.status] || "#888", background: (stColors[p.status] || "#888") + "18" }}>{stLabels[p.status] || p.status}</span>
                        </td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>{p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888", textAlign: "center" }}>{p.visualizacoes || 0}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          {p.token_publico && (
                            <button onClick={() => window.open(`https://contrato.controlacacambas.com.br/${p.token_publico}`, "_blank")} title="Abrir proposta" style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#1aae9e22", color: "#1aae9e", cursor: "pointer", fontWeight: 700, fontSize: 11, fontFamily: "inherit" }}>🔗 abrir</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Preview modal (iframe) */}
      {relPreviewHtml && (
        <div
          onClick={() => setRelPreviewHtml(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(720px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Preview — {String(relMes).padStart(2,"0")}/{relAno}</div>
              <button onClick={() => setRelPreviewHtml(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#666" }}>×</button>
            </div>
            <iframe title="preview-relatorio" srcDoc={relPreviewHtml} style={{ flex: 1, width: "100%", border: "none", minHeight: 500 }} />
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   EMPRESAS PAGE
   ══════════════════════════════════════════════ */

function EmpresasPage({ tenants, loading, onReload, addToast }) {
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [novoModal, setNovoModal] = useState(false);
  const [detalheModal, setDetalheModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  const filtered = tenants.filter(t => {
    if (filtroStatus !== "todos" && t.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      if (!t.nome.toLowerCase().includes(q) && !t.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleCreate = async (form) => {
    await apiFetch("/api/admin/tenants", { method: "POST", body: form });
    onReload();
  };

  const handleEdit = async (id, form) => {
    await apiFetch("/api/admin/tenants/" + id, { method: "PUT", body: form });
    addToast("Empresa atualizada!", form.nome, "✏️", "#3498db");
    onReload();
  };

  const handleStatus = async (id, nome, status) => {
    await apiFetch("/api/admin/tenants/" + id + "/status", { method: "PATCH", body: { status } });
    addToast("Status alterado!", nome + " → " + statusLabels[status], "🔄", statusColors[status]);
    setMenuOpen(null);
    onReload();
  };

  const handleReset = async (id, nome) => {
    if (!window.confirm("⚠️ ATENÇÃO: Isso vai apagar TODOS os dados de \"" + nome + "\":\n\n• Locações\n• Clientes\n• Motoristas\n• Veículos / Caçambas\n• Itinerários\n• Frota\n• Lançamentos\n• Configuração da empresa\n\nEsta ação NÃO pode ser desfeita!")) return;
    if (!window.confirm("🔴 ÚLTIMA CONFIRMAÇÃO: Tem CERTEZA que deseja resetar todos os dados de \"" + nome + "\"?")) return;
    try {
      await apiFetch("/api/admin/tenants/" + id + "/reset", { method: "POST" });
      addToast("Dados resetados!", nome, "🗑️", "#e74c3c");
      setMenuOpen(null); onReload();
    } catch (err) { alert("Erro ao resetar: " + err.message); }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm("⚠️ EXCLUIR EMPRESA: Isso vai remover PERMANENTEMENTE a empresa \"" + nome + "\" e TODOS os seus dados:\n\n• Todos os dados operacionais\n• Todos os usuários\n• Todos os documentos e arquivos\n• A empresa em si\n\nEsta ação NÃO pode ser desfeita!")) return;
    if (!window.confirm("🔴 ÚLTIMA CONFIRMAÇÃO: Tem ABSOLUTA CERTEZA que deseja excluir permanentemente \"" + nome + "\"?")) return;
    try {
      await apiFetch("/api/admin/tenants/" + id, { method: "DELETE" });
      addToast("Empresa excluída!", nome, "🗑️", "#e74c3c");
      setMenuOpen(null); onReload();
    } catch (err) { alert("Erro ao excluir: " + err.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#222", marginBottom: 4 }}>Empresas</h1>
          <p style={{ color: "#888", fontSize: 14 }}>{tenants.length} empresa(s) cadastrada(s)</p>
        </div>
        <button onClick={() => setNovoModal(true)} style={{ padding: "12px 24px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit" }}>
          + Nova Empresa
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome ou email..." style={{ flex: 1, maxWidth: 350 }} />
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ width: 160 }}>
          <option value="todos">Todos os Status</option>
          <option value="ativo">Ativo</option><option value="trial">Trial</option><option value="suspenso">Suspenso</option><option value="cancelado">Cancelado</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Nenhuma empresa encontrada</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "visible", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                {["Empresa", "Plano", "Status", "Armazenamento", "Usuários", "Locações", "Faturamento", ""].map(h => (
                  <th key={h} style={{ padding: "14px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} style={{ borderBottom: "1px solid #e5e7eb", cursor: "pointer", transition: "background .15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onClick={() => setDetalheModal(t)}>
                  <td className="mob-title" style={{ padding: "14px" }}>
                    <div style={{ fontWeight: 600, color: "#222", fontSize: 14 }}>{t.nome}</div>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{t.email}</div>
                  </td>
                  <td data-label="Plano" style={{ padding: "14px" }}>
                    <span style={{ padding: "4px 10px", background: "#f0f2f5", borderRadius: 6, fontSize: 12, fontWeight: 600, color: "#444", border: "1px solid #e5e7eb" }}>{planoLabels[t.plano] || t.plano}</span>
                  </td>
                  <td data-label="Status" style={{ padding: "14px" }}><StatusBadge status={t.status} /></td>
                  <td data-label="Armazenamento" style={{ padding: "14px" }}>
                    {(() => {
                      const limit = Number(t.storage_limit) || planoStorage[t.plano] || 0;
                      if (limit === 0) return <span style={{ fontSize: 11, color: "#999" }}>Sem armazenamento</span>;
                      const used = Number(t.storage_used) || 0;
                      const pct = Math.min((used / limit) * 100, 100);
                      const color = pct > 90 ? "#e74c3c" : pct > 70 ? "#f39c12" : "#1aae9e";
                      return (
                        <div style={{ minWidth: 100 }}>
                          <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>{fmtStorage(used)} / {fmtStorage(limit)}</div>
                          <div style={{ height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: pct + "%", background: color, borderRadius: 3, transition: "width .3s" }} />
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td data-label="Usuários" style={{ padding: "14px", fontWeight: 600, color: "#333" }}>{t.total_usuarios || 0}</td>
                  <td data-label="Locações" style={{ padding: "14px", fontWeight: 600, color: "#333" }}>{t.total_locacoes || 0}</td>
                  <td data-label="Faturamento" style={{ padding: "14px", fontWeight: 600, color: "#2ecc71" }}>{fmt(t.faturamento || 0)}</td>
                  <td className="mob-actions" style={{ padding: "14px" }} onClick={e => e.stopPropagation()}>
                    <button id={"menu-btn-" + t.id} onClick={() => setMenuOpen(menuOpen === t.id ? null : t.id)} style={{ background: "#f0f2f5", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 12px", color: "#555", cursor: "pointer", fontSize: 16 }}>⋮</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Menu flutuante */}
      {menuOpen && (() => {
        const t = tenants.find(x => x.id === menuOpen);
        if (!t) return null;
        const btn = document.getElementById("menu-btn-" + t.id);
        const rect = btn ? btn.getBoundingClientRect() : { top: 100, right: 100 };
        return (
          <>
            <div onClick={() => setMenuOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
            <div style={{ position: "fixed", top: rect.top + 40, right: window.innerWidth - rect.right, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 6, zIndex: 9999, minWidth: 180, boxShadow: "0 8px 30px rgba(0,0,0,.15)", animation: "fadeIn .2s ease" }}>
              <button onClick={() => { setDetalheModal(t); setMenuOpen(null); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#333", fontSize: 13, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f2f5"} onMouseLeave={e => e.currentTarget.style.background = "none"}>● Ver Detalhes</button>
              {t.status !== "ativo" && <button onClick={() => handleStatus(t.id, t.nome, "ativo")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#2ecc71", fontSize: 13, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f2f5"} onMouseLeave={e => e.currentTarget.style.background = "none"}>● Ativar</button>}
              {t.status !== "suspenso" && <button onClick={() => handleStatus(t.id, t.nome, "suspenso")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#f39c12", fontSize: 13, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f2f5"} onMouseLeave={e => e.currentTarget.style.background = "none"}>⏸️ Suspender</button>}
              {t.status !== "cancelado" && <button onClick={() => handleStatus(t.id, t.nome, "cancelado")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#e74c3c", fontSize: 13, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f2f5"} onMouseLeave={e => e.currentTarget.style.background = "none"}><i className="❌" style={{marginRight:4}}/> Cancelar</button>}
              <div style={{ borderTop: "1px solid #e5e7eb", margin: "4px 0" }} />
              <button onClick={() => handleReset(t.id, t.nome)} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#e74c3c", fontSize: 13, cursor: "pointer", borderRadius: 6, fontWeight: 600, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(231,76,60,.08)"} onMouseLeave={e => e.currentTarget.style.background = "none"}><i className="🗑️" style={{marginRight:4}}/> Resetar Dados</button>
              <button onClick={() => handleDelete(t.id, t.nome)} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#c0392b", fontSize: 13, cursor: "pointer", borderRadius: 6, fontWeight: 700, fontFamily: "inherit" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(192,57,43,.1)"} onMouseLeave={e => e.currentTarget.style.background = "none"}>💀 Excluir Empresa</button>
            </div>
          </>
        );
      })()}

      {novoModal && <NovaEmpresaModal onClose={() => setNovoModal(false)} onSave={handleCreate} />}
      {detalheModal && <DetalheEmpresaModal tenant={detalheModal} onClose={() => setDetalheModal(null)} onSave={handleEdit} addToast={addToast} onReload={onReload} />}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAPA OPERACIONAL — Empresas no mapa (v2)
   ══════════════════════════════════════════════ */

const MAPBOX_TOKEN = "pk.eyJ1IjoidGhpYWdvZjgwIiwiYSI6ImNtbWZmbnc1MzA4M2MycG9yYXE5cW56MnEifQ.4-pE0tuRHE2Ho7U3XNfuoQ";

function MapaEmpresas({ addToast }) {
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leafletReady, setLeafletReady] = useState(false);
  const [modo, setModo] = useState("empresas");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [buscaEmp, setBuscaEmp] = useState("");
  const [hoveredEmp, setHoveredEmp] = useState(null);

  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const css = document.createElement("link");
    css.rel = "stylesheet"; css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    js.onload = () => setLeafletReady(true);
    document.head.appendChild(js);
  }, []);

  useEffect(() => {
    setLoading(true);
    apiFetch("/api/admin/mapa-empresas").then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const iv = setInterval(() => { apiFetch("/api/admin/mapa-empresas").then(setData).catch(() => {}); }, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: false, scrollWheelZoom: true }).setView([-14.5, -51.0], 5);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN, {
      maxZoom: 18, tileSize: 512, zoomOffset: -1, attribution: '',
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [leafletReady]);

  useEffect(() => {
    if (!mapInstanceRef.current || !data || !leafletReady) return;
    const L = window.L;
    const map = mapInstanceRef.current;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    const PL = { basico:"Básico", controla:"Controla", controla_plus:"Controla+", base:"Básico", operacao:"Controla", escala:"Controla+" };

    if (modo === "empresas") {
      const emps = (data.empresas || []).filter(e => e.lat && e.lng);
      emps.forEach(e => {
        const on = e.online;
        const sz = on ? 32 : 24;
        const cor = on ? "#2ecc71" : "#64748b";
        const icon = L.divIcon({
          className: "", iconSize: [sz, sz], iconAnchor: [sz/2, sz/2], popupAnchor: [0, -sz/2-2],
          html: '<div style="position:relative;display:flex;align-items:center;justify-content:center;' + (e.posOrigem === "cidade" ? "opacity:.6;" : "") + '">'
            + (on ? '<div style="position:absolute;inset:-5px;border-radius:50%;background:rgba(46,204,113,.2);animation:pulseRing 2s infinite;pointer-events:none;"></div>' : '')
            + '<div style="width:' + sz + 'px;height:' + sz + 'px;border-radius:50%;background:' + cor + ';border:2px solid rgba(255,255,255,.85);box-shadow:0 2px 8px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;font-size:' + (on ? 12 : 9) + 'px;font-weight:800;color:#fff;">'
            + (e.nome?.[0] || "?").toUpperCase() + '</div></div>',
        });
        const fat = Number(e.faturamento || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        const loc = e.cidade ? e.cidade + (e.uf ? " - " + e.uf : "") : "";
        const popup = L.popup({ maxWidth: 240, className: "mp" }).setContent(
          '<div style="font-family:Segoe UI,sans-serif;">'
          + '<div style="font-size:13px;font-weight:800;color:#222;">' + (e.nome || "") + '</div>'
          + '<div style="font-size:10px;color:#999;margin:2px 0 6px;">' + (loc || e.email) + (e.posOrigem === "cidade" ? ' (aprox.)' : '') + '</div>'
          + '<div style="display:flex;gap:4px;margin-bottom:6px;">'
          + '<span style="padding:2px 6px;border-radius:4px;font-size:9px;font-weight:700;' + (on ? "background:#d1fae5;color:#059669" : "background:#f1f5f9;color:#64748b") + ';">' + (on ? "Online" : "Offline") + '</span>'
          + '<span style="padding:2px 6px;border-radius:4px;font-size:9px;font-weight:600;background:#f0fdfa;color:#0d9488;">' + (PL[e.plano] || e.plano) + '</span>'
          + '</div>'
          + '<div style="display:flex;gap:10px;padding-top:6px;border-top:1px solid #f0f0f0;font-size:10px;">'
          + '<div><span style="color:#999;">Loc</span> <b style="color:#333;">' + (e.total_locacoes || 0) + '</b></div>'
          + '<div><span style="color:#999;">Ativas</span> <b style="color:#1aae9e;">' + (e.ativas || 0) + '</b></div>'
          + '<div><span style="color:#999;">Fat</span> <b style="color:#059669;">' + fat + '</b></div>'
          + '</div></div>'
        );
        const marker = L.marker([e.lat, e.lng], { icon }).addTo(map).bindPopup(popup);
        marker._empId = e.id;
        markersRef.current.push(marker);
      });
      if (emps.length > 0) map.fitBounds(L.latLngBounds(emps.map(e => [e.lat, e.lng])), { padding: [40, 40], maxZoom: 10 });
    } else {
      const SC = { "Colocada":"#2ecc71", "Para Retirar":"#9b59b6", "Para Trocar":"#f39c12", "Para Colocar":"#1a9e8f" };
      let locs = (data.locacoes || []).filter(l => l.lat && l.lng);
      if (filtroStatus !== "todos") locs = locs.filter(l => l.status === filtroStatus);
      locs.forEach(l => {
        const cor = SC[l.status] || "#888";
        const icon = L.divIcon({ className: "", iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8],
          html: '<div style="width:12px;height:12px;border-radius:50%;background:' + cor + ';border:2px solid rgba(255,255,255,.85);box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>' });
        const val = Number(l.valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        const popup = '<div style="font-family:Segoe UI,sans-serif;font-size:11px;">'
          + '<b>' + (l.tenant_nome || "") + '</b><br/>'
          + '<span style="display:inline-block;padding:1px 6px;border-radius:4px;background:' + cor + '18;color:' + cor + ';font-weight:700;font-size:9px;margin:3px 0;">' + l.status + '</span>'
          + (l.cacamba_codigo ? '<br/><span style="color:#666;">Cç. ' + l.cacamba_codigo + '</span>' : '')
          + (l.valor ? '<br/><b style="color:#059669;">' + val + '</b>' : '')
          + '</div>';
        L.marker([l.lat, l.lng], { icon }).addTo(map).bindPopup(popup);
      });
      if (locs.length > 0) map.fitBounds(L.latLngBounds(locs.map(l => [l.lat, l.lng])), { padding: [40, 40], maxZoom: 14 });
    }
  }, [data, leafletReady, modo, filtroStatus]);

  const focusEmpresa = (e) => {
    if (!mapInstanceRef.current || !e.lat || !e.lng) return;
    mapInstanceRef.current.setView([e.lat, e.lng], 13);
    const mk = markersRef.current.find(m => m._empId === e.id);
    if (mk) setTimeout(() => mk.openPopup(), 300);
  };

  const empresas = data?.empresas || [];
  const onlineCount = empresas.filter(e => e.online).length;
  const totalLocs = (data?.locacoes || []).length;
  const fatTotal = empresas.reduce((s, e) => s + (parseFloat(e.faturamento) || 0), 0);
  const PL2 = { basico:"Básico", controla:"Controla", controla_plus:"Controla+", base:"Básico", operacao:"Controla", escala:"Controla+" };

  const empList = empresas
    .filter(e => !buscaEmp || (e.nome || "").toLowerCase().includes(buscaEmp.toLowerCase()))
    .sort((a, b) => (b.online ? 1 : 0) - (a.online ? 1 : 0));

  const cardStyle = (color) => ({ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)", borderLeft: "4px solid " + color });

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#222", marginBottom: 4 }}>Mapa Operacional</h1>
          <p style={{ color: "#888", fontSize: 14 }}>Visão geográfica de todas as empresas e locações</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: "#f0f2f5", borderRadius: 10, padding: 4 }}>
            {[{ key: "empresas", label: "Empresas" }, { key: "locacoes", label: "Locações" }].map(m => (
              <button key={m.key} onClick={() => setModo(m.key)} style={{
                padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit",
                background: modo === m.key ? "#1aae9e" : "transparent",
                color: modo === m.key ? "#fff" : "#666",
                fontSize: 13, fontWeight: modo === m.key ? 700 : 500, transition: "all .2s",
              }}>{m.label}</button>
            ))}
          </div>
          {modo === "locacoes" && (
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 13, fontFamily: "inherit" }}>
              <option value="todos">Todos</option>
              <option value="Colocada">Colocada</option>
              <option value="Para Retirar">P/ Retirar</option>
              <option value="Para Trocar">P/ Trocar</option>
              <option value="Para Colocar">P/ Colocar</option>
            </select>
          )}
        </div>
      </div>

      {/* Stats — mesmo padrão do Dashboard */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        {[
          { label: "Empresas", val: empresas.length, color: "#1aae9e" },
          { label: "Online Agora", val: onlineCount, color: "#2ecc71" },
          { label: "Locações Ativas", val: totalLocs, color: "#9b59b6" },
          { label: "Faturamento", val: fmt(fatTotal), color: "#059669" },
        ].map((c, i) => (
          <div key={i} style={cardStyle(c.color)}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#222" }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Mapa + Tabela lado a lado */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }}>
        {/* Mapa */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)", position: "relative" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>Mapa</span>
            <div style={{ display: "flex", gap: 8, fontSize: 10, color: "#888" }}>
              {modo === "empresas" ? (<>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ecc71" }} /> Online</span>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#64748b" }} /> Offline</span>
              </>) : (<>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#1a9e8f" }} /> P/Col</span>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ecc71" }} /> Col</span>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#9b59b6" }} /> P/Ret</span>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f39c12" }} /> P/Trc</span>
              </>)}
            </div>
          </div>
          {loading && (
            <div style={{ position: "absolute", top: 44, left: 0, right: 0, bottom: 0, background: "rgba(255,255,255,.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
              <span style={{ fontSize: 13, color: "#888" }}>Carregando...</span>
            </div>
          )}
          <div ref={mapRef} style={{ height: 460, width: "100%" }} />
        </div>

        {/* Tabela de Empresas */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>Empresas <span style={{ color: "#aaa", fontWeight: 500 }}>({empresas.length})</span></span>
            <input value={buscaEmp} onChange={e => setBuscaEmp(e.target.value)} placeholder="Buscar..." style={{ width: 160, padding: "5px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 11, background: "#f8f9fa" }} />
          </div>
          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {empList.map(e => {
              const on = e.online;
              return (
                <div key={e.id} onClick={() => focusEmpresa(e)}
                  onMouseEnter={() => setHoveredEmp(e.id)}
                  onMouseLeave={() => setHoveredEmp(null)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                    borderBottom: "1px solid #f5f5f5", cursor: e.lat ? "pointer" : "default",
                    background: hoveredEmp === e.id ? "#f0fdf4" : "transparent", transition: "background .15s",
                  }}>
                  {/* Avatar */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: on ? "linear-gradient(135deg, #d1fae5, #a7f3d0)" : "#f1f5f9",
                    border: on ? "2px solid #86efac" : "2px solid #e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 800, color: on ? "#059669" : "#94a3b8",
                  }}>
                    {(e.nome?.[0] || "?").toUpperCase()}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.nome}</span>
                      {on && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ecc71", flexShrink: 0 }} />}
                    </div>
                    <div style={{ fontSize: 11, color: "#999", display: "flex", gap: 8 }}>
                      <span>{e.cidade ? e.cidade + (e.uf ? "-" + e.uf : "") : "—"}</span>
                      <span style={{ color: "#ccc" }}>|</span>
                      <span>{PL2[e.plano] || e.plano}</span>
                    </div>
                  </div>
                  {/* Números */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>{fmt(e.faturamento || 0)}</div>
                    <div style={{ fontSize: 10, color: "#999" }}>{e.ativas || 0} ativas / {e.total_locacoes || 0} total</div>
                  </div>
                </div>
              );
            })}
            {empList.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 13 }}>Nenhuma empresa encontrada</div>}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulseRing { 0% { transform:scale(.8); opacity:1; } 100% { transform:scale(1.6); opacity:0; } }
        .mp .leaflet-popup-content-wrapper { border-radius: 10px !important; box-shadow: 0 4px 16px rgba(0,0,0,.12) !important; }
        .mp .leaflet-popup-content { margin: 10px 12px !important; }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CONTRATOS PAGE
   ══════════════════════════════════════════════ */

function ContratosPage({ addToast }) {
  const [contrato, setContrato] = useState({
    titulo: "CONTRATO DE PRESTAÇÃO DE SERVIÇOS — CONTROLA CAÇAMBAS",
    // Dados da CONTRATADA (Controla Caçambas)
    nomeContratada: "Controla Caçambas Tecnologia LTDA",
    cnpjContratada: "",
    enderecoContratada: "",
    cidadeContratada: "",
    estadoContratada: "",
    telefoneContratada: "",
    emailContratada: "",
    // Dados da CONTRATANTE (empresa que está contratando)
    nomeContratante: "",
    cnpjContratante: "",
    enderecoContratante: "",
    cidadeContratante: "",
    estadoContratante: "",
    telefoneContratante: "",
    emailContratante: "",
    responsavelContratante: "",
    cpfResponsavel: "",
    clausulas: [
      { titulo: "DO OBJETO", texto: "O presente contrato tem por objeto a prestação de serviços de disponibilização e manutenção da plataforma Controla Caçambas, sistema de gestão para empresas de locação de caçambas, incluindo controle de pedidos, cadastro de clientes, gestão de motoristas, controle financeiro, emissão de relatórios e demais funcionalidades previstas no plano contratado pela CONTRATANTE." },
      { titulo: "DOS PLANOS E VALORES", texto: "A CONTRATANTE deverá optar por um dos planos disponíveis (Básico, Profissional ou Enterprise), conforme tabela de preços vigente no momento da contratação. Os valores serão cobrados mensalmente, com vencimento todo dia {dia_vencimento} de cada mês. Reajustes poderão ocorrer anualmente com base no índice IGPM/FGV ou índice substituto, mediante aviso prévio de 30 (trinta) dias." },
      { titulo: "DA FORMA DE PAGAMENTO", texto: "O pagamento será realizado via boleto bancário, PIX ou cartão de crédito, conforme opção da CONTRATANTE. O atraso no pagamento superior a {dias_tolerancia} dias acarretará a suspensão temporária do acesso ao sistema, sem exclusão dos dados. Após 60 (sessenta) dias de inadimplência, a CONTRATADA reserva-se o direito de rescindir o contrato e excluir os dados armazenados." },
      { titulo: "DO PRAZO E VIGÊNCIA", texto: "O presente contrato terá vigência de 12 (doze) meses, com início na data de ativação da conta, renovando-se automaticamente por períodos iguais e sucessivos, salvo manifestação contrária de qualquer das partes com antecedência mínima de 30 (trinta) dias antes do término do período vigente." },
      { titulo: "DO CANCELAMENTO E RESCISÃO", texto: "A CONTRATANTE poderá solicitar o cancelamento a qualquer momento, mediante comunicação por escrito (email ou sistema). O cancelamento será efetivado ao final do período já pago, sem reembolso proporcional. Em caso de cancelamento antes do término do período mínimo de 3 (três) meses, será cobrada multa de {valor_multa} referente aos meses restantes." },
      { titulo: "DA POLÍTICA DE REEMBOLSO", texto: "Nos primeiros 7 (sete) dias após a contratação (período de teste), a CONTRATANTE poderá solicitar o reembolso integral do valor pago. Após esse período, não haverá reembolso por meses já pagos. Em caso de indisponibilidade do sistema superior a 48 horas consecutivas por motivo atribuível à CONTRATADA, será concedido crédito proporcional ao período de indisponibilidade." },
      { titulo: "DA DISPONIBILIDADE DO SERVIÇO", texto: "A CONTRATADA se compromete a manter o sistema Controla Caçambas disponível com uptime mínimo de 99,5% ao mês, excluídos períodos de manutenção programada (com aviso prévio de 24h), casos fortuitos ou de força maior, e indisponibilidades de terceiros (hospedagem, internet, etc.)." },
      { titulo: "DO SUPORTE TÉCNICO", texto: "A CONTRATADA disponibilizará suporte técnico via WhatsApp, email e sistema de chamados, em horário comercial (segunda a sexta, das 8h às 18h). O prazo de resposta será de até 24 horas úteis para chamados normais e 4 horas para chamados críticos (sistema totalmente inoperante)." },
      { titulo: "DA PROPRIEDADE INTELECTUAL", texto: "O sistema Controla Caçambas, incluindo código-fonte, design, marca e toda a propriedade intelectual, pertence exclusivamente à CONTRATADA. A CONTRATANTE adquire apenas o direito de uso do sistema durante a vigência do contrato, não podendo copiar, modificar, sublicenciar ou fazer engenharia reversa do software." },
      { titulo: "DA PROTEÇÃO DE DADOS (LGPD)", texto: "A CONTRATADA se compromete a tratar os dados pessoais inseridos pela CONTRATANTE em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018). Os dados pertencem à CONTRATANTE, que poderá solicitar exportação ou exclusão a qualquer momento. A CONTRATADA não compartilhará dados com terceiros, exceto quando necessário para a operação do sistema (hospedagem, processamento de pagamentos)." },
      { titulo: "DA LIMITAÇÃO DE RESPONSABILIDADE", texto: "A responsabilidade total da CONTRATADA, por qualquer causa relacionada a este contrato, fica limitada ao valor total pago pela CONTRATANTE nos últimos 12 (doze) meses. A CONTRATADA não se responsabiliza por danos indiretos, lucros cessantes, perda de dados decorrente de ação da CONTRATANTE, ou prejuízos causados por uso indevido do sistema." },
      { titulo: "DA CONFIDENCIALIDADE", texto: "As partes se comprometem a manter sigilo sobre todas as informações comerciais, técnicas e estratégicas obtidas em razão deste contrato. Esta obrigação permanece vigente por 2 (dois) anos após o encerramento do contrato." },
      { titulo: "DO USO ACEITÁVEL", texto: "A CONTRATANTE se compromete a não utilizar o sistema Controla Caçambas para fins ilícitos, a não compartilhar suas credenciais de acesso com terceiros não autorizados, e a manter seus dados de cadastro atualizados. O descumprimento desta cláusula autoriza a CONTRATADA a suspender o acesso imediatamente." },
      { titulo: "DAS ALTERAÇÕES CONTRATUAIS", texto: "A CONTRATADA poderá alterar os termos deste contrato mediante notificação por email com antecedência mínima de 30 (trinta) dias. Caso a CONTRATANTE não concorde com as alterações, poderá rescindir o contrato sem multa dentro de 15 (quinze) dias após a notificação." },
      { titulo: "DO FORO", texto: "Fica eleito o foro da comarca de {cidade_foro}/{estado_foro} para dirimir quaisquer questões oriundas deste contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja." },
    ],
    observacoes: "Este contrato é gerado automaticamente pela plataforma Controla Caçambas.",
    assinaturaDigital: true,
    periodoTeste: 7,
    multaCancelamento: true,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [novaTitulo, setNovaTitulo] = useState("");
  const [novaTexto, setNovaTexto] = useState("");

  const upd = (k, v) => setContrato(f => ({ ...f, [k]: v }));

  const updateClausula = (idx, field, value) => {
    const c = [...contrato.clausulas];
    c[idx] = { ...c[idx], [field]: value };
    upd("clausulas", c);
  };

  const removeClausula = (idx) => {
    upd("clausulas", contrato.clausulas.filter((_, i) => i !== idx));
    setEditIdx(null);
  };

  const moveClausula = (idx, dir) => {
    const c = [...contrato.clausulas];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= c.length) return;
    [c[idx], c[newIdx]] = [c[newIdx], c[idx]];
    upd("clausulas", c);
    setEditIdx(newIdx);
  };

  const addClausula = () => {
    if (!novaTitulo.trim() || !novaTexto.trim()) return;
    upd("clausulas", [...contrato.clausulas, { titulo: novaTitulo.trim(), texto: novaTexto.trim() }]);
    setNovaTitulo("");
    setNovaTexto("");
  };

  const handleSave = () => {
    addToast("Modelo salvo!", "Contrato atualizado com sucesso", "📄", "#1aae9e");
  };

  const sectionStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 24, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,.06)" };
  const inputStyle = { background: "#f5f7fa", border: "1px solid #dde1e6", color: "#333", padding: "10px 14px", borderRadius: 8, fontSize: 14, width: "100%", fontFamily: "inherit" };
  const lblStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 6, textTransform: "uppercase" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#222", marginBottom: 4 }}>Contratos</h1>
          <p style={{ color: "#888", fontSize: 14 }}>Modelo de contrato para novas empresas que contratarem o Controla Caçambas</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setPreviewOpen(!previewOpen)} style={{ padding: "10px 20px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, color: "#555", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            {previewOpen ? "Fechar Preview" : "Visualizar"}
          </button>
          <button onClick={handleSave} style={{ padding: "10px 20px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            Salvar Modelo
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: previewOpen ? "1fr 1fr" : "1fr", gap: 24 }}>
        {/* ─── Formulário ─── */}
        <div>
          {/* Título do contrato */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>Título do Contrato</h3>
            <div>
              <label style={lblStyle}>Título</label>
              <input value={contrato.titulo} onChange={e => upd("titulo", e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Dados da Contratada (Controla Caçambas) */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>Dados da CONTRATADA (Controla Caçambas)</h3>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 14 }}>Seus dados como prestadora do serviço.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Razão Social</label><input value={contrato.nomeContratada} onChange={e => upd("nomeContratada", e.target.value)} style={inputStyle} /></div>
              <div><label style={lblStyle}>CNPJ</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={contrato.cnpjContratada} onChange={e => upd("cnpjContratada", e.target.value)} placeholder="00.000.000/0000-00" style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={async () => { try { const d = await buscarCNPJ(contrato.cnpjContratada); upd("nomeContratada", d.razao_social); upd("enderecoContratada", d.endereco + (d.bairro ? ", " + d.bairro : "") + " - " + d.cidade + "/" + d.uf + (d.cep ? " CEP " + d.cep : "")); if (d.telefone) upd("telefoneContratada", d.telefone); if (d.email) upd("emailContratada", d.email); addToast("CNPJ", d.razao_social, "✅", "#2ecc71"); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 6, padding: "0 10px", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>🔍</button>
                </div>
              </div>
              <div><label style={lblStyle}>Telefone</label><input value={contrato.telefoneContratada} onChange={e => upd("telefoneContratada", e.target.value)} placeholder="(00) 00000-0000" style={inputStyle} /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Email</label><input value={contrato.emailContratada} onChange={e => upd("emailContratada", e.target.value)} placeholder="contato@controlacacambas.com.br" style={inputStyle} /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Endereço</label><input value={contrato.enderecoContratada} onChange={e => upd("enderecoContratada", e.target.value)} placeholder="Rua, Número - Bairro" style={inputStyle} /></div>
              <div><label style={lblStyle}>Cidade</label><input value={contrato.cidadeContratada} onChange={e => upd("cidadeContratada", e.target.value)} placeholder="Cidade" style={inputStyle} /></div>
              <div><label style={lblStyle}>Estado</label><input value={contrato.estadoContratada} onChange={e => upd("estadoContratada", e.target.value)} placeholder="UF" style={inputStyle} /></div>
            </div>
          </div>

          {/* Dados da Contratante (empresa que contrata) */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1aae9e", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>Dados da CONTRATANTE (Empresa Cliente)</h3>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 14 }}>Dados da empresa que está contratando o Controla Caçambas. Estes campos serão preenchidos para cada novo contrato.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Razão Social / Nome da Empresa</label><input value={contrato.nomeContratante} onChange={e => upd("nomeContratante", e.target.value)} placeholder="Nome da empresa contratante" style={inputStyle} /></div>
              <div><label style={lblStyle}>CNPJ</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={contrato.cnpjContratante} onChange={e => upd("cnpjContratante", e.target.value)} placeholder="00.000.000/0000-00" style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={async () => { try { const d = await buscarCNPJ(contrato.cnpjContratante); upd("nomeContratante", d.razao_social); upd("enderecoContratante", d.endereco + (d.bairro ? ", " + d.bairro : "") + " - " + d.cidade + "/" + d.uf + (d.cep ? " CEP " + d.cep : "")); if (d.telefone) upd("telefoneContratante", d.telefone); if (d.email) upd("emailContratante", d.email); addToast("CNPJ", d.razao_social, "✅", "#2ecc71"); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 6, padding: "0 10px", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>🔍</button>
                </div>
              </div>
              <div><label style={lblStyle}>Telefone</label><input value={contrato.telefoneContratante} onChange={e => upd("telefoneContratante", e.target.value)} placeholder="(00) 00000-0000" style={inputStyle} /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Email</label><input value={contrato.emailContratante} onChange={e => upd("emailContratante", e.target.value)} placeholder="email@empresa.com" style={inputStyle} /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={lblStyle}>Endereço</label><input value={contrato.enderecoContratante} onChange={e => upd("enderecoContratante", e.target.value)} placeholder="Rua, Número - Bairro" style={inputStyle} /></div>
              <div><label style={lblStyle}>Cidade</label><input value={contrato.cidadeContratante} onChange={e => upd("cidadeContratante", e.target.value)} placeholder="Cidade" style={inputStyle} /></div>
              <div><label style={lblStyle}>Estado</label><input value={contrato.estadoContratante} onChange={e => upd("estadoContratante", e.target.value)} placeholder="UF" style={inputStyle} /></div>
              <div><label style={lblStyle}>Responsável Legal</label><input value={contrato.responsavelContratante} onChange={e => upd("responsavelContratante", e.target.value)} placeholder="Nome do responsável" style={inputStyle} /></div>
              <div><label style={lblStyle}>CPF do Responsável</label><input value={contrato.cpfResponsavel} onChange={e => upd("cpfResponsavel", e.target.value)} placeholder="000.000.000-00" style={inputStyle} /></div>
            </div>
          </div>

          {/* Cláusulas */}
          <div style={sectionStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", display: "flex", alignItems: "center", gap: 8 }}>Cláusulas do Contrato</h3>
              <span style={{ fontSize: 12, color: "#999" }}>{contrato.clausulas.length} cláusulas</span>
            </div>

            {contrato.clausulas.map((cl, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 28, paddingTop: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1aae9e" }}>{i + 1}ª</span>
                  {editIdx === i && (
                    <>
                      <button onClick={() => moveClausula(i, -1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#999", padding: 0, lineHeight: 1 }} title="Mover para cima"><i className="🔼"/></button>
                      <button onClick={() => moveClausula(i, 1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#999", padding: 0, lineHeight: 1 }} title="Mover para baixo"><i className="🔽"/></button>
                    </>
                  )}
                </div>
                {editIdx === i ? (
                  <div style={{ flex: 1 }}>
                    <input value={cl.titulo} onChange={e => updateClausula(i, "titulo", e.target.value)} placeholder="Título da cláusula" style={{ ...inputStyle, fontWeight: 700, marginBottom: 8 }} />
                    <textarea value={cl.texto} onChange={e => updateClausula(i, "texto", e.target.value)} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => removeClausula(i)} style={{ padding: "4px 12px", background: "rgba(231,76,60,.1)", border: "1px solid rgba(231,76,60,.2)", borderRadius: 6, color: "#e74c3c", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Remover</button>
                      <button onClick={() => setEditIdx(null)} style={{ padding: "4px 12px", background: "#1aae9e", border: "none", borderRadius: 6, color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>OK</button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => setEditIdx(i)} style={{ flex: 1, padding: "10px 14px", background: "#f8f9fa", borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer", transition: "border-color .2s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#1aae9e"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "#e5e7eb"}>
                    <strong style={{ fontSize: 12, color: "#1aae9e", display: "block", marginBottom: 4 }}>{cl.titulo}</strong>
                    <span style={{ fontSize: 12, color: "#666", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{cl.texto}</span>
                  </div>
                )}
              </div>
            ))}

            <div style={{ marginTop: 18, padding: 16, background: "#f8f9fa", borderRadius: 10, border: "1px dashed #dde1e6" }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 10 }}>Adicionar nova cláusula</p>
              <input value={novaTitulo} onChange={e => setNovaTitulo(e.target.value)} placeholder="Título da cláusula (ex: DO SUPORTE)" style={{ ...inputStyle, marginBottom: 8 }} />
              <textarea value={novaTexto} onChange={e => setNovaTexto(e.target.value)} placeholder="Texto da cláusula..." rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 8 }} />
              <button onClick={addClausula} disabled={!novaTitulo.trim() || !novaTexto.trim()} style={{ padding: "10px 18px", background: novaTitulo.trim() && novaTexto.trim() ? "#1aae9e" : "#ccc", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: novaTitulo.trim() && novaTexto.trim() ? "pointer" : "default", fontFamily: "inherit" }}>+ Adicionar Cláusula</button>
            </div>
          </div>

          {/* Configurações */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>Configurações</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lblStyle}>Período de teste (dias)</label>
                <input type="number" value={contrato.periodoTeste} onChange={e => upd("periodoTeste", parseInt(e.target.value) || 0)} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lblStyle}>Observações Finais</label>
              <textarea value={contrato.observacoes} onChange={e => upd("observacoes", e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, color: "#444" }}>
                <input type="checkbox" checked={contrato.assinaturaDigital} onChange={e => upd("assinaturaDigital", e.target.checked)} style={{ width: 18, height: 18, accentColor: "#1aae9e" }} />
                Habilitar assinatura digital no contrato
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, color: "#444" }}>
                <input type="checkbox" checked={contrato.multaCancelamento} onChange={e => upd("multaCancelamento", e.target.checked)} style={{ width: 18, height: 18, accentColor: "#1aae9e" }} />
                Cobrar multa por cancelamento antecipado
              </label>
            </div>
          </div>
        </div>

        {/* ─── Preview ─── */}
        {previewOpen && (
          <div style={{ position: "sticky", top: 30, alignSelf: "start" }}>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "40px 36px", boxShadow: "0 4px 20px rgba(0,0,0,.08)", maxHeight: "85vh", overflow: "auto" }}>
              <div style={{ textAlign: "center", marginBottom: 30, borderBottom: "2px solid #1aae9e", paddingBottom: 20 }}>
                <h2 style={{ fontSize: 16, fontWeight: 800, color: "#222", letterSpacing: 1, lineHeight: 1.4 }}>{contrato.titulo}</h2>
              </div>

              <div style={{ fontSize: 13, color: "#444", lineHeight: 1.8, marginBottom: 24 }}>
                <p style={{ marginBottom: 12 }}>Pelo presente instrumento particular de contrato de prestação de serviços, de um lado:</p>
                <p style={{ marginBottom: 12 }}><strong>CONTRATADA:</strong> <strong>{contrato.nomeContratada || "[RAZÃO SOCIAL]"}</strong>, inscrita no CNPJ sob nº <strong>{contrato.cnpjContratada || "[CNPJ]"}</strong>, com sede em <strong>{contrato.enderecoContratada || "[ENDEREÇO]"}</strong>, <strong>{contrato.cidadeContratada || "[CIDADE]"}</strong>/<strong>{contrato.estadoContratada || "[UF]"}</strong>, telefone <strong>{contrato.telefoneContratada || "[TELEFONE]"}</strong>, email <strong>{contrato.emailContratada || "[EMAIL]"}</strong>;</p>
                <p style={{ marginBottom: 12 }}>E de outro lado:</p>
                <p><strong>CONTRATANTE:</strong> <strong>{contrato.nomeContratante || "[NOME DA EMPRESA]"}</strong>, inscrita no CNPJ sob nº <strong>{contrato.cnpjContratante || "[CNPJ]"}</strong>, com sede em <strong>{contrato.enderecoContratante || "[ENDEREÇO]"}</strong>, <strong>{contrato.cidadeContratante || "[CIDADE]"}</strong>/<strong>{contrato.estadoContratante || "[UF]"}</strong>, telefone <strong>{contrato.telefoneContratante || "[TELEFONE]"}</strong>, email <strong>{contrato.emailContratante || "[EMAIL]"}</strong>, neste ato representada por <strong>{contrato.responsavelContratante || "[RESPONSÁVEL]"}</strong>, CPF nº <strong>{contrato.cpfResponsavel || "[CPF]"}</strong>;</p>
              </div>

              <p style={{ fontSize: 13, color: "#444", marginBottom: 16 }}>Têm entre si justo e contratado o seguinte:</p>

              {contrato.clausulas.map((cl, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: "#444", lineHeight: 1.7 }}>
                    <strong style={{ color: "#1aae9e" }}>CLÁUSULA {i + 1}ª — {cl.titulo}</strong>
                  </p>
                  <p style={{ fontSize: 13, color: "#444", lineHeight: 1.7, marginTop: 4 }}>{cl.texto}</p>
                </div>
              ))}

              {contrato.observacoes && (
                <div style={{ marginTop: 20, padding: "14px 16px", background: "#f8f9fa", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <p style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}>{contrato.observacoes}</p>
                </div>
              )}

              <div style={{ marginTop: 40, borderTop: "1px solid #e5e7eb", paddingTop: 24 }}>
                <p style={{ fontSize: 13, color: "#444", textAlign: "center", marginBottom: 30 }}>{contrato.cidadeContratada || "[Cidade]"}, ______ de ________________ de ________.</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ borderTop: "1px solid #333", paddingTop: 8, marginTop: 50 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>CONTRATADA</p>
                      <p style={{ fontSize: 11, color: "#888" }}>{contrato.nomeContratada || "[Controla Caçambas]"}</p>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ borderTop: "1px solid #333", paddingTop: 8, marginTop: 50 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>CONTRATANTE</p>
                      <p style={{ fontSize: 11, color: "#888" }}>{contrato.nomeContratante || "[Nome da Empresa]"}</p>
                      <p style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>{contrato.responsavelContratante || "[Responsável Legal]"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {contrato.assinaturaDigital && (
                <div style={{ marginTop: 24, textAlign: "center", padding: "10px", background: "rgba(26,174,158,.08)", borderRadius: 8, border: "1px solid rgba(26,174,158,.2)" }}>
                  <span style={{ fontSize: 11, color: "#1aae9e", fontWeight: 600 }}>Assinatura digital habilitada</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   USUÁRIOS PAGE
   ══════════════════════════════════════════════ */

const roleLabels = { admin: "Admin", user: "Usuário" };
const roleColors = { admin: "#e67e22", user: "#3498db" };

function UsuariosPage({ addToast }) {
  const [usuarios, setUsuarios] = useState([]);
  const [tenantsList, setTenantsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroTenant, setFiltroTenant] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [pagina, setPagina] = useState(1);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [actionLoading, setActionLoading] = useState("");
  const PER_PAGE = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [users, tenants] = await Promise.all([
        apiFetch("/api/admin/usuarios"),
        apiFetch("/api/admin/tenants"),
      ]);
      setUsuarios(users || []);
      setTenantsList(tenants || []);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = usuarios.filter(u => {
    if (filtroTenant !== "todos" && String(u.tenant_id) !== filtroTenant) return false;
    if (filtroStatus !== "todos" && u.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (u.nome || "").toLowerCase().includes(q) || (u.login || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.cpf || "").includes(q) || (u.tenant_nome || "").toLowerCase().includes(q);
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginados = filtered.slice((pagina - 1) * PER_PAGE, pagina * PER_PAGE);

  const toggleSel = (id) => setSelecionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => {
    const ids = paginados.map(u => u.id);
    const allSel = ids.every(id => selecionados.includes(id));
    if (allSel) setSelecionados(s => s.filter(x => !ids.includes(x)));
    else setSelecionados(s => [...new Set([...s, ...ids])]);
  };
  const numSel = selecionados.length;

  const handleDelete = async (user) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${user.nome}" (${user.login})?`)) return;
    try {
      await apiFetch(`/api/admin/usuarios/${user.id}`, { method: "DELETE" });
      addToast("Usuário excluído!", user.nome, "🗑️", "#e74c3c");
      setSelecionados(s => s.filter(x => x !== user.id));
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
  };

  const handleDeleteBatch = async () => {
    if (!numSel) return;
    if (!window.confirm(`Excluir ${numSel} usuário(s) selecionado(s)?`)) return;
    setActionLoading("delete");
    let ok = 0, fail = 0;
    for (const uid of selecionados) {
      try { await apiFetch(`/api/admin/usuarios/${uid}`, { method: "DELETE" }); ok++; } catch { fail++; }
    }
    addToast("Exclusão em lote", `${ok} excluído(s)${fail ? `, ${fail} erro(s)` : ""}`, "🗑️", "#e74c3c");
    setSelecionados([]);
    setActionLoading("");
    load();
  };

  const actBtn = (bg) => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: actionLoading ? 0.6 : 1 });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#222", marginBottom: 4 }}>Usuários</h1>
          <p style={{ color: "#888", fontSize: 14 }}>{filtered.length} usuário(s) encontrado(s) de {usuarios.length} total</p>
        </div>
        <button onClick={() => setModalNovo(true)} style={{ padding: "12px 24px", background: "linear-gradient(135deg, #1aae9e, #158f82)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit" }}>
          + Novo Usuário
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <input value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} placeholder="Buscar por nome, login, email ou empresa..." style={{ flex: 1, minWidth: 250, maxWidth: 400 }} />
        <select value={filtroTenant} onChange={e => { setFiltroTenant(e.target.value); setPagina(1); }} style={{ width: 220 }}>
          <option value="todos">Todas as Empresas</option>
          {tenantsList.map(t => <option key={t.id} value={String(t.id)}>{t.nome}</option>)}
        </select>
        <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value); setPagina(1); }} style={{ width: 150 }}>
          <option value="todos">Todos Status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
      </div>

      {/* Barra de ações multi-select */}
      {numSel > 0 && (
        <div style={{ background: "linear-gradient(135deg, #1e2d4f, #1a3a5c)", borderRadius: 12, padding: "12px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", animation: "fadeIn .2s ease" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
            {numSel} {numSel === 1 ? "usuário selecionado" : "usuários selecionados"}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {numSel === 1 && (
              <button onClick={() => setModalEdit(usuarios.find(u => u.id === selecionados[0]))} style={actBtn("#3498db")} disabled={!!actionLoading}><i className="✏️" style={{marginRight:4}}/> Editar</button>
            )}
            <button onClick={handleDeleteBatch} style={actBtn("#e74c3c")} disabled={!!actionLoading}>
              {actionLoading === "delete" ? "Excluindo..." : "🗑️ Excluir"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div> : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "visible", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th style={{ padding: "14px 10px 14px 14px", borderBottom: "1px solid #e5e7eb" }}><input type="checkbox" checked={paginados.length > 0 && paginados.every(u => selecionados.includes(u.id))} onChange={toggleAll} style={{ accentColor: "#1aae9e", cursor: "pointer" }} /></th>
                {["Usuário", "Login / CPF", "Empresa", "Cargo", "Status", "Último Acesso", ""].map(h => (
                  <th key={h} style={{ padding: "14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>Nenhum usuário encontrado</td></tr>
              ) : paginados.map(u => {
                const sel = selecionados.includes(u.id);
                return (
                <tr key={u.id} style={{ borderBottom: "1px solid #f0f0f0", background: sel ? "#f0faf8" : "", transition: "background .15s" }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "#f8f9fa"; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = ""; }}>
                  <td className="mob-check" style={{ padding: "14px 10px 14px 14px" }}><input type="checkbox" checked={sel} onChange={() => toggleSel(u.id)} style={{ accentColor: "#1aae9e", cursor: "pointer" }} /></td>
                  <td className="mob-title" style={{ padding: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.status === "ativo" ? "#1aae9e" : "#ccc", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#fff", flexShrink: 0 }}>
                        {(u.avatar || u.nome?.[0] || "U").toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#222", fontSize: 14 }}>{u.nome}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>{u.email || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Login / CPF" style={{ padding: "14px" }}>
                    <div style={{ fontWeight: 600, color: "#555", fontSize: 13, fontFamily: "monospace" }}>{u.login}</div>
                    {u.cpf && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{u.cpf}</div>}
                  </td>
                  <td data-label="Empresa" style={{ padding: "14px" }}>
                    <span style={{ padding: "4px 10px", background: "#f0f2f5", borderRadius: 6, fontSize: 12, fontWeight: 600, color: "#444", border: "1px solid #e5e7eb" }}>{u.tenant_nome || "—"}</span>
                  </td>
                  <td data-label="Cargo" style={{ padding: "14px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${roleColors[u.role] || "#888"}18`, color: roleColors[u.role] || "#888", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </td>
                  <td data-label="Status" style={{ padding: "14px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: u.status === "ativo" ? "#2ecc7122" : "#e74c3c22", color: u.status === "ativo" ? "#2ecc71" : "#e74c3c", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: u.status === "ativo" ? "#2ecc71" : "#e74c3c" }} />
                      {u.status === "ativo" ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td data-label="Último Acesso" style={{ padding: "14px", fontSize: 12, color: "#666" }}>{u.ultimo_acesso ? fmtDate(u.ultimo_acesso) : <span style={{ color: "#ccc" }}>Nunca</span>}</td>
                  <td className="mob-actions" style={{ padding: "14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setModalEdit(u)} title="Editar" style={{ background: "#3498db18", color: "#3498db", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}><i className="✏️"/></button>
                      <button onClick={() => handleDelete(u)} title="Excluir" style={{ background: "#e74c3c18", color: "#e74c3c", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}><i className="🗑️"/></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px", borderTop: "1px solid #f0f0f0" }}>
              <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #ddd", background: "#f5f5f5", color: "#555", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Anterior</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map(p => (
                <button key={p} onClick={() => setPagina(p)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #ddd", background: p === pagina ? "#1aae9e" : "#f5f5f5", color: p === pagina ? "#fff" : "#555", fontSize: 12, fontWeight: p === pagina ? 700 : 400, cursor: "pointer", fontFamily: "inherit" }}>{p}</button>
              ))}
              <button onClick={() => setPagina(p => Math.min(totalPages, p + 1))} disabled={pagina >= totalPages} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #ddd", background: "#f5f5f5", color: "#555", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Próxima →</button>
            </div>
          )}
        </div>
      )}

      {modalNovo && <NovoUsuarioModal tenants={tenantsList} onClose={() => { setModalNovo(false); load(); }} onSave={async (form) => {
        try {
          await apiFetch("/api/admin/usuarios", { method: "POST", body: form });
          addToast("Usuário criado!", form.nome, "👤", "#2ecc71");
        } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); throw err; }
      }} />}

      {modalEdit && <EditUsuarioModal user={modalEdit} tenants={tenantsList} onClose={() => setModalEdit(null)} onSave={async (form) => {
        try {
          await apiFetch(`/api/admin/usuarios/${modalEdit.id}`, { method: "PUT", body: form });
          addToast("Usuário atualizado!", form.nome, "✏️", "#3498db");
          setModalEdit(null);
          load();
        } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
      }} />}
    </div>
  );
}

/* ─── Modal Novo Usuário ─── */

function NovoUsuarioModal({ tenants, onClose, onSave }) {
  const [form, setForm] = useState({ tenant_id: "", nome: "", login: "", senha: "", email: "", cpf: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const tenantsFiltrados = tenants.filter(t => {
    if (!buscaEmpresa) return true;
    return t.nome.toLowerCase().includes(buscaEmpresa.toLowerCase()) || t.email.toLowerCase().includes(buscaEmpresa.toLowerCase());
  });

  const tenantSelecionado = tenants.find(t => t.id === Number(form.tenant_id));
  const roleLabels = { admin: "Administrador", user: "Operador" };

  const handleSave = async () => {
    if (!form.tenant_id) return alert("Selecione uma empresa");
    if (!form.nome) return alert("Nome é obrigatório");
    if (!form.login) return alert("Login é obrigatório");
    if (!form.senha || form.senha.length < 4) return alert("Senha deve ter no mínimo 4 caracteres");
    setSaving(true);
    try { await onSave({ ...form, tenant_id: Number(form.tenant_id) }); setShowSuccess(true); } catch (e) {}
    setSaving(false);
  };

  if (showSuccess) {
    return <SuccessPopup
      titulo="Usuário Criado!"
      subtitulo={`O usuário "${form.nome}" foi criado com sucesso e já pode acessar o sistema.`}
      detalhes={[
        { label: "Nome", value: form.nome },
        { label: "Empresa", value: tenantSelecionado?.nome || "—" },
        { label: "Login", value: form.login },
        { label: "Cargo", value: roleLabels[form.role] || form.role },
        ...(form.email ? [{ label: "Email", value: form.email }] : []),
      ]}
      onClose={onClose}
    />;
  }

  return (
    <Modal title="Novo Usuário" onClose={onClose} width={560}>
      {/* Campo de busca de empresa */}
      <div style={{ marginBottom: 16, position: "relative" }}>
        <label style={labelStyle}>Empresa *</label>
        {tenantSelecionado ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0faf8", border: "1px solid #1aae9e44", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1aae9e", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: "#fff" }}>
              {tenantSelecionado.nome[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1e2d4f" }}>{tenantSelecionado.nome}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{tenantSelecionado.email} · {planoLabels[tenantSelecionado.plano] || tenantSelecionado.plano}</div>
            </div>
            <button onClick={() => { upd("tenant_id", ""); setBuscaEmpresa(""); }} style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        ) : (
          <>
            <input
              value={buscaEmpresa}
              onChange={e => { setBuscaEmpresa(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Buscar empresa por nome ou email..."
              style={{ width: "100%" }}
            />
            {showDropdown && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, maxHeight: 200, overflow: "auto", zIndex: 999, boxShadow: "0 8px 30px rgba(0,0,0,.12)", marginTop: 4 }}>
                {tenantsFiltrados.length === 0 ? (
                  <div style={{ padding: "14px", color: "#aaa", fontSize: 13, textAlign: "center" }}>Nenhuma empresa encontrada</div>
                ) : tenantsFiltrados.map(t => (
                  <button key={t.id} onClick={() => { upd("tenant_id", String(t.id)); setBuscaEmpresa(""); setShowDropdown(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: statusColors[t.status] || "#888", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: "#fff", flexShrink: 0 }}>
                      {t.nome[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{t.nome}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{t.email} · {planoLabels[t.plano] || t.plano} · <span style={{ color: statusColors[t.status] }}>{statusLabels[t.status] || t.status}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Nome Completo *</label><input value={form.nome} onChange={e => upd("nome", e.target.value)} placeholder="Nome do usuário" /></div>
        <div><label style={labelStyle}>Login *</label><input value={form.login} onChange={e => upd("login", e.target.value)} placeholder="login.acesso" /></div>
        <div><label style={labelStyle}>Senha *</label><input type="text" value={form.senha} onChange={e => upd("senha", e.target.value)} placeholder="Senha inicial" /></div>
        <div><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={e => upd("email", e.target.value)} placeholder="email@empresa.com" /></div>
        <div><label style={labelStyle}>CPF</label><input value={form.cpf} onChange={e => upd("cpf", e.target.value)} placeholder="000.000.000-00" maxLength={14} /></div>
        <div>
          <label style={labelStyle}>Cargo</label>
          <select value={form.role} onChange={e => upd("role", e.target.value)}>
            <option value="admin">Admin</option>
            <option value="user">Usuário</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #1aae9e, #158f82)", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Criando..." : "Criar Usuário"}
        </button>
      </div>
    </Modal>
  );
}

/* ─── Modal Editar Usuário ─── */

function EditUsuarioModal({ user, tenants, onClose, onSave }) {
  const [form, setForm] = useState({
    nome: user.nome || "",
    login: user.login || "",
    email: user.email || "",
    cpf: user.cpf || "",
    role: user.role || "admin",
    status: user.status || "ativo",
    tenant_id: String(user.tenant_id || ""),
    senha: "",
  });
  const [saving, setSaving] = useState(false);
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const tenantsFiltrados = tenants.filter(t => {
    if (!buscaEmpresa) return true;
    return t.nome.toLowerCase().includes(buscaEmpresa.toLowerCase()) || t.email.toLowerCase().includes(buscaEmpresa.toLowerCase());
  });

  const tenantSelecionado = tenants.find(t => t.id === Number(form.tenant_id));

  const handleSave = async () => {
    if (!form.tenant_id) return alert("Selecione uma empresa");
    if (!form.nome) return alert("Nome é obrigatório");
    if (!form.login) return alert("Login é obrigatório");
    if (form.senha && form.senha.length < 4) return alert("Senha deve ter no mínimo 4 caracteres");
    setSaving(true);
    const payload = { ...form, tenant_id: Number(form.tenant_id) };
    if (!payload.senha) delete payload.senha;
    try { await onSave(payload); } catch (e) {}
    setSaving(false);
  };

  return (
    <Modal title={`Editar Usuário — ${user.nome}`} onClose={onClose} width={560}>
      {/* Campo de busca de empresa */}
      <div style={{ marginBottom: 16, position: "relative" }}>
        <label style={labelStyle}>Empresa *</label>
        {tenantSelecionado ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0faf8", border: "1px solid #1aae9e44", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1aae9e", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: "#fff" }}>
              {tenantSelecionado.nome[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1e2d4f" }}>{tenantSelecionado.nome}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{tenantSelecionado.email} · {planoLabels[tenantSelecionado.plano] || tenantSelecionado.plano}</div>
            </div>
            <button onClick={() => { upd("tenant_id", ""); setBuscaEmpresa(""); }} style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        ) : (
          <>
            <input
              value={buscaEmpresa}
              onChange={e => { setBuscaEmpresa(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Buscar empresa por nome ou email..."
              style={{ width: "100%" }}
            />
            {showDropdown && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, maxHeight: 200, overflow: "auto", zIndex: 999, boxShadow: "0 8px 30px rgba(0,0,0,.12)", marginTop: 4 }}>
                {tenantsFiltrados.length === 0 ? (
                  <div style={{ padding: "14px", color: "#aaa", fontSize: 13, textAlign: "center" }}>Nenhuma empresa encontrada</div>
                ) : tenantsFiltrados.map(t => (
                  <button key={t.id} onClick={() => { upd("tenant_id", String(t.id)); setBuscaEmpresa(""); setShowDropdown(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: statusColors[t.status] || "#888", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: "#fff", flexShrink: 0 }}>
                      {t.nome[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{t.nome}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{t.email} · {planoLabels[t.plano] || t.plano} · <span style={{ color: statusColors[t.status] }}>{statusLabels[t.status] || t.status}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Nome Completo *</label><input value={form.nome} onChange={e => upd("nome", e.target.value)} /></div>
        <div><label style={labelStyle}>Login *</label><input value={form.login} onChange={e => upd("login", e.target.value)} /></div>
        <div><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={e => upd("email", e.target.value)} /></div>
        <div><label style={labelStyle}>CPF</label><input value={form.cpf} onChange={e => upd("cpf", e.target.value)} placeholder="000.000.000-00" maxLength={14} /></div>
        <div>
          <label style={labelStyle}>Cargo</label>
          <select value={form.role} onChange={e => upd("role", e.target.value)}>
            <option value="admin">Admin</option>
            <option value="user">Usuário</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={form.status} onChange={e => upd("status", e.target.value)}>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Nova Senha <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(deixe em branco para manter)</span></label>
          <input type="text" value={form.senha} onChange={e => upd("senha", e.target.value)} placeholder="Nova senha (opcional)" />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#3498db", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Salvando..." : "Salvar Alterações"}
        </button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   ASSINATURAS & COBRANÇA PAGE (v2)
   ══════════════════════════════════════════════ */

const thS = { textAlign: "left", padding: "12px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #e5e7eb" };
const tdS = { padding: "10px 14px", verticalAlign: "middle" };
const pgBtn = { padding: "6px 12px", borderRadius: 6, border: "1px solid #ddd", background: "#f5f5f5", color: "#555", fontSize: 12, cursor: "pointer", fontFamily: "inherit" };

const cicloLabels = { mensal: "Mensal", semestral: "Semestral", anual: "Anual" };
const formaLabels = { boleto: "Boleto", pix: "PIX", cartao: "Cartão", manual: "Manual" };

const situacaoConfig = {
  pago:          { label: "Pago",              color: "#2ecc71", bg: "#2ecc7118", icon: "✓" },
  pendente:      { label: "Pendente",          color: "#f39c12", bg: "#f39c1218", icon: "●" },
  vencendo:      { label: "Vencendo",          color: "#e67e22", bg: "#e67e2218", icon: "⏰" },
  vencido:       { label: "Vencido",           color: "#e74c3c", bg: "#e74c3c18", icon: "!" },
  sem_cobranca:  { label: "Sem cobrança",      color: "#999",    bg: "#99999918", icon: "—" },
  trial:         { label: "Em trial",          color: "#3498db", bg: "#3498db18", icon: "◎" },
  falha:         { label: "Falha no pagamento",color: "#c0392b", bg: "#c0392b18", icon: "✕" },
};

const statusEmpresaConfig = {
  ativo:    { label: "Ativo",    color: "#2ecc71", bg: "#2ecc7118" },
  trial:    { label: "Trial",    color: "#3498db", bg: "#3498db18" },
  suspenso: { label: "Suspenso", color: "#e74c3c", bg: "#e74c3c18" },
  cancelado:{ label: "Cancelado",color: "#999",    bg: "#99999918" },
};

// Helper: calcular situação real no frontend
function calcSituacao(t) {
  if (t.situacao_cobranca_calc) return t.situacao_cobranca_calc;
  if (t.status === "trial") return "trial";
  if (!t.ultimo_boleto_status && !t.prox_cobranca) return "sem_cobranca";
  if (t.ultimo_boleto_status === "pago") return "pago";
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const venc = t.ultimo_boleto_vencimento || t.prox_cobranca;
  if (venc) {
    const dv = new Date(venc); dv.setHours(0,0,0,0);
    const diff = Math.floor((dv - hoje) / 86400000);
    if (diff < 0) return "vencido";
    if (diff <= 7) return "vencendo";
  }
  if (t.ultimo_boleto_status === "pendente" || t.ultimo_boleto_status === "emitido") return "pendente";
  return "sem_cobranca";
}

// Helper: ordenação por urgência
const situacaoOrdem = { vencido: 0, vencendo: 1, pendente: 2, falha: 3, pago: 4, trial: 5, sem_cobranca: 6 };

function SituacaoBadge({ situacao }) {
  const cfg = situacaoConfig[situacao] || situacaoConfig.sem_cobranca;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "3px 10px", borderRadius: 12, whiteSpace: "nowrap" }}>
      <span style={{ fontSize: 10 }}>{cfg.icon}</span> {cfg.label}
    </span>
  );
}

function StatusEmpresaBadge({ status }) {
  const cfg = statusEmpresaConfig[status] || statusEmpresaConfig.ativo;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "3px 10px", borderRadius: 12, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color }} /> {cfg.label}
    </span>
  );
}

function VencimentoBadge({ data }) {
  if (!data) return <span style={{ color: "#ccc", fontSize: 12 }}>—</span>;
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const dv = new Date(data); dv.setHours(0,0,0,0);
  const diff = Math.floor((dv - hoje) / 86400000);
  let color = "#555", bg = "transparent", extra = "";
  if (diff < 0)            { color = "#e74c3c"; bg = "#e74c3c14"; extra = ` · ${Math.abs(diff)}d atrás`; }
  else if (diff === 0)     { color = "#e67e22"; bg = "#e67e2214"; extra = " · hoje"; }
  else if (diff <= 3)      { color = "#e67e22"; bg = "#e67e2214"; extra = ` · em ${diff}d`; }
  else if (diff <= 7)      { color = "#f39c12"; bg = "#f39c1214"; extra = ` · em ${diff}d`; }
  else if (diff <= 15)     { color = "#16a085"; bg = "#16a08514"; extra = ` · em ${diff}d`; }
  else                     { color = "#5a6f7c"; bg = "#5a6f7c10"; extra = ` · em ${diff}d`; }
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color, background: bg, padding: "3px 10px", borderRadius: 6, whiteSpace: "nowrap" }}>
      {fmtDate(data)}<span style={{ opacity: 0.85 }}>{extra}</span>
    </span>
  );
}

/* ══════════════════════════════════════════════
   CALENDÁRIO DE RECEBIMENTOS
   ══════════════════════════════════════════════ */

function CalendarioPage({ addToast }) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diaModal, setDiaModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/admin/cobranca/calendario?ano=${ano}&mes=${mes}`);
      setData(r);
    } catch (e) { addToast("Erro", e.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [ano, mes, addToast]);

  useEffect(() => { load(); }, [load]);

  const irMes = (delta) => {
    let m = mes + delta, a = ano;
    if (m < 1) { m = 12; a--; }
    if (m > 12) { m = 1; a++; }
    setMes(m); setAno(a);
  };
  const irHoje = () => {
    const d = new Date();
    setAno(d.getFullYear()); setMes(d.getMonth() + 1);
  };

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const fmtBRL = (v) => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  // Construir grid do mês — 6 linhas x 7 colunas (sun=0..sat=6)
  const primeiroDia = new Date(ano, mes - 1, 1);
  const ultimoDia = new Date(ano, mes, 0);
  const totalDiasMes = ultimoDia.getDate();
  const inicioSemana = primeiroDia.getDay(); // 0=dom
  const grid = [];
  for (let i = 0; i < 42; i++) {
    const offset = i - inicioSemana;
    const diaDoMes = offset + 1;
    if (diaDoMes < 1 || diaDoMes > totalDiasMes) {
      grid.push(null);
    } else {
      const iso = `${ano}-${String(mes).padStart(2,"0")}-${String(diaDoMes).padStart(2,"0")}`;
      const itens = data?.por_dia?.[iso] || [];
      const total = itens.reduce((s, it) => s + (parseFloat(it.valor) || 0), 0);
      grid.push({ dia: diaDoMes, iso, itens, total });
    }
  }

  const hojeISO = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,"0")}-${String(hoje.getDate()).padStart(2,"0")}`;

  // Cor por estado do dia (apenas para boletos não-previstos; "previsto" é estimativa visual diferenciada)
  const corDoItem = (it) => {
    if (it.status === "pago") return "#16a34a";
    if (it.status === "previsto") return "#7d8fa9";
    const venc = new Date(it.vencimento);
    venc.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    if (venc < today) return "#e74c3c";
    return "#1aae9e";
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>🗓️ Calendário de Recebimentos</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Visão mensal dos boletos de assinatura — previsto, recebido, em atraso.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => irMes(-1)} style={navBtn}>‹ Anterior</button>
          <button onClick={irHoje} style={{ ...navBtn, background: "#1aae9e", color: "#fff", borderColor: "#1aae9e" }}>Hoje</button>
          <button onClick={() => irMes(1)} style={navBtn}>Próximo ›</button>
        </div>
      </div>

      {/* Mês selecionado */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 28, fontWeight: 800, color: "#1e2d4f" }}>{meses[mes - 1]}</span>
        <span style={{ fontSize: 22, fontWeight: 600, color: "#888" }}>{ano}</span>
      </div>

      {/* Cards de resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <ResumoCard label="Previsto no mês"  valor={data?.totais?.previsto} qtd={data?.totais?.qtd_total}    cor="#1e2d4f" />
        <ResumoCard label="Recebido"          valor={data?.totais?.recebido} qtd={data?.totais?.qtd_pago}     cor="#16a34a" />
        <ResumoCard label="Em atraso"         valor={data?.totais?.atrasado} qtd={data?.totais?.qtd_atrasado} cor="#e74c3c" />
        <ResumoCard label="Pendente / a vencer" valor={data?.totais?.pendente} qtd={data?.totais?.qtd_pendente} cor="#1aae9e" />
      </div>

      {/* Calendário */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
        {/* Cabeçalho dos dias da semana */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "#f8f9fa", borderBottom: "1px solid #e5e7eb" }}>
          {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d => (
            <div key={d} style={{ padding: "12px 0", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>
              {d}
            </div>
          ))}
        </div>
        {/* Grid */}
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "#888" }}>Carregando...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {grid.map((cell, i) => {
              if (!cell) {
                return <div key={i} style={{ minHeight: 110, background: "#fafafa", borderRight: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0" }} />;
              }
              const isHoje = cell.iso === hojeISO;
              const temItens = cell.itens.length > 0;
              const corBorda = isHoje ? "#1aae9e" : "#f0f0f0";
              return (
                <div
                  key={i}
                  onClick={() => temItens && setDiaModal(cell)}
                  style={{
                    minHeight: 110, padding: 8,
                    borderRight: "1px solid #f0f0f0",
                    borderBottom: "1px solid #f0f0f0",
                    background: isHoje ? "#f0faf8" : "#fff",
                    cursor: temItens ? "pointer" : "default",
                    transition: "background .15s",
                    position: "relative",
                  }}
                  onMouseEnter={e => { if (temItens) e.currentTarget.style.background = "#f7fdfc"; }}
                  onMouseLeave={e => { if (temItens) e.currentTarget.style.background = isHoje ? "#f0faf8" : "#fff"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{
                      fontSize: 14, fontWeight: isHoje ? 800 : 600,
                      color: isHoje ? "#1aae9e" : "#1e2d4f",
                      width: 26, height: 26, borderRadius: "50%",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: isHoje ? "#1aae9e22" : "transparent",
                    }}>{cell.dia}</span>
                    {temItens && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#1aae9e", background: "#1aae9e15", padding: "2px 6px", borderRadius: 8 }}>
                        {cell.itens.length}
                      </span>
                    )}
                  </div>
                  {temItens && (
                    <>
                      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: "#1e2d4f" }}>
                        {fmtBRL(cell.total)}
                      </div>
                      <div style={{ display: "flex", gap: 3, marginTop: 6, flexWrap: "wrap" }}>
                        {cell.itens.slice(0, 4).map((it, k) => (
                          <span
                            key={k}
                            title={`${it.tenant_nome} — ${fmtBRL(it.valor)}`}
                            style={{ width: 8, height: 8, borderRadius: "50%", background: corDoItem(it), display: "inline-block" }}
                          />
                        ))}
                        {cell.itens.length > 4 && (
                          <span style={{ fontSize: 10, color: "#888", marginLeft: 2 }}>+{cell.itens.length - 4}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 18, marginTop: 14, fontSize: 12, color: "#666" }}>
        <Legenda cor="#16a34a" label="Pago" />
        <Legenda cor="#1aae9e" label="A vencer" />
        <Legenda cor="#e74c3c" label="Em atraso" />
        <Legenda cor="#7d8fa9" label="Previsto (sem boleto ainda)" />
      </div>

      {diaModal && (
        <DiaDetalheModal
          dia={diaModal}
          onClose={() => setDiaModal(null)}
          onReload={load}
          addToast={addToast}
        />
      )}
    </div>
  );
}

const navBtn = {
  padding: "8px 14px", background: "#fff", border: "1px solid #e5e7eb",
  borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#555",
  cursor: "pointer", fontFamily: "inherit",
};

function ResumoCard({ label, valor, qtd, cor }) {
  const fmtBRL = (v) => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", borderTop: `3px solid ${cor}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: cor, marginTop: 6 }}>{fmtBRL(valor)}</div>
      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{qtd ?? 0} {qtd === 1 ? "lançamento" : "lançamentos"}</div>
    </div>
  );
}

function Legenda({ cor, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: cor }} /> {label}
    </span>
  );
}

function DiaDetalheModal({ dia, onClose, onReload, addToast }) {
  const fmtBRL = (v) => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const [m, d, y] = dia.iso.split("-").reverse();
  const dataFmt = `${d}/${m}/${y}`;

  const corStatus = (it) => {
    if (it.status === "pago") return { bg: "#16a34a18", color: "#16a34a", label: "Pago" };
    if (it.status === "previsto") return { bg: "#7d8fa918", color: "#7d8fa9", label: "Previsto" };
    const venc = new Date(it.vencimento);
    venc.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    if (venc < today) return { bg: "#e74c3c18", color: "#e74c3c", label: "Em atraso" };
    return { bg: "#1aae9e18", color: "#1aae9e", label: "A vencer" };
  };

  return (
    <Modal title={`📅 Recebimentos — ${dataFmt}`} onClose={onClose} width={680}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, padding: "10px 14px", background: "#f8f9fa", borderRadius: 8 }}>
        <span style={{ fontSize: 13, color: "#666" }}>{dia.itens.length} lançamento(s)</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: "#1e2d4f" }}>{fmtBRL(dia.total)}</span>
      </div>

      <div style={{ maxHeight: 460, overflow: "auto" }}>
        {dia.itens.map((it, i) => {
          const cs = corStatus(it);
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {it.tenant_nome}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  {it.referencia ? `Ref ${it.referencia}` : "Assinatura recorrente"}
                  {it.plano ? ` · ${it.plano}` : ""}
                  {it.tenant_tel ? ` · ${it.tenant_tel}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f" }}>{fmtBRL(it.valor)}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: cs.color, background: cs.bg, padding: "3px 10px", borderRadius: 12 }}>
                  {cs.label}
                </span>
                {it.asaas_invoice_url && (
                  <a href={it.asaas_invoice_url} target="_blank" rel="noreferrer"
                     style={{ fontSize: 11, color: "#3498db", background: "#3498db18", padding: "3px 8px", borderRadius: 6, textDecoration: "none", fontWeight: 700 }}>
                    🔗 Asaas
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/* ──────────────────────────────────────────────
   Vista AGENDA — agrupa tenants por proximidade do vencimento
   Categorias: vencidos / hoje / esta semana / próxima semana / mais distantes / sem vencimento
   ────────────────────────────────────────────── */
function AgendaVista({ tenants, onSelect }) {
  const fmtBRL = (v) => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const grupos = useMemo(() => {
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const buckets = {
      vencidos:    { label: "Vencidos",         icon: "🔴", cor: "#e74c3c", itens: [] },
      hoje:        { label: "Hoje",             icon: "⏰", cor: "#e67e22", itens: [] },
      semana:      { label: "Esta semana",      icon: "⚠️", cor: "#f39c12", itens: [], range: "1 a 7 dias" },
      proxSemana:  { label: "8 a 15 dias",      icon: "📅", cor: "#16a085", itens: [] },
      futuro:      { label: "Mais de 15 dias",  icon: "🟢", cor: "#1aae9e", itens: [] },
      sem:         { label: "Sem vencimento",   icon: "—",  cor: "#888",    itens: [] },
    };
    for (const t of tenants) {
      const venc = t.prox_cobranca || t.ultimo_boleto_vencimento;
      if (!venc) { buckets.sem.itens.push({ t, diff: null }); continue; }
      const dv = new Date(venc); dv.setHours(0,0,0,0);
      const diff = Math.floor((dv - hoje) / 86400000);
      const item = { t, diff, vencData: dv };
      if (diff < 0)        buckets.vencidos.itens.push(item);
      else if (diff === 0) buckets.hoje.itens.push(item);
      else if (diff <= 7)  buckets.semana.itens.push(item);
      else if (diff <= 15) buckets.proxSemana.itens.push(item);
      else                 buckets.futuro.itens.push(item);
    }
    // Ordenar interno: vencidos por mais atrasado primeiro; demais por vencimento ascendente
    buckets.vencidos.itens.sort((a, b) => a.diff - b.diff);
    ["semana", "proxSemana", "futuro"].forEach(k => buckets[k].itens.sort((a, b) => a.diff - b.diff));
    return buckets;
  }, [tenants]);

  // Calcular totais
  const totalDe = (key) => grupos[key].itens.reduce((s, it) => s + (parseFloat(it.t.valor_plano) || 0), 0);

  const ordem = ["vencidos","hoje","semana","proxSemana","futuro","sem"];
  const totalGeral = tenants.reduce((s, t) => s + (parseFloat(t.valor_plano) || 0), 0);

  if (tenants.length === 0) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 60, textAlign: "center", color: "#aaa" }}>
        Nenhuma empresa para exibir nesta vista
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header total */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#1e2d4f", borderRadius: 10, padding: "12px 18px", color: "#fff" }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>{tenants.length} EMPRESA(S) NO RECORTE</div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{fmtBRL(totalGeral)}</div>
      </div>

      {ordem.map(key => {
        const g = grupos[key];
        if (g.itens.length === 0) return null;
        return (
          <div key={key} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
            {/* Header do bucket */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 16px", background: g.cor + "10", borderBottom: `2px solid ${g.cor}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16 }}>{g.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: g.cor, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {g.label}
                </span>
                {g.range && <span style={{ fontSize: 11, color: "#888" }}>({g.range})</span>}
                <span style={{ fontSize: 11, color: "#666", background: "#fff", padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                  {g.itens.length}
                </span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1e2d4f" }}>{fmtBRL(totalDe(key))}</div>
            </div>

            {/* Itens */}
            {g.itens.map((it, i) => {
              const t = it.t;
              const dataLabel = it.t.prox_cobranca
                ? new Date(it.t.prox_cobranca).toLocaleDateString("pt-BR")
                : "—";
              let etiqueta = "";
              if (it.diff !== null) {
                if (it.diff < 0) etiqueta = `${Math.abs(it.diff)}d atrás`;
                else if (it.diff === 0) etiqueta = "hoje";
                else etiqueta = `em ${it.diff}d`;
              }
              return (
                <div
                  key={t.id}
                  onClick={() => onSelect && onSelect(t)}
                  style={{
                    display: "grid", gridTemplateColumns: "120px 1fr 100px 130px 100px",
                    gap: 14, alignItems: "center", padding: "12px 16px",
                    borderBottom: i < g.itens.length - 1 ? "1px solid #f0f0f0" : "none",
                    cursor: "pointer", transition: "background .15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f" }}>{dataLabel}</div>
                    {etiqueta && <div style={{ fontSize: 11, color: g.cor, fontWeight: 600, marginTop: 2 }}>{etiqueta}</div>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.nome}
                      {t.asaas_subscription_id && (
                        <span title="Assinatura recorrente ativa" style={{ fontSize: 8, fontWeight: 800, background: "#0d7a6e", color: "#fff", padding: "1px 5px", borderRadius: 8, letterSpacing: 0.3 }}>🔁</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.email}{t.tel ? ` · ${t.tel}` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1e2d4f" }}>{fmtBRL(t.valor_plano)}</div>
                    <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{cicloLabels[t._ciclo] || "Mensal"}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: t._forma === "pix" ? "#8e44ad" : t._forma === "boleto" ? "#e67e22" : "#999", background: t._forma === "pix" ? "#8e44ad18" : t._forma === "boleto" ? "#e67e2218" : "#99999918", padding: "2px 8px", borderRadius: 6 }}>
                      {formaLabels[t._forma] || "Boleto"}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>{planoLabels[t.plano] || t.plano || "—"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <SituacaoBadge situacao={t._situacao} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function AssinaturasPage({ addToast }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionados, setSelecionados] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroSituacao, setFiltroSituacao] = useState("todos");
  const [filtroForma, setFiltroForma] = useState("todos");
  const [filtroCiclo, setFiltroCiclo] = useState("todos");
  const [filtroAssinatura, setFiltroAssinatura] = useState("todos");
  const [filtroPlano, setFiltroPlano] = useState("todos");
  const [filtroVenc, setFiltroVenc] = useState("todos");
  const [filtroAcao, setFiltroAcao] = useState(false);
  const [modoVista, setModoVista] = useState("lista"); // "lista" | "agenda"
  const [modalEdit, setModalEdit] = useState(null);
  const [modalDetail, setModalDetail] = useState(null);
  const [modalBoleto, setModalBoleto] = useState(false);
  const [asaasStatus, setAsaasStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState("");
  const [lembreteLoading, setLembreteLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const PER_PAGE = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, cs] = await Promise.all([
        apiFetch("/api/admin/cobranca/tenants"),
        apiFetch("/api/admin/cobranca/asaas/status").catch(() => null),
      ]);
      setTenants(data || []);
      setAsaasStatus(cs);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  // Enriquecer tenants com situação calculada
  const enriched = useMemo(() => tenants.map(t => ({
    ...t,
    _situacao: calcSituacao(t),
    _ciclo: t.ciclo_cobranca || "mensal",
    _forma: t.forma_pagamento || "boleto",
  })), [tenants]);

  // Cards de resumo
  const resumo = useMemo(() => {
    const r = { ativas: 0, trials: 0, suspensas: 0, vencidas: 0, vencendo7d: 0, pagasMes: 0, pix: 0, boleto: 0, mensal: 0, semestral: 0, anual: 0, assinaturas: 0 };
    const mesAtual = new Date().toISOString().slice(0, 7);
    enriched.forEach(t => {
      if (t.status === "ativo") r.ativas++;
      if (t.status === "trial") r.trials++;
      if (t.status === "suspenso") r.suspensas++;
      if (t._situacao === "vencido") r.vencidas++;
      if (t._situacao === "vencendo") r.vencendo7d++;
      if (t._situacao === "pago") {
        const pgDate = t.data_ultimo_pagamento || t.ultimo_pagamento_boleto;
        if (pgDate && String(pgDate).slice(0, 7) === mesAtual) r.pagasMes++;
      }
      if (t._forma === "pix") r.pix++;
      if (t._forma === "boleto") r.boleto++;
      if (t._ciclo === "mensal") r.mensal++;
      if (t._ciclo === "semestral") r.semestral++;
      if (t._ciclo === "anual") r.anual++;
      if (t.asaas_subscription_id) r.assinaturas++;
    });
    return r;
  }, [enriched]);

  // Planos disponíveis (dinâmico)
  const planosDisponiveis = useMemo(() => [...new Set(enriched.map(t => t.plano).filter(Boolean))], [enriched]);

  // Filtros
  const filtered = useMemo(() => {
    let list = enriched;
    if (busca) {
      const q = busca.toLowerCase();
      list = list.filter(t =>
        (t.nome || "").toLowerCase().includes(q) || (t.email || "").toLowerCase().includes(q)
        || (t.cpf_cnpj_cobranca || "").includes(q) || (t.cnpj || "").includes(q)
        || (t.razao_social || "").toLowerCase().includes(q)
      );
    }
    if (filtroStatus !== "todos") list = list.filter(t => t.status === filtroStatus);
    if (filtroSituacao !== "todos") list = list.filter(t => t._situacao === filtroSituacao);
    if (filtroForma !== "todos") list = list.filter(t => t._forma === filtroForma);
    if (filtroCiclo !== "todos") list = list.filter(t => t._ciclo === filtroCiclo);
    if (filtroAssinatura === "ativa") list = list.filter(t => !!t.asaas_subscription_id);
    else if (filtroAssinatura === "sem") list = list.filter(t => !t.asaas_subscription_id);
    if (filtroPlano !== "todos") list = list.filter(t => t.plano === filtroPlano);
    if (filtroVenc !== "todos") {
      const hoje = new Date(); hoje.setHours(0,0,0,0);
      list = list.filter(t => {
        const venc = t.prox_cobranca || t.ultimo_boleto_vencimento;
        if (!venc) return filtroVenc === "sem_vencimento";
        const dv = new Date(venc); dv.setHours(0,0,0,0);
        const diff = Math.floor((dv - hoje) / 86400000);
        if (filtroVenc === "hoje") return diff === 0;
        if (filtroVenc === "3dias") return diff >= 0 && diff <= 3;
        if (filtroVenc === "7dias") return diff >= 0 && diff <= 7;
        if (filtroVenc === "15dias") return diff >= 0 && diff <= 15;
        if (filtroVenc === "30dias") return diff >= 0 && diff <= 30;
        if (filtroVenc === "vencidos") return diff < 0;
        if (filtroVenc === "sem_vencimento") return false;
        return true;
      });
    }
    if (filtroAcao) {
      list = list.filter(t => ["vencido", "vencendo", "falha", "pendente"].includes(t._situacao) || (t.status === "ativo" && !t.prox_cobranca && t._situacao === "sem_cobranca"));
    }
    // Ordenação inteligente
    // Quando o usuário está filtrando por vencimento, ordena cronologicamente (do mais próximo pro mais distante)
    // Caso contrário, mantém ordenação por urgência (vencidos primeiro)
    if (filtroVenc !== "todos") {
      list = [...list].sort((a, b) => {
        const va = a.prox_cobranca ? new Date(a.prox_cobranca).getTime() : Infinity;
        const vb = b.prox_cobranca ? new Date(b.prox_cobranca).getTime() : Infinity;
        if (va !== vb) return va - vb;
        return (b.valor_plano || 0) - (a.valor_plano || 0); // desempate por valor desc
      });
    } else {
      list = [...list].sort((a, b) => (situacaoOrdem[a._situacao] ?? 99) - (situacaoOrdem[b._situacao] ?? 99));
    }
    return list;
  }, [enriched, busca, filtroStatus, filtroSituacao, filtroForma, filtroCiclo, filtroAssinatura, filtroPlano, filtroVenc, filtroAcao]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginados = filtered.slice((pagina - 1) * PER_PAGE, pagina * PER_PAGE);

  const toggleSel = (id) => setSelecionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => {
    const ids = paginados.map(t => t.id);
    const allSel = ids.every(id => selecionados.includes(id));
    if (allSel) setSelecionados(s => s.filter(x => !ids.includes(x)));
    else setSelecionados(s => [...new Set([...s, ...ids])]);
  };

  const numSel = selecionados.length;
  const isSingle = numSel === 1;

  // Ações
  const handleEnviarAviso = async () => {
    if (!numSel) return;
    setActionLoading("aviso");
    try {
      const res = await apiFetch("/api/admin/cobranca/enviar-aviso", { method: "POST", body: { tenantIds: selecionados } });
      const ok = (res.resultados || []).filter(r => r.ok).length;
      const fail = (res.resultados || []).filter(r => !r.ok).length;
      addToast("Avisos enviados", `${ok} enviado(s)${fail ? `, ${fail} erro(s)` : ""}`, "📧", "#1aae9e");
      setSelecionados([]);
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setActionLoading("");
  };

  const handleToggleStatus = async () => {
    if (!isSingle) return;
    const t = tenants.find(x => x.id === selecionados[0]);
    if (!t) return;
    const acao = t.status === "suspenso" ? "reativar" : "suspender";
    if (!window.confirm(`Tem certeza que deseja ${acao} a empresa "${t.nome}"?`)) return;
    setActionLoading("status");
    try {
      await apiFetch(`/api/admin/cobranca/tenants/${t.id}/toggle-status`, { method: "POST" });
      addToast(acao === "suspender" ? "Empresa suspensa" : "Empresa reativada", t.nome, acao === "suspender" ? "🔒" : "🔓", acao === "suspender" ? "#e74c3c" : "#2ecc71");
      setSelecionados([]);
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setActionLoading("");
  };

  const handleProcessarLembretes = async () => {
    setLembreteLoading(true);
    try {
      const res = await apiFetch("/api/admin/cobranca/processar-lembretes", { method: "POST" });
      addToast("Lembretes processados", `${res.enviados} enviado(s), ${res.erros} erro(s)`, "📧", "#1aae9e");
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLembreteLoading(false);
  };

  const handleRefreshAsaas = async () => {
    setRefreshLoading(true);
    try {
      const res = await apiFetch("/api/admin/cobranca/asaas/refresh", { method: "POST" });
      const nErros = (res.erros || []).length;
      addToast(
        "Asaas sincronizado",
        `${res.sincronizados || 0} sincronizado(s), ${res.reativados || 0} reativado(s)${nErros ? `, ${nErros} erro(s)` : ""}`,
        "🔄",
        nErros ? "#f39c12" : "#1aae9e"
      );
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setRefreshLoading(false);
  };

  const actBtn = (bg) => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: actionLoading ? 0.6 : 1 });

  const selectStyle = { padding: "7px 10px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", color: "#333" };

  // Componente da barra de ações
  const actionBar = numSel > 0 && (
    <div style={{ background: "linear-gradient(135deg, #1e2d4f, #1a3a5c)", borderRadius: 12, padding: "12px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", animation: "fadeIn .2s ease" }}>
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
        {numSel} {numSel === 1 ? "empresa selecionada" : "empresas selecionadas"}
      </span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {isSingle && (
          <>
            <button onClick={() => setModalEdit(tenants.find(t => t.id === selecionados[0]))} style={actBtn("#3498db")} disabled={!!actionLoading}>✏️ Editar</button>
            <button onClick={() => { (async () => { try { const d = await apiFetch(`/api/admin/cobranca/tenants/${selecionados[0]}`); setModalDetail(d); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } })(); }} style={actBtn("#8e44ad")} disabled={!!actionLoading}>🔍 Detalhes</button>
            <button onClick={handleToggleStatus} style={actBtn(tenants.find(x => x.id === selecionados[0])?.status === "suspenso" ? "#27ae60" : "#e74c3c")} disabled={!!actionLoading}>
              {tenants.find(x => x.id === selecionados[0])?.status === "suspenso" ? "🔓 Reativar" : "🔒 Suspender"}
            </button>
          </>
        )}
        <button onClick={() => setModalBoleto(true)} style={actBtn("#e67e22")} disabled={!!actionLoading}>🧾 Gerar Boleto</button>
        <button onClick={handleEnviarAviso} style={actBtn("#27ae60")} disabled={!!actionLoading}>
          {actionLoading === "aviso" ? "Enviando..." : "📧 Enviar Aviso"}
        </button>
      </div>
    </div>
  );

  const cardStyle = (color) => ({ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", minWidth: 100, textAlign: "center", borderTop: `3px solid ${color}` });

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>💰 Assinaturas & Cobrança</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>{filtered.length} de {enriched.length} empresa(s)</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={handleRefreshAsaas} disabled={refreshLoading} style={{ ...actBtn("#1aae9e"), opacity: refreshLoading ? 0.6 : 1, fontSize: 11, padding: "7px 12px" }}>
            {refreshLoading ? "Sincronizando..." : "🔄 Atualizar do Asaas"}
          </button>
          <button onClick={handleProcessarLembretes} disabled={lembreteLoading} style={{ ...actBtn("#8e44ad"), opacity: lembreteLoading ? 0.6 : 1, fontSize: 11, padding: "7px 12px" }}>
            {lembreteLoading ? "Processando..." : "📧 Processar Lembretes"}
          </button>
        </div>
      </div>

      {/* Alerta de integração */}
      {asaasStatus && !asaasStatus.configurado && (
        <div style={{ background: "#fffbeb", border: "1px solid #fbbf2440", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>Integração Asaas não configurada</div>
            <div style={{ fontSize: 12, color: "#a16207" }}>Configure a API Key do Asaas no backend para emissão automática de cobranças (Boleto, PIX, Cartão).</div>
          </div>
        </div>
      )}

      {/* Cards de resumo */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        <div style={cardStyle("#2ecc71")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.ativas}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>ATIVAS</div></div>
        <div style={cardStyle("#3498db")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.trials}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>TRIALS</div></div>
        <div style={cardStyle("#e74c3c")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.suspensas}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>SUSPENSAS</div></div>
        <div style={{ ...cardStyle("#e74c3c"), borderTopColor: "#c0392b" }}><div style={{ fontSize: 20, fontWeight: 800, color: "#e74c3c" }}>{resumo.vencidas}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>VENCIDAS</div></div>
        <div style={cardStyle("#e67e22")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.vencendo7d}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>VENCENDO 7D</div></div>
        <div style={cardStyle("#2ecc71")}><div style={{ fontSize: 20, fontWeight: 800, color: "#2ecc71" }}>{resumo.pagasMes}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>PAGAS MÊS</div></div>
        <div style={cardStyle("#9b59b6")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.pix}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>PIX</div></div>
        <div style={cardStyle("#e67e22")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.boleto}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>BOLETO</div></div>
        <div style={cardStyle("#1aae9e")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.mensal}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>MENSAL</div></div>
        <div style={cardStyle("#1aae9e")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.semestral}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>SEMESTRAL</div></div>
        <div style={cardStyle("#1aae9e")}><div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{resumo.anual}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>ANUAL</div></div>
        <div style={{ ...cardStyle("#0d7a6e"), borderTopColor: "#0d7a6e" }}><div style={{ fontSize: 20, fontWeight: 800, color: "#0d7a6e" }}>{resumo.assinaturas}</div><div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>🔁 ASSINATURAS</div></div>
      </div>

      {/* Barra de filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input placeholder="Buscar por nome, email, CPF/CNPJ..." value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} style={{ ...selectStyle, width: 240, padding: "8px 12px" }} />
        <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Status: Todos</option>
          <option value="ativo">Ativo</option><option value="trial">Trial</option><option value="suspenso">Suspenso</option>
        </select>
        <select value={filtroSituacao} onChange={e => { setFiltroSituacao(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Cobrança: Todos</option>
          <option value="pago">Pago</option><option value="pendente">Pendente</option><option value="vencendo">Vencendo</option>
          <option value="vencido">Vencido</option><option value="sem_cobranca">Sem cobrança</option><option value="trial">Em trial</option><option value="falha">Falha</option>
        </select>
        <select value={filtroForma} onChange={e => { setFiltroForma(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Forma: Todos</option>
          <option value="pix">PIX</option><option value="boleto">Boleto</option><option value="cartao">Cartão</option><option value="manual">Manual</option>
        </select>
        <select value={filtroCiclo} onChange={e => { setFiltroCiclo(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Ciclo: Todos</option>
          <option value="mensal">Mensal</option><option value="semestral">Semestral</option><option value="anual">Anual</option>
        </select>
        <select value={filtroAssinatura || "todos"} onChange={e => { setFiltroAssinatura(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Assinatura: Todos</option>
          <option value="ativa">🔁 Com assinatura</option>
          <option value="sem">📋 Sem assinatura</option>
        </select>
        {planosDisponiveis.length > 1 && (
          <select value={filtroPlano} onChange={e => { setFiltroPlano(e.target.value); setPagina(1); }} style={selectStyle}>
            <option value="todos">Plano: Todos</option>
            {planosDisponiveis.map(p => <option key={p} value={p}>{planoLabels[p] || p}</option>)}
          </select>
        )}
        <select value={filtroVenc} onChange={e => { setFiltroVenc(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Vencimento: Todos</option>
          <option value="hoje">Hoje</option>
          <option value="3dias">Próximos 3 dias</option>
          <option value="7dias">Próximos 7 dias</option>
          <option value="15dias">Próximos 15 dias</option>
          <option value="30dias">Próximos 30 dias</option>
          <option value="vencidos">Vencidos</option>
          <option value="sem_vencimento">Sem vencimento</option>
        </select>
        <button onClick={() => { setFiltroAcao(!filtroAcao); setPagina(1); }} style={{ ...selectStyle, background: filtroAcao ? "#e74c3c" : "#fff", color: filtroAcao ? "#fff" : "#e74c3c", border: filtroAcao ? "1px solid #e74c3c" : "1px solid #e74c3c55", fontWeight: 700 }}>
          {filtroAcao ? "✕ Ação necessária" : "⚡ Ação necessária"}
        </button>
        {/* Toggle Lista / Agenda */}
        <div style={{ marginLeft: "auto", display: "flex", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 3, gap: 2 }}>
          <button
            onClick={() => setModoVista("lista")}
            style={{
              padding: "6px 14px", border: "none", borderRadius: 6,
              background: modoVista === "lista" ? "#1aae9e" : "transparent",
              color: modoVista === "lista" ? "#fff" : "#888",
              fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit"
            }}
          >☰ Lista</button>
          <button
            onClick={() => setModoVista("agenda")}
            style={{
              padding: "6px 14px", border: "none", borderRadius: 6,
              background: modoVista === "agenda" ? "#1aae9e" : "transparent",
              color: modoVista === "agenda" ? "#fff" : "#888",
              fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit"
            }}
          >📅 Agenda</button>
        </div>
      </div>

      {actionBar}

      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div> : modoVista === "agenda" ? (
        <AgendaVista tenants={filtered} onSelect={(t) => { (async () => { try { const d = await apiFetch(`/api/admin/cobranca/tenants/${t.id}`); setModalDetail(d); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } })(); }} />
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th style={thS}><input type="checkbox" checked={paginados.length > 0 && paginados.every(t => selecionados.includes(t.id))} onChange={toggleAll} style={{ accentColor: "#1aae9e", cursor: "pointer" }} /></th>
                <th style={thS}>Empresa</th>
                <th style={thS}>Plano</th>
                <th style={thS}>Ciclo</th>
                <th style={thS}>Forma Pgto</th>
                <th style={thS}>Valor</th>
                <th style={thS}>Próx. Vencimento</th>
                <th style={thS}>Sit. Cobrança</th>
                <th style={thS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>Nenhuma empresa encontrada</td></tr>
              ) : paginados.map(t => {
                const sel = selecionados.includes(t.id);
                return (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f0f0f0", background: sel ? "#f0faf8" : "", transition: "background .15s", cursor: "pointer" }}
                    onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "#fafafa"; }}
                    onMouseLeave={e => { if (!sel) e.currentTarget.style.background = ""; }}
                    onClick={(e) => { if (e.target.tagName !== "INPUT") toggleSel(t.id); }}>
                    <td className="mob-check" style={tdS}><input type="checkbox" checked={sel} onChange={() => toggleSel(t.id)} style={{ accentColor: "#1aae9e", cursor: "pointer" }} /></td>
                    <td className="mob-title" style={tdS}>
                      <div style={{ fontWeight: 700, color: "#1e2d4f", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                        {t.nome}
                        {t.asaas_subscription_id && (
                          <span title="Assinatura recorrente ativa" style={{ fontSize: 9, fontWeight: 800, background: "#0d7a6e", color: "#fff", padding: "2px 6px", borderRadius: 10, letterSpacing: 0.3 }}>🔁 AUTO</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#888" }}>{t.email}</div>
                      {(t.cpf_cnpj_cobranca || t.cnpj) && <div style={{ fontSize: 10, color: "#aaa" }}>{t.cpf_cnpj_cobranca || t.cnpj}</div>}
                    </td>
                    <td data-label="Plano" style={tdS}>
                      <span style={{ background: "#1aae9e18", color: "#1aae9e", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{planoLabels[t.plano] || t.plano || "—"}</span>
                    </td>
                    <td data-label="Ciclo" style={tdS}>
                      <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>{cicloLabels[t._ciclo] || "Mensal"}</span>
                    </td>
                    <td data-label="Forma Pgto" style={tdS}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: t._forma === "pix" ? "#8e44ad" : t._forma === "boleto" ? "#e67e22" : "#999", background: t._forma === "pix" ? "#8e44ad18" : t._forma === "boleto" ? "#e67e2218" : "#99999918", padding: "3px 8px", borderRadius: 8 }}>
                        {formaLabels[t._forma] || "Boleto"}
                      </span>
                    </td>
                    <td data-label="Valor" style={tdS}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f" }}>{fmt(t.valor_plano)}</span>
                    </td>
                    <td data-label="Próx. Vencimento" style={tdS}>
                      <VencimentoBadge data={t.prox_cobranca || t.ultimo_boleto_vencimento} />
                    </td>
                    <td data-label="Sit. Cobrança" style={tdS}><SituacaoBadge situacao={t._situacao} /></td>
                    <td data-label="Status" style={tdS}><StatusEmpresaBadge status={t.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px", borderTop: "1px solid #f0f0f0" }}>
              <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1} style={pgBtn}>← Anterior</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map(p => (
                <button key={p} onClick={() => setPagina(p)} style={{ ...pgBtn, background: p === pagina ? "#1aae9e" : "#f5f5f5", color: p === pagina ? "#fff" : "#555", fontWeight: p === pagina ? 700 : 400 }}>{p}</button>
              ))}
              <button onClick={() => setPagina(p => Math.min(totalPages, p + 1))} disabled={pagina >= totalPages} style={pgBtn}>Próxima →</button>
            </div>
          )}
        </div>
      )}

      {/* Modal Editar */}
      {modalEdit && <EditCobrancaModal tenant={modalEdit} onClose={() => setModalEdit(null)} onSave={async (data) => {
        try {
          await apiFetch(`/api/admin/cobranca/tenants/${modalEdit.id}`, { method: "PUT", body: data });
          addToast("Salvo!", "Dados de cobrança atualizados", "✅", "#2ecc71");
          setModalEdit(null);
          setSelecionados([]);
          load();
        } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
      }} />}

      {/* Modal Detalhes */}
      {modalDetail && <DetailCobrancaModal data={modalDetail} onClose={() => setModalDetail(null)} addToast={addToast} onReload={load} />}

      {/* Modal Gerar Boleto */}
      {modalBoleto && <GerarBoletoModal tenantIds={selecionados} tenants={tenants} onClose={() => setModalBoleto(false)} onGerado={() => { setModalBoleto(false); setSelecionados([]); load(); }} addToast={addToast} />}
    </div>
  );
}

/* ─── Modal Editar Cobrança (v2) ─── */

function EditCobrancaModal({ tenant, onClose, onSave }) {
  const [form, setForm] = useState({
    razao_social: tenant.razao_social || tenant.nome || "",
    cpf_cnpj_cobranca: tenant.cpf_cnpj_cobranca || tenant.cnpj || "",
    tel: tenant.tel || "",
    endereco_cobranca: tenant.endereco_cobranca || "",
    bairro_cobranca: tenant.bairro_cobranca || "",
    cidade_cobranca: tenant.cidade_cobranca || "",
    uf_cobranca: tenant.uf_cobranca || "",
    cep_cobranca: tenant.cep_cobranca || "",
    dia_vencimento: tenant.dia_vencimento || 10,
    forma_pagamento: tenant.forma_pagamento || "boleto",
    valor_plano: tenant.valor_plano || 0,
    prox_cobranca: tenant.prox_cobranca ? String(tenant.prox_cobranca).split("T")[0] : "",
    ciclo_cobranca: tenant.ciclo_cobranca || "mensal",
    plano: tenant.plano || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.cpf_cnpj_cobranca) return alert("CPF/CNPJ é obrigatório");
    const doc = form.cpf_cnpj_cobranca.replace(/\D/g, "");
    if (doc.length !== 11 && doc.length !== 14) return alert("CPF deve ter 11 dígitos ou CNPJ 14 dígitos");
    setSaving(true);
    try { await onSave(form); } catch (e) {}
    setSaving(false);
  };

  const secTitle = (text) => <div style={{ gridColumn: "1 / -1", fontSize: 12, fontWeight: 700, color: "#1e2d4f", borderBottom: "1px solid #eee", paddingBottom: 6, marginTop: 8 }}>{text}</div>;

  return (
    <Modal title={`Editar Cobrança — ${tenant.nome}`} onClose={onClose} width={640}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {secTitle("Dados da Empresa")}
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Razão Social / Nome</label><input value={form.razao_social} onChange={e => upd("razao_social", e.target.value)} /></div>
        <div><label style={labelStyle}>CPF/CNPJ Cobrança</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={form.cpf_cnpj_cobranca} onChange={e => upd("cpf_cnpj_cobranca", e.target.value)} placeholder="00.000.000/0000-00" style={{ flex: 1 }} />
            <button type="button" onClick={async () => { try { const d = await buscarCNPJ(form.cpf_cnpj_cobranca); upd("razao_social", d.razao_social); upd("endereco_cobranca", d.endereco); upd("bairro_cobranca", d.bairro); upd("cidade_cobranca", d.cidade); upd("uf_cobranca", d.uf); upd("cep_cobranca", d.cep); addToast("CNPJ", d.razao_social, "✅", "#2ecc71"); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 6, padding: "0 10px", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>🔍 Buscar</button>
          </div>
        </div>
        <div><label style={labelStyle}>Plano</label>
          <select value={form.plano} onChange={e => upd("plano", e.target.value)}>
            <option value="basico">Básico</option><option value="controla">Controla</option><option value="controla_plus">Controla Plus</option>
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>📱 Telefone / WhatsApp <span style={{ fontWeight: 400, color: "#888", fontSize: 11 }}>· usado para envio das mensagens de cobrança</span></label>
          <input
            value={form.tel}
            onChange={e => upd("tel", e.target.value)}
            placeholder="(11) 98765-4321"
          />
        </div>

        {secTitle("Endereço de Cobrança")}
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Endereço</label><input value={form.endereco_cobranca} onChange={e => upd("endereco_cobranca", e.target.value)} /></div>
        <div><label style={labelStyle}>Bairro</label><input value={form.bairro_cobranca} onChange={e => upd("bairro_cobranca", e.target.value)} /></div>
        <div><label style={labelStyle}>Cidade</label><input value={form.cidade_cobranca} onChange={e => upd("cidade_cobranca", e.target.value)} /></div>
        <div><label style={labelStyle}>UF</label><input value={form.uf_cobranca} onChange={e => upd("uf_cobranca", e.target.value)} maxLength={2} /></div>
        <div><label style={labelStyle}>CEP</label><input value={form.cep_cobranca} onChange={e => upd("cep_cobranca", e.target.value)} /></div>

        {secTitle("Configuração de Cobrança")}
        <div><label style={labelStyle}>Valor (R$)</label><input type="number" step="0.01" value={form.valor_plano} onChange={e => upd("valor_plano", e.target.value)} /></div>
        <div><label style={labelStyle}>Ciclo de Cobrança</label>
          <select value={form.ciclo_cobranca} onChange={e => upd("ciclo_cobranca", e.target.value)}>
            <option value="mensal">Mensal</option><option value="semestral">Semestral</option><option value="anual">Anual</option>
          </select>
        </div>
        <div><label style={labelStyle}>Forma de Pagamento</label>
          <select value={form.forma_pagamento} onChange={e => upd("forma_pagamento", e.target.value)}>
            <option value="boleto">Boleto</option><option value="pix">PIX</option><option value="cartao">Cartão</option><option value="manual">Manual</option>
          </select>
        </div>
        <div><label style={labelStyle}>Dia de Vencimento</label><input type="number" min={1} max={28} value={form.dia_vencimento} onChange={e => upd("dia_vencimento", e.target.value)} /></div>
        <div><label style={labelStyle}>Próximo Vencimento</label><input type="date" value={form.prox_cobranca} onChange={e => upd("prox_cobranca", e.target.value)} /></div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#1aae9e", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

/* ─── Helpers WhatsApp ─── */

// Normaliza telefone pra E.164 sem o "+" (formato wa.me)
// "(11) 98765-4321" → "5511987654321"
function telToWhatsApp(tel) {
  let n = (tel || "").toString().replace(/\D/g, "");
  if (!n) return "";
  // Se tem 10 ou 11 dígitos (DDD+número), adiciona 55 (Brasil)
  if (n.length === 10 || n.length === 11) n = "55" + n;
  // Se já começa com 55 e tem 12-13 dígitos, deixa
  return n;
}

// Saudação por hora do dia
function saudacaoAgora() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

// Formata referência YYYY-MM como "Mês/Ano" em PT-BR
function formatReferencia(ref) {
  if (!ref) return "";
  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const m = String(ref).match(/^(\d{4})-(\d{2})/);
  if (!m) return ref;
  const idx = parseInt(m[2], 10) - 1;
  return `${meses[idx] || m[2]}/${m[1]}`;
}

// Parser robusto de data — aceita "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss.sssZ", Date, etc.
// Retorna { date: Date, fmt: "DD/MM/YYYY" } ou null se inválido
function parseVencimento(v) {
  if (!v) return null;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const date = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    if (!isNaN(date.getTime())) return { date, fmt: `${m[3]}/${m[2]}/${m[1]}` };
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return { date: d, fmt: `${dd}/${mm}/${yyyy}` };
  }
  return null;
}

// Monta a mensagem do WhatsApp baseada no status do boleto e dias até vencimento
function montarMensagemBoleto(tenant, boleto) {
  const nome = tenant.nome || tenant.razao_social || "";
  const primeiroNome = nome.split(" ")[0] || nome;
  const valor = parseFloat(boleto.valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const vencP = parseVencimento(boleto.vencimento);
  const vencFmt = vencP ? vencP.fmt : "-";
  const ref = formatReferencia(boleto.referencia);

  // Calcular diferença de dias
  let diffDias = null;
  if (vencP) {
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const v = new Date(vencP.date); v.setHours(0,0,0,0);
    diffDias = Math.floor((v - hoje) / 86400000);
  }

  const link = boleto.asaas_invoice_url || boleto.asaas_bankslip_url || "";
  const linhaLink = link ? `\n*Pagamento (PIX, cartão ou boleto):*\n${link}\n` : "";

  let titulo = "";
  let abertura = "";
  let fecho = "";

  if (diffDias !== null && diffDias < 0) {
    // VENCIDO
    const diasAtraso = Math.abs(diffDias);
    titulo = `*Boleto em atraso - Controla Caçambas*`;
    abertura = `${saudacaoAgora()}, ${primeiroNome}! Tudo bem?\n\nIdentifiquei aqui que seu boleto da mensalidade do *Controla Caçambas* está em atraso há ${diasAtraso} dia${diasAtraso === 1 ? "" : "s"}. Pra manter o sistema ativo, segue o link de pagamento:`;
    fecho = `\nJá pagou? Me manda o comprovante aqui que eu confirmo manualmente. Se precisar parcelar ou tiver alguma dificuldade, podemos conversar.\n\nObrigado!`;
  } else if (diffDias !== null && diffDias === 0) {
    // VENCE HOJE
    titulo = `*Lembrete - Boleto vence hoje*`;
    abertura = `${saudacaoAgora()}, ${primeiroNome}!\n\nLembrete amigável: seu boleto da mensalidade do *Controla Caçambas* vence *hoje*. Pra evitar interrupção do sistema, segue o link de pagamento:`;
    fecho = `\nSe já pagou, pode desconsiderar. Qualquer coisa estou à disposição.`;
  } else if (diffDias !== null && diffDias <= 3) {
    // VENCE EM 1-3 DIAS
    titulo = `*Lembrete - Boleto vencendo*`;
    abertura = `${saudacaoAgora()}, ${primeiroNome}!\n\nLembrete amigável: seu boleto da mensalidade do *Controla Caçambas* vence em *${diffDias} dia${diffDias === 1 ? "" : "s"}*. Já está disponível para pagamento:`;
    fecho = `\nSe já pagou, pode desconsiderar. Qualquer dúvida me chama!`;
  } else {
    // VENCE EM MAIS DE 3 DIAS — boleto disponibilizado
    titulo = `*Boleto disponível - Controla Caçambas*`;
    abertura = `${saudacaoAgora()}, ${primeiroNome}! Tudo bem?\n\nEstá disponível seu boleto da mensalidade do *Controla Caçambas* — sistema de gestão de caçambas.`;
    fecho = `\nQualquer dúvida estou à disposição. Obrigado pela parceria!`;
  }

  const detalhes = [
    `*Referência:* ${ref}`,
    `*Vencimento:* ${vencFmt}`,
    `*Valor:* ${valor}`,
  ].join("\n");

  return `${titulo}\n\n${abertura}\n\n${detalhes}\n${linhaLink}${fecho}`;
}

// Abre o WhatsApp Web/App com a mensagem pronta
function abrirWhatsApp(tenant, boleto) {
  const numero = telToWhatsApp(tenant.tel || tenant.telefone || tenant.contato_tel || "");
  if (!numero) {
    alert("Tenant sem telefone cadastrado.\n\nCadastre o telefone na ficha do tenant antes de enviar pelo WhatsApp.");
    return false;
  }
  const texto = montarMensagemBoleto(tenant, boleto);
  const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

/* ─── Modal Detalhes (v2) ─── */

function DetailCobrancaModal({ data, onClose, addToast, onReload }) {
  const { tenant, boletos = [], logs = [] } = data;
  const [confirmPag, setConfirmPag] = useState(null);
  const [ativandoAssin, setAtivandoAssin] = useState(false);
  const [cancelandoAssin, setCancelandoAssin] = useState(false);
  const [modalAtivarAssin, setModalAtivarAssin] = useState(false);

  const temAssinatura = !!tenant?.asaas_subscription_id;

  const handleCancelarAssinatura = async () => {
    if (!window.confirm(`Cancelar assinatura recorrente de ${tenant.nome}?\n\nO Asaas não gerará mais cobranças automáticas a partir de agora. Cobranças já emitidas continuam válidas.`)) return;
    setCancelandoAssin(true);
    try {
      await apiFetch(`/api/admin/cobranca/tenants/${tenant.id}/cancelar-assinatura`, { method: "POST" });
      addToast("Assinatura cancelada", "Não haverá mais cobranças automáticas", "🛑", "#e74c3c");
      onReload();
      onClose();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setCancelandoAssin(false);
  };

  const handleSincronizarAssinatura = async () => {
    setAtivandoAssin(true);
    try {
      await apiFetch(`/api/admin/cobranca/tenants/${tenant.id}/atualizar-assinatura`, { method: "POST" });
      addToast("Assinatura sincronizada", "Valor/ciclo/forma atualizados no Asaas", "🔄", "#3498db");
      onReload();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setAtivandoAssin(false);
  };

  const [buscandoCobr, setBuscandoCobr] = useState(false);
  const handleBuscarCobrancas = async () => {
    setBuscandoCobr(true);
    try {
      const r = await apiFetch(`/api/admin/cobranca/tenants/${tenant.id}/sincronizar-cobrancas`, { method: "POST" });
      const msg = `${r.criados||0} nova(s), ${r.atualizados||0} atualizada(s) — ${r.total||0} no Asaas`;
      addToast("Cobranças sincronizadas", msg, "📥", "#16a34a");
      onReload();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setBuscandoCobr(false);
  };

  const handleConfirmarPag = async (boletoId) => {
    try {
      await apiFetch(`/api/admin/cobranca/boletos/${boletoId}/confirmar-pagamento`, { method: "POST" });
      addToast("Pagamento confirmado!", "", "✅", "#2ecc71");
      setConfirmPag(null);
      onReload();
      onClose();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
  };

  const handleCancelar = async (boletoId) => {
    if (!window.confirm("Tem certeza que deseja cancelar este boleto?")) return;
    try {
      await apiFetch(`/api/admin/cobranca/boletos/${boletoId}/cancelar`, { method: "POST" });
      addToast("Boleto cancelado", "", "🗑️", "#e74c3c");
      onReload();
      onClose();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
  };

  const stColors = { pendente: "#f39c12", emitido: "#3498db", pago: "#2ecc71", vencido: "#e74c3c", cancelado: "#999" };
  const ciclo = tenant?.ciclo_cobranca || "mensal";
  const cicloLabel = cicloLabels[ciclo] || "Mensal";
  const formaLabel = formaLabels[tenant?.forma_pagamento] || "Boleto";

  return (
    <Modal title={`Detalhes — ${tenant?.nome}`} onClose={onClose} width={740}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Plano</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e2d4f" }}>{planoLabels[tenant?.plano] || tenant?.plano}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1aae9e" }}>{fmt(tenant?.valor_plano)}</div>
        </div>
        <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Ciclo / Forma</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e2d4f" }}>{cicloLabel}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{formaLabel} · Dia {tenant?.dia_vencimento || "—"}</div>
        </div>
        <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Próx. Vencimento</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e2d4f" }}>{tenant?.prox_cobranca ? fmtDate(tenant.prox_cobranca) : "—"}</div>
        </div>
        <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>CPF/CNPJ</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", wordBreak: "break-all" }}>{tenant?.cpf_cnpj_cobranca || "—"}</div>
        </div>
      </div>

      {/* ─── Linha do WhatsApp do tenant ─── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: tenant?.tel ? "#e7f9ed" : "#fff5f5",
        border: `1px solid ${tenant?.tel ? "#b9e8c8" : "#f5c6c6"}`,
        borderRadius: 10, padding: "10px 14px", marginBottom: 18
      }}>
        <div>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px" }}>📱 WhatsApp do Cliente</div>
          {tenant?.tel ? (
            <div style={{ fontSize: 14, fontWeight: 700, color: "#128c7e", marginTop: 2 }}>{tenant.tel}</div>
          ) : (
            <div style={{ fontSize: 12, color: "#c0392b", marginTop: 2, fontWeight: 600 }}>
              ⚠ Sem WhatsApp cadastrado — clique em "Editar" para adicionar
            </div>
          )}
        </div>
        {tenant?.tel && (
          <a
            href={`https://wa.me/${telToWhatsApp(tenant.tel)}`}
            target="_blank"
            rel="noreferrer"
            style={{
              background: "#25d366", color: "#fff", textDecoration: "none",
              borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 6
            }}
          >
            💬 Abrir conversa
          </a>
        )}
      </div>

      {/* ─── Seção de Assinatura Recorrente ─── */}
      <div style={{
        background: temAssinatura ? "linear-gradient(135deg,#f0faf8,#e0f7f2)" : "#fff8e1",
        border: `1px solid ${temAssinatura ? "#1aae9e44" : "#f39c1244"}`,
        borderRadius: 12, padding: 16, marginBottom: 20
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: temAssinatura ? "#0d7a6e" : "#92400e", display: "flex", alignItems: "center", gap: 6 }}>
              {temAssinatura ? "🔁 Assinatura Recorrente Ativa" : "📋 Cobrança Manual"}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
              {temAssinatura
                ? `O Asaas gera cobrança automaticamente todo ${cicloLabel.toLowerCase()} e envia para o cliente.`
                : "Você precisa emitir cada cobrança manualmente. Ative a assinatura para automatizar."}
            </div>
            {temAssinatura && (
              <div style={{ fontSize: 10, color: "#888", marginTop: 4, fontFamily: "monospace" }}>
                sub_id: {tenant.asaas_subscription_id}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {temAssinatura ? (
              <>
                <button
                  onClick={handleBuscarCobrancas}
                  disabled={buscandoCobr || ativandoAssin || cancelandoAssin}
                  title="Busca no Asaas as cobranças geradas pela assinatura e cria/atualiza os boletos locais"
                  style={{ fontSize: 12, background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", opacity: (buscandoCobr || ativandoAssin || cancelandoAssin) ? 0.6 : 1 }}
                >
                  {buscandoCobr ? "Buscando..." : "📥 Buscar Cobranças"}
                </button>
                <button
                  onClick={handleSincronizarAssinatura}
                  disabled={ativandoAssin || cancelandoAssin || buscandoCobr}
                  title="Atualiza no Asaas o valor/ciclo/forma de pagamento desta assinatura"
                  style={{ fontSize: 12, background: "#3498db", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", opacity: (ativandoAssin || cancelandoAssin || buscandoCobr) ? 0.6 : 1 }}
                >
                  {ativandoAssin ? "..." : "🔄 Atualizar Dados"}
                </button>
                <button
                  onClick={handleCancelarAssinatura}
                  disabled={ativandoAssin || cancelandoAssin || buscandoCobr}
                  style={{ fontSize: 12, background: "#e74c3c", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", opacity: (ativandoAssin || cancelandoAssin || buscandoCobr) ? 0.6 : 1 }}
                >
                  {cancelandoAssin ? "..." : "🛑 Cancelar Assinatura"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setModalAtivarAssin(true)}
                disabled={ativandoAssin}
                style={{ fontSize: 13, background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 800, cursor: "pointer", opacity: ativandoAssin ? 0.6 : 1 }}
              >
                ✨ Ativar Assinatura Recorrente
              </button>
            )}
          </div>
        </div>
      </div>

      {modalAtivarAssin && (
        <AtivarAssinaturaModal
          tenant={tenant}
          onClose={() => setModalAtivarAssin(false)}
          onAtivado={() => { setModalAtivarAssin(false); onReload(); onClose(); }}
          addToast={addToast}
        />
      )}

      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e2d4f", marginBottom: 10, marginTop: 6 }}>Boletos ({boletos.length})</h3>
      {boletos.length === 0 ? <div style={{ color: "#aaa", fontSize: 13, padding: 16 }}>Nenhum boleto gerado</div> : (
        <div style={{ maxHeight: 280, overflow: "auto", marginBottom: 16 }}>
          {boletos.map(b => (
            <div key={b.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <span style={{ fontWeight: 700, color: "#1e2d4f", fontSize: 13 }}>Ref: {b.referencia}</span>
                <span style={{ marginLeft: 12, fontWeight: 700, color: "#333" }}>{fmt(b.valor)}</span>
                <span style={{ marginLeft: 12, fontSize: 12, color: "#888" }}>Venc: {fmtDate(b.vencimento)}</span>
                {b.data_pagamento && <span style={{ marginLeft: 8, fontSize: 11, color: "#2ecc71" }}>Pago: {fmtDate(b.data_pagamento)}</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: stColors[b.status] || "#888", padding: "3px 10px", background: (stColors[b.status] || "#888") + "18", borderRadius: 12 }}>{b.status}</span>
                {b.asaas_invoice_url && (
                  <a href={b.asaas_invoice_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, background: "#3498db22", color: "#3498db", border: "none", borderRadius: 6, padding: "4px 8px", textDecoration: "none", fontWeight: 700 }}>🔗 Pagar</a>
                )}
                {(b.status === "pendente" || b.status === "emitido" || b.status === "vencido") && (
                  <button
                    onClick={() => abrirWhatsApp(tenant, b)}
                    title="Abrir WhatsApp com mensagem pronta para o cliente"
                    style={{ fontSize: 11, background: "#25d36622", color: "#128c7e", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontSize: 12 }}>📱</span> WhatsApp
                  </button>
                )}
                {b.asaas_payment_id && (b.status === "pendente" || b.status === "emitido") && (
                  <button onClick={async () => { try { const r = await apiFetch(`/api/admin/cobranca/boletos/${b.id}/consultar-asaas`, { method: "POST" }); addToast("Asaas", `Status: ${r.asaasStatus} → ${r.statusInterno}`, "🔄", "#3498db"); loadDetail(modalDetail.id); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } }} style={{ fontSize: 11, background: "#8e44ad22", color: "#8e44ad", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontWeight: 700 }}>🔄 Asaas</button>
                )}
                {(b.status === "pendente" || b.status === "emitido") && (
                  <>
                    <button onClick={() => setConfirmPag(b.id)} style={{ fontSize: 11, background: "#2ecc7122", color: "#2ecc71", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontWeight: 700 }}>✓ Pago</button>
                    <button onClick={() => handleCancelar(b.id)} style={{ fontSize: 11, background: "#e74c3c22", color: "#e74c3c", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontWeight: 700 }}>✕</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmPag && (
        <div style={{ background: "#fff8e1", border: "1px solid #f39c12", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <p style={{ fontWeight: 700, color: "#e67e22", margin: "0 0 10px" }}>Confirmar pagamento deste boleto?</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => handleConfirmarPag(confirmPag)} style={{ background: "#2ecc71", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer" }}>Sim, confirmar</button>
            <button onClick={() => setConfirmPag(null)} style={{ background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 8, padding: "8px 16px", cursor: "pointer" }}>Cancelar</button>
          </div>
        </div>
      )}

      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e2d4f", marginBottom: 10, marginTop: 6 }}>Histórico de Ações</h3>
      {logs.length === 0 ? <div style={{ color: "#aaa", fontSize: 13, padding: 16 }}>Nenhum registro</div> : (
        <div style={{ maxHeight: 180, overflow: "auto" }}>
          {logs.map(l => (
            <div key={l.id} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f8f8f8", fontSize: 12 }}>
              <span style={{ color: "#888", whiteSpace: "nowrap" }}>{fmtDate(l.created_at)}</span>
              <span style={{ fontWeight: 600, color: "#555" }}>{l.acao}</span>
              <span style={{ color: "#888" }}>{l.detalhes}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ─── Modal Ativar Assinatura Recorrente ─── */

function AtivarAssinaturaModal({ tenant, onClose, onAtivado, addToast }) {
  // Data padrão: prox_cobranca do tenant OU próximo mês no dia de vencimento
  const calcDataDefault = () => {
    if (tenant.prox_cobranca) return String(tenant.prox_cobranca).split("T")[0];
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    const dia = parseInt(tenant.dia_vencimento) || 10;
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(dia, lastDay));
    return d.toISOString().split("T")[0];
  };

  const [nextDueDate, setNextDueDate] = useState(calcDataDefault());
  const [loading, setLoading] = useState(false);

  const ciclo = tenant.ciclo_cobranca || "mensal";
  const forma = tenant.forma_pagamento || "boleto";
  const valor = parseFloat(tenant.valor_plano) || 0;

  const handleAtivar = async () => {
    if (!nextDueDate) return alert("Informe a data da primeira cobrança");
    if (!valor || valor <= 0) return alert("Valor do plano do tenant está zerado");
    const doc = (tenant.cpf_cnpj_cobranca || tenant.cnpj || "").replace(/\D/g, "");
    if (!doc) return alert("CPF/CNPJ de cobrança é obrigatório");

    setLoading(true);
    try {
      await apiFetch(`/api/admin/cobranca/tenants/${tenant.id}/ativar-assinatura`, {
        method: "POST",
        body: { nextDueDate }
      });
      addToast("Assinatura ativada!", `Primeira cobrança em ${fmtDate(nextDueDate)}`, "🎉", "#2ecc71");
      onAtivado();
    } catch (err) {
      addToast("Erro", err.message, "❌", "#e74c3c");
    }
    setLoading(false);
  };

  return (
    <Modal title={`Ativar Assinatura — ${tenant.nome}`} onClose={onClose} width={540}>
      <div style={{ background: "#f0faf8", border: "1px solid #1aae9e44", borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0d7a6e", marginBottom: 6 }}>
          Como funciona a assinatura recorrente
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          <li>O Asaas emite a cobrança automaticamente na data definida</li>
          <li>Repete a cada ciclo (mensal/semestral/anual) no mesmo dia</li>
          <li>Envia email ao cliente com link de pagamento</li>
          <li>Aplica multa de 2% e juros de 1%/mês em caso de atraso</li>
          <li>Você recebe confirmação automática quando pago</li>
        </ul>
      </div>

      <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Valor</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1aae9e" }}>{fmt(valor)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Ciclo</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e2d4f" }}>{cicloLabels[ciclo] || "Mensal"}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Forma</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e2d4f" }}>{formaLabels[forma] || "Boleto"}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>CPF/CNPJ</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", wordBreak: "break-all" }}>
              {tenant.cpf_cnpj_cobranca || tenant.cnpj || <span style={{color:"#e74c3c"}}>⚠️ não cadastrado</span>}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Data da 1ª Cobrança</label>
        <input
          type="date"
          value={nextDueDate}
          onChange={e => setNextDueDate(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", fontSize: 14, border: "1px solid #ddd", borderRadius: 8 }}
        />
        <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
          💡 Como o tenant já pagou este mês, a próxima cobrança deve ser no próximo vencimento. Depois repete automaticamente a cada ciclo.
        </div>
      </div>

      <div style={{ background: "#fff8e1", border: "1px solid #f39c1244", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: "#92400e" }}>
        ⚠️ <strong>Atenção:</strong> após ativar, o Asaas passa a controlar as cobranças. Se houver boleto pendente deste mês, cancele-o antes para evitar cobrança duplicada.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} disabled={loading} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit" }}>
          Cancelar
        </button>
        <button
          onClick={handleAtivar}
          disabled={loading}
          style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#1aae9e", color: "#fff", fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: loading ? 0.6 : 1 }}
        >
          {loading ? "Ativando..." : "✨ Ativar Assinatura"}
        </button>
      </div>
    </Modal>
  );
}

/* ─── Modal Gerar Boleto ─── */

function GerarBoletoModal({ tenantIds, tenants, onClose, onGerado, addToast }) {
  const now = new Date();
  const mesRef = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [referencia, setReferencia] = useState(mesRef);
  const [vencimento, setVencimento] = useState("");
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Auto-calcular vencimento baseado na referência + dia dos tenants
  useEffect(() => {
    if (referencia && !vencimento) {
      const t = tenants.find(x => tenantIds.includes(x.id));
      const dia = t?.dia_vencimento || 10;
      setVencimento(`${referencia}-${String(dia).padStart(2, "0")}`);
    }
  }, [referencia, tenants, tenantIds, vencimento]);

  const selecionadosInfo = tenants.filter(t => tenantIds.includes(t.id));
  const totalValor = selecionadosInfo.reduce((s, t) => s + (parseFloat(t.valor_plano) || 0), 0);

  const handleGerar = async () => {
    if (!referencia || !vencimento) return alert("Preencha a referência e o vencimento");
    setGerando(true);
    try {
      const res = await apiFetch("/api/admin/cobranca/gerar-boleto", {
        method: "POST",
        body: { tenantIds, referencia, vencimento }
      });
      setResultado(res);
      const ok = (res.resultados || []).filter(r => r.ok).length;
      addToast("Boletos gerados!", `${ok} de ${tenantIds.length} boleto(s) gerado(s)`, "🧾", "#1aae9e");
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setGerando(false);
  };

  return (
    <Modal title="Gerar Boleto(s)" onClose={onClose} width={560}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ background: "#f0faf8", border: "1px solid #1aae9e44", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1aae9e" }}>{tenantIds.length} empresa(s) selecionada(s)</div>
          {selecionadosInfo.map(t => (
            <div key={t.id} style={{ fontSize: 13, color: "#333", marginTop: 4 }}>• {t.nome} — {fmt(t.valor_plano)} ({cicloLabels[t.ciclo_cobranca] || "Mensal"})</div>
          ))}
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800, color: "#1e2d4f" }}>Total: {fmt(totalValor)}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div><label style={labelStyle}>Mês de Referência</label><input type="month" value={referencia} onChange={e => setReferencia(e.target.value)} /></div>
          <div><label style={labelStyle}>Data de Vencimento</label><input type="date" value={vencimento} onChange={e => setVencimento(e.target.value)} /></div>
        </div>
      </div>

      {resultado && (
        <div style={{ marginBottom: 16 }}>
          {(resultado.resultados || []).map((r, i) => {
            const t = tenants.find(x => x.id === r.tenantId);
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, marginBottom: 4, background: r.ok ? "#f0faf8" : "#fef0f0" }}>
                <span style={{ fontSize: 13, color: "#333" }}>{t?.nome || `#${r.tenantId}`}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: r.ok ? "#2ecc71" : "#e74c3c" }}>{r.ok ? "✓ Gerado" : `✕ ${r.error}`}</span>
              </div>
            );
          })}
          {!resultado.asaasAtivo && <div style={{ fontSize: 12, color: "#f39c12", marginTop: 8 }}>⚠️ Asaas não configurado — cobranças criadas como pendentes (manual)</div>}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit" }}>{resultado ? "Fechar" : "Cancelar"}</button>
        {!resultado && <button onClick={handleGerar} disabled={gerando} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#e67e22", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: gerando ? 0.6 : 1 }}>
          {gerando ? "Gerando..." : "🧾 Gerar Boleto(s)"}
        </button>}
        {resultado && <button onClick={onGerado} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#1aae9e", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Concluir</button>}
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   EMAILS ENVIADOS PAGE (Admin)
   ══════════════════════════════════════════════ */

const tipoEmailLabels = {
  boas_vindas: "Boas-vindas Empresa",
  boas_vindas_usuario: "Boas-vindas Usuário",
  reset_senha: "Reset de Senha",
  reset_admin: "Reset Admin",
  lembrete_cobranca: "Lembrete Cobrança",
  pagamento_confirmado: "Pagamento Confirmado",
  aviso_cobranca: "Aviso Cobrança",
  suporte: "Suporte",
  lead: "Lead Site",
};

const tipoEmailColors = {
  boas_vindas: "#2ecc71",
  boas_vindas_usuario: "#27ae60",
  reset_senha: "#e67e22",
  reset_admin: "#e67e22",
  lembrete_cobranca: "#f39c12",
  pagamento_confirmado: "#2ecc71",
  aviso_cobranca: "#9b59b6",
  suporte: "#3498db",
  lead: "#1aae9e",
};

function EmailsPage({ addToast }) {
  const [emails, setEmails] = useState([]);
  const [resumo, setResumo] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [pagina, setPagina] = useState(1);
  const PER_PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroTipo !== "todos") params.set("tipo", filtroTipo);
      if (busca) params.set("busca", busca);
      params.set("limit", PER_PAGE);
      params.set("offset", (pagina - 1) * PER_PAGE);
      const data = await apiFetch(`/api/admin/emails?${params.toString()}`);
      setEmails(data.emails || []);
      setTotal(data.total || 0);
      setResumo(data.resumo || []);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [addToast, filtroTipo, busca, pagina]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const fmtData = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("pt-BR") + " " + dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const selectStyle = { padding: "7px 10px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", color: "#333" };

  const totalEnviados = resumo.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>📧 Emails Enviados</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>{total} email(s) registrado(s)</p>
      </div>

      {/* Cards de resumo por tipo */}
      {resumo.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 16px", minWidth: 80, textAlign: "center", borderTop: "3px solid #1e2d4f" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{totalEnviados}</div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>TOTAL</div>
          </div>
          {resumo.map(r => {
            const cor = tipoEmailColors[r.tipo] || "#888";
            return (
              <div key={r.tipo} onClick={() => { setFiltroTipo(r.tipo); setPagina(1); }} style={{ background: filtroTipo === r.tipo ? cor + "18" : "#fff", border: `1px solid ${filtroTipo === r.tipo ? cor : "#e5e7eb"}`, borderRadius: 10, padding: "10px 16px", minWidth: 80, textAlign: "center", borderTop: `3px solid ${cor}`, cursor: "pointer", transition: "all .15s" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{r.total}</div>
                <div style={{ fontSize: 9, color: "#888", fontWeight: 600, whiteSpace: "nowrap" }}>{(tipoEmailLabels[r.tipo] || r.tipo).toUpperCase()}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input placeholder="Buscar por destinatário, assunto ou detalhes..." value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} style={{ ...selectStyle, width: 300, padding: "8px 12px" }} />
        <select value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPagina(1); }} style={selectStyle}>
          <option value="todos">Tipo: Todos</option>
          {Object.entries(tipoEmailLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {filtroTipo !== "todos" && (
          <button onClick={() => { setFiltroTipo("todos"); setPagina(1); }} style={{ ...selectStyle, background: "#e74c3c", color: "#fff", border: "1px solid #e74c3c", fontWeight: 700, cursor: "pointer" }}>✕ Limpar filtro</button>
        )}
      </div>

      {/* Tabela */}
      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div> : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th style={thS}>Data</th>
                <th style={thS}>Tipo</th>
                <th style={thS}>Destinatário</th>
                <th style={thS}>Assunto</th>
                <th style={thS}>Empresa</th>
                <th style={thS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>Nenhum email encontrado</td></tr>
              ) : emails.map(e => {
                const cor = tipoEmailColors[e.tipo] || "#888";
                return (
                  <tr key={e.id} style={{ borderBottom: "1px solid #f0f0f0" }}
                    onMouseEnter={ev => ev.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={ev => ev.currentTarget.style.background = ""}>
                    <td data-label="Data" style={tdS}><span style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>{fmtData(e.created_at)}</span></td>
                    <td data-label="Tipo" style={tdS}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: cor, background: cor + "18", padding: "3px 8px", borderRadius: 8, whiteSpace: "nowrap" }}>
                        {tipoEmailLabels[e.tipo] || e.tipo}
                      </span>
                    </td>
                    <td className="mob-title" style={tdS}><span style={{ fontSize: 12, color: "#333" }}>{e.destinatario}</span></td>
                    <td data-label="Assunto" style={tdS}>
                      <div style={{ fontSize: 12, color: "#1e2d4f", fontWeight: 600, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.assunto}</div>
                      {e.detalhes && <div style={{ fontSize: 11, color: "#999", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.detalhes}</div>}
                    </td>
                    <td data-label="Empresa" style={tdS}><span style={{ fontSize: 12, color: "#555" }}>{e.tenant_nome || "—"}</span></td>
                    <td data-label="Status" style={tdS}>
                      {e.status === "erro" ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#e74c3c", background: "#e74c3c18", padding: "3px 8px", borderRadius: 8 }} title={e.erro || ""}>Erro</span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#2ecc71", background: "#2ecc7118", padding: "3px 8px", borderRadius: 8 }}>Enviado</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px", borderTop: "1px solid #f0f0f0" }}>
              <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1} style={pgBtn}>← Anterior</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map(p => (
                <button key={p} onClick={() => setPagina(p)} style={{ ...pgBtn, background: p === pagina ? "#1aae9e" : "#f5f5f5", color: p === pagina ? "#fff" : "#555", fontWeight: p === pagina ? 700 : 400 }}>{p}</button>
              ))}
              <button onClick={() => setPagina(p => Math.min(totalPages, p + 1))} disabled={pagina >= totalPages} style={pgBtn}>Próxima →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   IA PERGUNTAS PAGE (Admin)
   ══════════════════════════════════════════════ */

function IAPage({ addToast }) {
  const [perguntas, setPerguntas] = useState([]);
  const [resumo, setResumo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTenant, setFiltroTenant] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = filtroTenant ? `/api/admin/ia/perguntas?tenantId=${filtroTenant}` : "/api/admin/ia/perguntas";
        const data = await apiFetch(url);
        setPerguntas(data.perguntas || []);
        setResumo(data.resumoMensal || []);
      } catch (err) {
        addToast("Erro", "Não foi possível carregar perguntas da IA", "❌", "#e74c3c");
      }
      setLoading(false);
    })();
  }, [filtroTenant, addToast]);

  const tenants = [...new Set(perguntas.map(p => p.tenant_id))];
  const tenantNames = {};
  perguntas.forEach(p => { tenantNames[p.tenant_id] = p.tenant_nome || `Tenant #${p.tenant_id}`; });

  const fmtData = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("pt-BR") + " " + dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>🤖 Controla.AI — Perguntas</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Todas as perguntas feitas à IA pelos tenants</p>
        </div>
        <select value={filtroTenant} onChange={e => setFiltroTenant(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, background: "#fff" }}>
          <option value="">Todos os tenants</option>
          {tenants.map(tid => (
            <option key={tid} value={tid}>{tenantNames[tid]}</option>
          ))}
        </select>
      </div>

      {/* Resumo mensal */}
      {resumo.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
          {resumo.map((r, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", minWidth: 180 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>{r.tenant_nome}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1e2d4f", margin: "4px 0" }}>{r.total_mes}</div>
              <div style={{ fontSize: 11, color: "#aaa" }}>perguntas este mês (limite: 50)</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div>
      ) : perguntas.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Nenhuma pergunta registrada.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {perguntas.map(p => (
            <div key={p.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
              <div onClick={() => setExpanded(expanded === p.id ? null : p.id)} style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1e2d4f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.pergunta}</div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>
                    {p.tenant_nome} · {p.usuario_nome} · {fmtData(p.created_at)} · {p.tokens_entrada + p.tokens_saida} tokens
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" style={{ transform: expanded === p.id ? "rotate(90deg)" : "rotate(0)", transition: "transform .15s", flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
              </div>
              {expanded === p.id && (
                <div style={{ padding: "0 18px 16px", borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#1aae9e", marginBottom: 4 }}>PERGUNTA:</div>
                    <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{p.pergunta}</div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", marginBottom: 4 }}>RESPOSTA:</div>
                    <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.5, background: "#f8f9fa", padding: 12, borderRadius: 8 }}>{p.resposta}</div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
                    Modelo: {p.modelo} · Tokens entrada: {p.tokens_entrada} · Tokens saída: {p.tokens_saida}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   REUNIÕES — Cronograma de Reuniões
   ══════════════════════════════════════════════ */

function ReunioesPage({ addToast }) {
  const [reunioes, setReunioes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("todas");
  const [viewMode, setViewMode] = useState("lista"); // lista | calendario

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroStatus !== "todas" ? `?status=${filtroStatus}` : "";
      const data = await apiFetch(`/api/admin/reunioes${params}`);
      setReunioes(data);
    } catch { addToast("Erro", "Não foi possível carregar reuniões", "❌", "#e74c3c"); }
    setLoading(false);
  }, [filtroStatus, addToast]);

  useEffect(() => { load(); }, [load]);

  const fmtData = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const fmtHora = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };
  const fmtDiaSemana = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("pt-BR", { weekday: "long" });
  };

  const statusColors = { agendada: "#3498db", realizada: "#2ecc71", cancelada: "#e74c3c", reagendada: "#f39c12" };
  const statusLabels = { agendada: "Agendada", realizada: "Realizada", cancelada: "Cancelada", reagendada: "Reagendada" };
  const localLabels = { online: "Online", presencial: "Presencial", telefone: "Telefone" };
  const localIcons = { online: "💻", presencial: "📍", telefone: "📞" };

  const handleStatus = async (id, status) => {
    try {
      await apiFetch(`/api/admin/reunioes/${id}/status`, { method: "PATCH", body: { status } });
      addToast("Sucesso", `Reunião marcada como ${statusLabels[status]}`, "✅", "#2ecc71");
      load();
    } catch { addToast("Erro", "Erro ao atualizar status", "❌", "#e74c3c"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta reunião?")) return;
    try {
      await apiFetch(`/api/admin/reunioes/${id}`, { method: "DELETE" });
      addToast("Sucesso", "Reunião excluída", "🗑️", "#888");
      load();
    } catch { addToast("Erro", "Erro ao excluir", "❌", "#e74c3c"); }
  };

  // Agrupar por data para visualização
  const reunioesPorDia = {};
  reunioes.forEach(r => {
    const dia = fmtData(r.data_reuniao);
    if (!reunioesPorDia[dia]) reunioesPorDia[dia] = [];
    reunioesPorDia[dia].push(r);
  });

  // Stats
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const proximasHoje = reunioes.filter(r => { const d = new Date(r.data_reuniao); d.setHours(0,0,0,0); return d.getTime() === hoje.getTime() && r.status === "agendada"; });
  const estaSemana = reunioes.filter(r => { const d = new Date(r.data_reuniao); const diff = (d - hoje) / 86400000; return diff >= 0 && diff < 7 && r.status === "agendada"; });
  const totalAgendadas = reunioes.filter(r => r.status === "agendada").length;
  const totalRealizadas = reunioes.filter(r => r.status === "realizada").length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>📅 Cronograma de Reuniões</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Gerencie reuniões com empresas interessadas no Controla</p>
        </div>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "#1aae9e", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 10px rgba(26,174,158,.3)" }}>
          + Nova Reunião
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Hoje</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#1aae9e", marginTop: 4 }}>{proximasHoje.length}</div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>reuniões agendadas</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Esta Semana</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#3498db", marginTop: 4 }}>{estaSemana.length}</div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>próximos 7 dias</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Agendadas</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#f39c12", marginTop: 4 }}>{totalAgendadas}</div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>pendentes</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Realizadas</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#2ecc71", marginTop: 4 }}>{totalRealizadas}</div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>concluídas</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        {["todas", "agendada", "realizada", "cancelada", "reagendada"].map(s => (
          <button key={s} onClick={() => setFiltroStatus(s)} style={{ padding: "7px 16px", borderRadius: 8, border: filtroStatus === s ? "2px solid " + (statusColors[s] || "#1aae9e") : "1px solid #dde1e6", background: filtroStatus === s ? (statusColors[s] || "#1aae9e") + "18" : "#fff", color: filtroStatus === s ? (statusColors[s] || "#1aae9e") : "#666", fontSize: 13, fontWeight: filtroStatus === s ? 700 : 500, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" }}>
            {s === "todas" ? "Todas" : statusLabels[s]}
          </button>
        ))}
      </div>

      {/* Lista de Reuniões */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>Carregando...</div>
      ) : reunioes.length === 0 ? (
        <div style={{ textAlign: "center", padding: 80, color: "#888" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Nenhuma reunião encontrada</div>
          <div style={{ fontSize: 13 }}>Clique em "Nova Reunião" para agendar</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {Object.entries(reunioesPorDia).map(([dia, items]) => (
            <div key={dia}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", textTransform: "capitalize" }}>
                  {items[0] && fmtDiaSemana(items[0].data_reuniao)}
                </div>
                <div style={{ fontSize: 13, color: "#888" }}>{dia}</div>
                <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map(r => (
                  <div key={r.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 22px", display: "flex", alignItems: "flex-start", gap: 16, boxShadow: "0 1px 3px rgba(0,0,0,.04)", borderLeft: `4px solid ${statusColors[r.status] || "#ddd"}`, transition: "box-shadow .2s" }}>
                    {/* Hora */}
                    <div style={{ minWidth: 60, textAlign: "center", flexShrink: 0 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#1e2d4f" }}>{fmtHora(r.data_reuniao)}</div>
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{r.duracao_min}min</div>
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: "#1e2d4f" }}>{r.empresa}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: (statusColors[r.status] || "#ddd") + "20", color: statusColors[r.status] || "#888" }}>{statusLabels[r.status] || r.status}</span>
                        <span style={{ fontSize: 11, color: "#888", display: "flex", alignItems: "center", gap: 4 }}>{localIcons[r.local_tipo]} {localLabels[r.local_tipo]}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13, color: "#666" }}>
                        {r.contato && <span>👤 {r.contato}</span>}
                        {r.telefone && <span>📱 {r.telefone}</span>}
                        {r.email && <span>✉️ {r.email}</span>}
                        {r.local_detalhe && <span style={{ color: "#888" }}>📍 {r.local_detalhe}</span>}
                      </div>
                      {r.notas && <div style={{ fontSize: 12, color: "#888", marginTop: 8, fontStyle: "italic", background: "#f8f9fa", padding: "8px 12px", borderRadius: 8 }}>{r.notas}</div>}
                    </div>
                    {/* Ações */}
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {r.status === "agendada" && (
                        <button onClick={() => handleStatus(r.id, "realizada")} title="Marcar como realizada" style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>✅</button>
                      )}
                      {r.status === "agendada" && (
                        <button onClick={() => handleStatus(r.id, "cancelada")} title="Cancelar" style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>❌</button>
                      )}
                      <button onClick={() => { setEditItem(r); setShowModal(true); }} title="Editar" style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>✏️</button>
                      <button onClick={() => handleDelete(r.id)} title="Excluir" style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Nova/Editar Reunião */}
      {showModal && <ReuniaoModal item={editItem} onClose={() => { setShowModal(false); setEditItem(null); }} onSave={() => { setShowModal(false); setEditItem(null); load(); addToast("Sucesso", editItem ? "Reunião atualizada" : "Reunião agendada", "✅", "#2ecc71"); }} />}
    </div>
  );
}

function ReuniaoModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    empresa: item?.empresa || "",
    contato: item?.contato || "",
    telefone: item?.telefone || "",
    email: item?.email || "",
    data_reuniao: item?.data_reuniao ? new Date(item.data_reuniao).toISOString().slice(0, 16) : "",
    duracao_min: item?.duracao_min || 30,
    local_tipo: item?.local_tipo || "online",
    local_detalhe: item?.local_detalhe || "",
    status: item?.status || "agendada",
    notas: item?.notas || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.empresa || !form.data_reuniao) return;
    setSaving(true);
    try {
      const url = item ? `/api/admin/reunioes/${item.id}` : "/api/admin/reunioes";
      const method = item ? "PUT" : "POST";
      await apiFetch(url, { method, body: form });
      onSave();
    } catch { alert("Erro ao salvar reunião"); }
    setSaving(false);
  };

  const labelSt = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#888", marginBottom: 6, textTransform: "uppercase" };
  const inputSt = { width: "100%", boxSizing: "border-box", background: "#f5f7fa", border: "1px solid #dde1e6", color: "#333", padding: "12px 14px", borderRadius: 8, fontSize: 14, fontFamily: "inherit" };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 540, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.2)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "22px 28px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e2d4f" }}>{item ? "✏️ Editar Reunião" : "📅 Nova Reunião"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>✕</button>
        </div>
        <div style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelSt}>Empresa *</label>
            <input value={form.empresa} onChange={e => setForm({...form, empresa: e.target.value})} placeholder="Nome da empresa" style={inputSt} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={labelSt}>Contato</label>
              <input value={form.contato} onChange={e => setForm({...form, contato: e.target.value})} placeholder="Nome do contato" style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Telefone</label>
              <input value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})} placeholder="(11) 99999-9999" style={inputSt} />
            </div>
          </div>
          <div>
            <label style={labelSt}>Email</label>
            <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@empresa.com" type="email" style={inputSt} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={labelSt}>Data e Hora *</label>
              <input type="datetime-local" value={form.data_reuniao} onChange={e => setForm({...form, data_reuniao: e.target.value})} style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Duração (min)</label>
              <select value={form.duracao_min} onChange={e => setForm({...form, duracao_min: parseInt(e.target.value)})} style={inputSt}>
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={45}>45 minutos</option>
                <option value={60}>1 hora</option>
                <option value={90}>1h30</option>
                <option value={120}>2 horas</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={labelSt}>Tipo</label>
              <select value={form.local_tipo} onChange={e => setForm({...form, local_tipo: e.target.value})} style={inputSt}>
                <option value="online">💻 Online</option>
                <option value="presencial">📍 Presencial</option>
                <option value="telefone">📞 Telefone</option>
              </select>
            </div>
            <div>
              <label style={labelSt}>Local / Link</label>
              <input value={form.local_detalhe} onChange={e => setForm({...form, local_detalhe: e.target.value})} placeholder="Link do meet, endereço..." style={inputSt} />
            </div>
          </div>
          {item && (
            <div>
              <label style={labelSt}>Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} style={inputSt}>
                <option value="agendada">Agendada</option>
                <option value="realizada">Realizada</option>
                <option value="cancelada">Cancelada</option>
                <option value="reagendada">Reagendada</option>
              </select>
            </div>
          )}
          <div>
            <label style={labelSt}>Notas / Observações</label>
            <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} placeholder="Anotações sobre a reunião..." rows={3} style={{ ...inputSt, resize: "vertical" }} />
          </div>
        </div>
        <div style={{ padding: "16px 28px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", color: "#666", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving || !form.empresa || !form.data_reuniao} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: !form.empresa || !form.data_reuniao ? "#ccc" : "#1aae9e", color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Salvando..." : item ? "Salvar Alterações" : "Agendar Reunião"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MONITORAMENTO — Dashboard de Atividade
   ══════════════════════════════════════════════ */

function MonitoramentoPage({ addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroTenant, setFiltroTenant] = useState("");
  const [tenants, setTenants] = useState([]);
  const [busca, setBusca] = useState("");
  const [ordenar, setOrdenar] = useState("ultimo_acesso");
  const [detalheUser, setDetalheUser] = useState(null);
  const [detalheData, setDetalheData] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroTenant ? `?tenantId=${filtroTenant}` : "";
      const [d, t] = await Promise.all([
        apiFetch("/api/admin/monitoramento" + params),
        apiFetch("/api/admin/tenants"),
      ]);
      setData(d);
      setTenants(t);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [filtroTenant, addToast]);

  useEffect(() => { load(); }, [load]);

  const loadDetalhe = async (uid) => {
    setDetalheLoading(true);
    try {
      const d = await apiFetch(`/api/admin/monitoramento/usuario/${uid}`);
      setDetalheData(d);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setDetalheLoading(false);
  };

  const fmtDuracao = (s) => {
    if (!s && s !== 0) return "—";
    if (s <= 0) return "—";
    if (s < 60) return s + "s";
    if (s < 3600) return Math.floor(s / 60) + "min " + (s % 60) + "s";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h + "h " + m + "min";
  };

  const tempoAtras = (d) => {
    if (!d) return "Nunca";
    const ms = Date.now() - new Date(d).getTime();
    if (ms < 600000) return "Agora";
    if (ms < 3600000) return Math.floor(ms / 60000) + " min atrás";
    if (ms < 86400000) return Math.floor(ms / 3600000) + "h atrás";
    if (ms < 2592000000) return Math.floor(ms / 86400000) + "d atrás";
    return fmtDate(d);
  };

  const paginaLabel = (p) => {
    const map = {
      dashboard: "Dashboard", locacoes: "Locações", mapa: "Mapa", clientes: "Clientes",
      itinerarios: "Itinerários", motoristas: "Motoristas", cacambas: "Caçambas",
      frota: "Frota", manutencao: "Manutenção", combustivel: "Combustível",
      relatorios: "Relatórios", configuracoes: "Configurações", orcamentos: "Orçamentos",
      tipos_cacamba: "Tipos Caçamba", importacao: "Importação",
    };
    return map[p] || p || "—";
  };

  if (loading && !data) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 40, height: 40, border: "4px solid #e5e7eb", borderTop: "4px solid #1aae9e", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
        <div style={{ color: "#888", fontSize: 13 }}>Carregando dados de monitoramento...</div>
      </div>
    </div>
  );

  if (!data) return null;

  const { resumo, usuarios, paginasPopulares, loginsPorDia, horariosPico } = data;

  // Filtrar e ordenar usuários
  let filtered = usuarios;
  if (busca) {
    const b = busca.toLowerCase();
    filtered = filtered.filter(u => u.nome?.toLowerCase().includes(b) || u.login?.toLowerCase().includes(b) || u.tenant_nome?.toLowerCase().includes(b) || u.email?.toLowerCase().includes(b));
  }
  filtered = [...filtered].sort((a, b) => {
    if (ordenar === "ultimo_acesso") return (new Date(b.ultimo_acesso || 0)) - (new Date(a.ultimo_acesso || 0));
    if (ordenar === "logins") return (b.totalLogins30d || 0) - (a.totalLogins30d || 0);
    if (ordenar === "tempo") return (b.tempoTotal || 0) - (a.tempoTotal || 0);
    if (ordenar === "dias") return (b.diasAtivosMes || 0) - (a.diasAtivosMes || 0);
    if (ordenar === "nome") return (a.nome || "").localeCompare(b.nome || "");
    return 0;
  });

  const card = { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "20px 24px" };
  const statCard = (icon, label, value, color, sub) => (
    <div style={{ ...card, textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -8, right: -8, fontSize: 48, opacity: 0.06 }}>{icon}</div>
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || "#222" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{sub}</div>}
    </div>
  );

  // Gráfico de barras simples para logins por dia
  const maxLogins = Math.max(...(loginsPorDia || []).map(d => d.total), 1);

  // Gráfico de horários pico
  const maxHora = Math.max(...(horariosPico || []).map(h => h.total), 1);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>📊 Monitoramento de Atividade</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Acompanhe o uso do sistema em tempo real</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={filtroTenant} onChange={e => setFiltroTenant(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 13, fontFamily: "inherit", color: "#333", minWidth: 180 }}>
            <option value="">Todas as empresas</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          <button onClick={load} style={{ padding: "8px 16px", background: "#1aae9e", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>🔄 Atualizar</button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {statCard("👥", "Total Usuários", resumo.totalUsuarios, "#1e2d4f")}
        {statCard("🟢", "Ativos 24h", resumo.ativos24h, "#2ecc71", `${Math.round(resumo.ativos24h / Math.max(resumo.totalUsuarios, 1) * 100)}% do total`)}
        {statCard("📅", "Ativos 7 dias", resumo.ativos7d, "#3498db", `${Math.round(resumo.ativos7d / Math.max(resumo.totalUsuarios, 1) * 100)}% do total`)}
        {statCard("💤", "Inativos +30d", resumo.inativos30d, resumo.inativos30d > 0 ? "#e74c3c" : "#888", resumo.inativos30d > 0 ? "Atenção!" : "Tudo ok")}
      </div>

      {/* Gráficos: Logins por dia + Horários pico */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Logins por dia */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>📈 Logins por Dia <span style={{ fontSize: 11, color: "#aaa", fontWeight: 400 }}>(últimos 30 dias)</span></div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 120 }}>
            {(loginsPorDia || []).map((d, i) => {
              const h = Math.max((d.total / maxLogins) * 100, 4);
              const dia = new Date(d.dia).getDate();
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <div style={{ fontSize: 9, color: "#aaa" }}>{d.total}</div>
                  <div style={{ width: "100%", height: h, background: "linear-gradient(180deg, #1aae9e, #158f82)", borderRadius: "4px 4px 0 0", minWidth: 4, transition: "height .3s" }} title={`${d.dia}: ${d.total} logins`} />
                  {(i === 0 || i === (loginsPorDia || []).length - 1 || dia === 1 || dia === 15) && (
                    <div style={{ fontSize: 8, color: "#bbb" }}>{dia}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Horários pico */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>🕐 Horários de Acesso <span style={{ fontSize: 11, color: "#aaa", fontWeight: 400 }}>(últimos 30 dias)</span></div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 120 }}>
            {Array.from({ length: 24 }, (_, hora) => {
              const entry = (horariosPico || []).find(h => h.hora === hora);
              const total = entry?.total || 0;
              const h = Math.max((total / maxHora) * 100, 2);
              const isPico = total >= maxHora * 0.7;
              return (
                <div key={hora} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  {total > 0 && <div style={{ fontSize: 8, color: "#aaa" }}>{total}</div>}
                  <div style={{ width: "100%", height: h, background: isPico ? "linear-gradient(180deg, #e67e22, #d35400)" : "linear-gradient(180deg, #3498db, #2980b9)", borderRadius: "3px 3px 0 0", minWidth: 3, transition: "height .3s" }} title={`${hora}h: ${total} logins`} />
                  {(hora % 3 === 0) && <div style={{ fontSize: 8, color: "#bbb" }}>{hora}h</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Páginas populares */}
      {paginasPopulares?.length > 0 && (
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>📱 Páginas Mais Visitadas</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {paginasPopulares.map((p, i) => {
              const maxP = paginasPopulares[0]?.total || 1;
              const pct = (p.total / maxP) * 100;
              return (
                <div key={i} style={{ background: `linear-gradient(90deg, rgba(26,174,158,${0.08 + pct * 0.002}) ${pct}%, transparent ${pct}%)`, border: "1px solid rgba(26,174,158,.2)", borderRadius: 8, padding: "8px 14px", display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1e2d4f" }}>{paginaLabel(p.pagina)}</span>
                  <span style={{ fontSize: 11, color: "#1aae9e", fontWeight: 700 }}>{p.total}x</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabela de Usuários */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f" }}>👥 Usuários ({filtered.length})</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar usuário..." style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 12, width: 200, fontFamily: "inherit" }} />
            <select value={ordenar} onChange={e => setOrdenar(e.target.value)} style={{ padding: "7px 10px", borderRadius: 8, border: "1px solid #dde1e6", fontSize: 12, fontFamily: "inherit" }}>
              <option value="ultimo_acesso">Último acesso</option>
              <option value="logins">Mais logins</option>
              <option value="tempo">Mais tempo online</option>
              <option value="dias">Dias ativos</option>
              <option value="nome">Nome A-Z</option>
            </select>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Usuário</th>
                <th style={{ textAlign: "left", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Empresa</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Status</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Último Acesso</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Logins (30d)</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Dias Ativos</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Tempo Médio</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Tempo Total</th>
                <th style={{ textAlign: "center", padding: "10px 12px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderBottom: "1px solid #f0f2f5", transition: "background .15s" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafe"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td className="mob-title" style={{ padding: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: u.online ? "#2ecc71" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: u.online ? "#fff" : "#888", position: "relative", flexShrink: 0 }}>
                        {(u.nome || "U")[0].toUpperCase()}
                        {u.online && <div style={{ position: "absolute", bottom: -1, right: -1, width: 10, height: 10, borderRadius: "50%", background: "#2ecc71", border: "2px solid #fff" }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: "#1e2d4f", fontSize: 13 }}>{u.nome}</div>
                        <div style={{ fontSize: 11, color: "#aaa" }}>{u.login} · {u.role}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Empresa" style={{ padding: "12px" }}>
                    <div style={{ fontSize: 12, color: "#555" }}>{u.tenant_nome}</div>
                  </td>
                  <td data-label="Status" style={{ padding: "12px", textAlign: "center" }}>
                    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: u.online ? "rgba(46,204,113,.15)" : u.ultimo_acesso && (Date.now() - new Date(u.ultimo_acesso).getTime() < 86400000) ? "rgba(52,152,219,.15)" : "rgba(0,0,0,.06)", color: u.online ? "#27ae60" : u.ultimo_acesso && (Date.now() - new Date(u.ultimo_acesso).getTime() < 86400000) ? "#2980b9" : "#888" }}>
                      {u.online ? "🟢 Online" : u.ultimo_acesso && (Date.now() - new Date(u.ultimo_acesso).getTime() < 86400000) ? "Ativo hoje" : u.ultimo_acesso && (Date.now() - new Date(u.ultimo_acesso).getTime() < 604800000) ? "Ativo esta semana" : "Inativo"}
                    </span>
                  </td>
                  <td data-label="Último Acesso" style={{ padding: "12px", textAlign: "center", fontSize: 12, color: "#555" }}>{tempoAtras(u.ultimo_acesso)}</td>
                  <td data-label="Logins (30d)" style={{ padding: "12px", textAlign: "center" }}>
                    <span style={{ fontWeight: 700, color: u.totalLogins30d > 20 ? "#27ae60" : u.totalLogins30d > 5 ? "#2980b9" : "#888" }}>{u.totalLogins30d}</span>
                  </td>
                  <td data-label="Dias Ativos" style={{ padding: "12px", textAlign: "center" }}>
                    <span style={{ fontWeight: 700, color: u.diasAtivosMes > 15 ? "#27ae60" : u.diasAtivosMes > 5 ? "#2980b9" : "#888" }}>{u.diasAtivosMes}/{new Date().getDate()}</span>
                  </td>
                  <td data-label="Tempo Médio" style={{ padding: "12px", textAlign: "center", fontSize: 12, color: "#555" }}>{fmtDuracao(u.duracaoMedia)}</td>
                  <td data-label="Tempo Total" style={{ padding: "12px", textAlign: "center", fontSize: 12, color: "#555" }}>{fmtDuracao(u.tempoTotal)}</td>
                  <td className="mob-actions" style={{ padding: "12px", textAlign: "center" }}>
                    <button onClick={() => { setDetalheUser(u); loadDetalhe(u.id); }} style={{ padding: "5px 12px", background: "#f0f2f5", border: "1px solid #dde1e6", borderRadius: 6, fontSize: 11, cursor: "pointer", color: "#555", fontFamily: "inherit", fontWeight: 600 }}>Detalhes</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🔍</div>
            <div style={{ fontSize: 14 }}>Nenhum usuário encontrado</div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Usuário */}
      {detalheUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }} onClick={() => { setDetalheUser(null); setDetalheData(null); }}>
          <div style={{ background: "#fff", borderRadius: 16, width: 680, maxHeight: "85vh", overflow: "auto", boxShadow: "0 24px 64px rgba(0,0,0,.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "24px 28px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e2d4f" }}>{detalheUser.nome}</h3>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#888" }}>{detalheUser.login} · {detalheUser.tenant_nome} · {detalheUser.role}</p>
              </div>
              <button onClick={() => { setDetalheUser(null); setDetalheData(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa" }}>✕</button>
            </div>

            <div style={{ padding: "24px 28px" }}>
              {detalheLoading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ width: 30, height: 30, border: "3px solid #e5e7eb", borderTop: "3px solid #1aae9e", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
                  <div style={{ color: "#888", fontSize: 12 }}>Carregando...</div>
                </div>
              ) : detalheData ? (
                <>
                  {/* Info cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
                    <div style={{ background: "#f0faf8", borderRadius: 10, padding: 14, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>ÚLTIMO ACESSO</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginTop: 4 }}>{tempoAtras(detalheUser.ultimo_acesso)}</div>
                    </div>
                    <div style={{ background: "#f0f5ff", borderRadius: 10, padding: 14, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>LOGINS (30D)</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginTop: 4 }}>{detalheUser.totalLogins30d}</div>
                    </div>
                    <div style={{ background: "#fef9f0", borderRadius: 10, padding: 14, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>TEMPO TOTAL</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginTop: 4 }}>{fmtDuracao(detalheUser.tempoTotal)}</div>
                    </div>
                  </div>

                  {/* Dispositivos */}
                  {detalheData?.deviceResumo && Object.keys(detalheData.deviceResumo).length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700, color: "#888", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Dispositivos Usados</h4>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {Object.entries(detalheData.deviceResumo).map(([device, count]) => {
                          const deviceIcons = { Desktop: "🖥️", Mobile: "📱", Tablet: "📱", Desconhecido: "❓" };
                          const deviceColors = { Desktop: "#3498db", Mobile: "#2ecc71", Tablet: "#9b59b6", Desconhecido: "#888" };
                          return (
                            <div key={device} style={{ background: `${deviceColors[device] || "#888"}12`, border: `1px solid ${deviceColors[device] || "#888"}33`, borderRadius: 10, padding: "12px 18px", textAlign: "center", minWidth: 100 }}>
                              <div style={{ fontSize: 24 }}>{deviceIcons[device] || "❓"}</div>
                              <div style={{ fontSize: 18, fontWeight: 800, color: deviceColors[device] || "#888", marginTop: 4 }}>{count}</div>
                              <div style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{device}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Sessões recentes */}
                  {detalheData.sessoes?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 10 }}>🕐 Sessões Recentes</div>
                      <div style={{ maxHeight: 200, overflow: "auto" }}>
                        {detalheData.sessoes.map((s, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderBottom: "1px solid #f0f2f5", fontSize: 12 }}>
                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.duracao ? "#2ecc71" : "#e5e7eb", flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <span style={{ color: "#555" }}>{fmtDate(s.inicio)}</span>
                              <span style={{ color: "#aaa", margin: "0 6px" }}>·</span>
                              <span style={{ color: "#1aae9e", fontWeight: 600 }}>{fmtDuracao(s.duracao)}</span>
                            </div>
                            {s.paginas_visitadas && (
                              <div style={{ fontSize: 11, color: "#aaa", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {s.paginas_visitadas.split(", ").map(p => paginaLabel(p)).join(" → ")}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Últimas atividades */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 10 }}>📋 Últimas Atividades</div>
                    <div style={{ maxHeight: 250, overflow: "auto" }}>
                      {(detalheData.logs || []).map((l, i) => {
                        const acaoIcon = l.acao === "login" ? "🔑" : l.acao === "logout" ? "🚪" : "📱";
                        const acaoColor = l.acao === "login" ? "#27ae60" : l.acao === "logout" ? "#e74c3c" : "#3498db";
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 12 }}>
                            <span>{acaoIcon}</span>
                            <span style={{ color: acaoColor, fontWeight: 600, minWidth: 70 }}>{l.acao === "page_view" ? paginaLabel(l.pagina) : l.acao}</span>
                            <span style={{ color: "#aaa", flex: 1, fontSize: 11 }}>{l.ip}</span>
                            {l.device && <span style={{ padding: "2px 6px", background: l.device === "Mobile" ? "rgba(46,204,113,.12)" : "rgba(52,152,219,.12)", borderRadius: 4, fontSize: 10, fontWeight: 600, color: l.device === "Mobile" ? "#2ecc71" : "#3498db", marginLeft: 6 }}>{l.device === "Desktop" ? "🖥️" : "📱"} {l.browser || ""}</span>}
                            {l.duracao_sessao && <span style={{ color: "#1aae9e", fontSize: 11 }}>⏱ {fmtDuracao(l.duracao_sessao)}</span>}
                            <span style={{ color: "#bbb", fontSize: 11 }}>{fmtDate(l.created_at)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MÓDULOS ADICIONAIS PAGE
   ══════════════════════════════════════════════ */

/* ══════════════════════════════════════════════
   PROPOSTAS PAGE
   ══════════════════════════════════════════════ */

function PropostasPage({ addToast }) {
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'nova' | proposta_obj
  const [filtro, setFiltro] = useState("todos");
  const [busca, setBusca] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/api/admin/propostas");
      setPropostas(data || []);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const filtradas = useMemo(() => {
    let list = propostas;
    if (filtro !== "todos") list = list.filter(p => p.status === filtro);
    if (busca.trim()) {
      const q = busca.toLowerCase();
      list = list.filter(p =>
        (p.cliente_nome || "").toLowerCase().includes(q) ||
        (p.cliente_empresa || "").toLowerCase().includes(q) ||
        (p.codigo || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [propostas, filtro, busca]);

  const stats = useMemo(() => ({
    total: propostas.length,
    rascunho: propostas.filter(p => p.status === "rascunho").length,
    enviada: propostas.filter(p => p.status === "enviada").length,
    aceita: propostas.filter(p => p.status === "aceita").length,
    recusada: propostas.filter(p => p.status === "recusada").length,
    valorTotal: propostas.filter(p => p.status === "aceita").reduce((s, p) => s + parseFloat(p.valor_final || 0), 0),
  }), [propostas]);

  const handleExcluir = async (id) => {
    if (!window.confirm("Excluir esta proposta?")) return;
    try {
      await apiFetch(`/api/admin/propostas/${id}`, { method: "DELETE" });
      addToast("Excluída", "Proposta removida", "🗑️", "#e74c3c");
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
  };

  const handleDuplicar = async (id) => {
    try {
      await apiFetch(`/api/admin/propostas/${id}/duplicar`, { method: "POST" });
      addToast("Duplicada", "Proposta duplicada com sucesso", "📋", "#3498db");
      load();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
  };

  const fmt = v => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const fmtDate = d => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  const stColors = { rascunho: "#888", enviada: "#3498db", aceita: "#2ecc71", recusada: "#e74c3c", expirada: "#f39c12" };
  const stLabels = { rascunho: "Rascunho", enviada: "Enviada", aceita: "Aceita", recusada: "Recusada", expirada: "Expirada" };
  const thS = { textAlign: "left", padding: "12px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #e5e7eb" };

  const APP_URL = "https://app.controlacacambas.com.br";

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>Propostas Comerciais</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Crie e envie propostas personalizadas para seus clientes</p>
        </div>
        <button onClick={() => setModal("nova")} style={{ background: "linear-gradient(135deg, #1aae9e, #0d8a7e)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
          + Nova Proposta
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total", value: stats.total, color: "#1e2d4f" },
          { label: "Rascunhos", value: stats.rascunho, color: "#888" },
          { label: "Enviadas", value: stats.enviada, color: "#3498db" },
          { label: "Aceitas", value: stats.aceita, color: "#2ecc71" },
          { label: "Valor Aceitas", value: fmt(stats.valorTotal), color: "#1aae9e" },
        ].map((s, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, empresa ou código..." style={{ flex: 1, minWidth: 200, padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
        {["todos", "rascunho", "enviada", "aceita", "recusada"].map(f => (
          <button key={f} onClick={() => setFiltro(f)} style={{ padding: "6px 14px", borderRadius: 8, border: filtro === f ? "2px solid #1aae9e" : "1px solid #e5e7eb", background: filtro === f ? "#1aae9e15" : "#fff", color: filtro === f ? "#1aae9e" : "#888", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            {f === "todos" ? "Todos" : (stLabels[f] || f)}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>Carregando...</div> : filtradas.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 14 }}>Nenhuma proposta encontrada</div>
          </div>
        ) : (
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>
              <th style={thS}>Código</th>
              <th style={thS}>Cliente</th>
              <th style={thS}>Plano</th>
              <th style={thS}>Valor</th>
              <th style={thS}>Status</th>
              <th style={thS}>Criada</th>
              <th style={thS}>Views</th>
              <th style={{ ...thS, textAlign: "right" }}>Ações</th>
            </tr></thead>
            <tbody>
              {filtradas.map(p => (
                <tr key={p.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
                  <td className="mob-title" style={{ padding: "12px 14px", fontWeight: 700, color: "#1aae9e", fontSize: 12 }}>{p.codigo}</td>
                  <td data-label="Cliente" style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 600, color: "#222" }}>{p.cliente_nome || "—"}</div>
                    {p.cliente_empresa && <div style={{ fontSize: 11, color: "#888" }}>{p.cliente_empresa}</div>}
                  </td>
                  <td data-label="Plano" style={{ padding: "12px 14px", fontSize: 12, color: "#555" }}>{p.plano || "—"}</td>
                  <td data-label="Valor" style={{ padding: "12px 14px" }}>
                    {p.desconto_percentual > 0 && <div style={{ fontSize: 10, color: "#888", textDecoration: "line-through" }}>{fmt(p.valor_original)}</div>}
                    <div style={{ fontWeight: 700, color: "#1e2d4f" }}>{fmt(p.valor_final)}</div>
                    {p.desconto_percentual > 0 && <div style={{ fontSize: 10, color: "#2ecc71", fontWeight: 600 }}>-{p.desconto_percentual}%</div>}
                  </td>
                  <td data-label="Status" style={{ padding: "12px 14px" }}>
                    <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: stColors[p.status] || "#888", background: (stColors[p.status] || "#888") + "18" }}>
                      {stLabels[p.status] || p.status}
                    </span>
                  </td>
                  <td data-label="Criada" style={{ padding: "12px 14px", fontSize: 12, color: "#888" }}>{fmtDate(p.created_at)}</td>
                  <td data-label="Views" style={{ padding: "12px 14px", fontSize: 12, color: "#888", textAlign: "center" }}>{p.visualizacoes || 0}</td>
                  <td className="mob-actions" style={{ padding: "12px 14px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={() => setModal(p)} title="Editar" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#3498db22", color: "#3498db", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>✏️</button>
                      {p.token_publico && <button onClick={() => { navigator.clipboard.writeText(`https://contrato.controlacacambas.com.br/${p.token_publico}`); addToast("Copiado", "Link copiado!", "🔗", "#1aae9e"); }} title="Copiar link" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#1aae9e22", color: "#1aae9e", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>🔗</button>}
                      {p.status === "aceita" && p.token_publico && <button onClick={() => window.open(`https://contrato.controlacacambas.com.br/${p.token_publico}`, "_blank")} title="Ver contrato assinado" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#2ecc7122", color: "#2ecc71", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>📄</button>}
                      <button onClick={() => handleDuplicar(p.id)} title="Duplicar" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#f39c1222", color: "#f39c12", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>📋</button>
                      <button onClick={() => handleExcluir(p.id)} title="Excluir" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#e74c3c22", color: "#e74c3c", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Nova/Editar Proposta */}
      {modal && <PropostaModal
        proposta={modal === "nova" ? null : modal}
        onClose={() => setModal(null)}
        onSaved={() => { setModal(null); load(); }}
        addToast={addToast}
      />}
    </div>
  );
}

/* ── Modal de Proposta (Nova / Editar) ───────────────── */
function PropostaModal({ proposta, onClose, onSaved, addToast }) {
  const isEdit = !!proposta;
  const [saving, setSaving] = useState(false);
  const [fullData, setFullData] = useState(proposta); // dados completos com assinatura
  const [tenantsList, setTenantsList] = useState([]);
  const [form, setForm] = useState({
    tenant_id: proposta?.tenant_id || null,
    cliente_nome: proposta?.cliente_nome || "",
    cliente_email: proposta?.cliente_email || "",
    cliente_telefone: proposta?.cliente_telefone || "",
    cliente_empresa: proposta?.cliente_empresa || "",
    cliente_cpf_cnpj: proposta?.cliente_cpf_cnpj || "",
    cliente_cidade: proposta?.cliente_cidade || "",
    cliente_uf: proposta?.cliente_uf || "",
    plano: proposta?.plano || "Controla",
    ciclo: proposta?.ciclo || "mensal",
    valor_original: proposta?.valor_original || 200,
    desconto_percentual: proposta?.desconto_percentual || 0,
    itens: (() => { try { return typeof proposta?.itens === "string" ? JSON.parse(proposta.itens) : (proposta?.itens || []); } catch { return []; } })(),
    observacoes: proposta?.observacoes || "",
    validade_dias: proposta?.validade_dias || 15,
    condicoes: proposta?.condicoes || "Pagamento via boleto ou PIX. Suporte incluso por email e WhatsApp. Treinamento inicial gratuito.",
    status: proposta?.status || "rascunho",
  });

  // Buscar dados completos (com assinatura_img) quando abrir proposta existente
  useEffect(() => {
    if (isEdit && proposta?.id) {
      apiFetch(`/api/admin/propostas/${proposta.id}`).then(data => { if (data && data.id) setFullData(data); }).catch(() => {});
    }
  }, [isEdit, proposta?.id]);

  // Lista de empresas para vínculo (opcional)
  useEffect(() => {
    apiFetch("/api/admin/tenants-full").then(d => setTenantsList(d || [])).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Vincular empresa: seta tenant_id e pré-preenche dados do cliente
  const handleVincularTenant = (v) => {
    if (!v) return upd("tenant_id", null);
    const t = tenantsList.find(x => String(x.id) === String(v));
    if (!t) return upd("tenant_id", Number(v));
    setForm(f => ({
      ...f,
      tenant_id: Number(v),
      cliente_nome: t.nome || f.cliente_nome,
      cliente_email: t.email || f.cliente_email,
      cliente_empresa: t.nome || f.cliente_empresa,
      cliente_telefone: t.tel || f.cliente_telefone,
      cliente_cpf_cnpj: t.cpf_cnpj_cobranca || f.cliente_cpf_cnpj,
      cliente_cidade: t.cidade_cobranca || f.cliente_cidade,
      cliente_uf: t.uf_cobranca || f.cliente_uf,
    }));
  };

  // Cálculos
  const descontoValor = (parseFloat(form.valor_original) * parseFloat(form.desconto_percentual || 0)) / 100;
  const valorFinal = parseFloat(form.valor_original) - descontoValor;

  // Itens
  const addItem = () => upd("itens", [...form.itens, { nome: "", incluso: true }]);
  const removeItem = (i) => upd("itens", form.itens.filter((_, idx) => idx !== i));
  const updItem = (i, k, v) => {
    const arr = [...form.itens];
    arr[i] = { ...arr[i], [k]: v };
    upd("itens", arr);
  };

  const handleSave = async (status) => {
    if (!form.cliente_nome.trim()) return addToast("Erro", "Nome do cliente é obrigatório", "❌", "#e74c3c");
    setSaving(true);
    try {
      const body = {
        ...form,
        desconto_valor: descontoValor,
        valor_final: valorFinal,
        status: status || form.status,
      };
      if (isEdit) {
        await apiFetch(`/api/admin/propostas/${proposta.id}`, { method: "PUT", body });
      } else {
        await apiFetch("/api/admin/propostas", { method: "POST", body });
      }
      addToast("Salvo", status === "enviada" ? "Proposta salva e pronta para envio!" : "Proposta salva!", "✅", "#2ecc71");
      onSaved();
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setSaving(false);
  };

  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: "#888", marginBottom: 4, textTransform: "uppercase" };
  const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
  const fmt = v => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const cicloLabel = { mensal: "/mês", semestral: "/semestre", anual: "/ano" };
  const APP_URL = "https://app.controlacacambas.com.br";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: 720, maxHeight: "92vh", overflow: "auto", animation: "slideIn .3s ease" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 10, borderRadius: "16px 16px 0 0" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e2d4f" }}>{isEdit ? "Editar Proposta" : "Nova Proposta"}</h2>
            {isEdit && <span style={{ fontSize: 12, color: "#1aae9e", fontWeight: 700 }}>{proposta.codigo}</span>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

          {/* CONTRATO ASSINADO — aparece no topo quando aceita */}
          {isEdit && proposta.status === "aceita" && (
            <div style={{ background: "#eaf6f0", borderRadius: 12, padding: 20, border: "2px solid #2ecc71" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#2ecc71", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✓</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#1a8a5c" }}>Contrato Assinado</div>
                  {(fullData?.aceita_em || proposta.aceita_em) && <div style={{ fontSize: 12, color: "#555" }}>Aceito em {new Date(fullData?.aceita_em || proposta.aceita_em).toLocaleString("pt-BR")}</div>}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, marginBottom: 14 }}>
                {(fullData?.assinatura_nome || proposta.assinatura_nome) && <div><span style={{ color: "#888" }}>Assinante:</span> <strong style={{ color: "#222" }}>{fullData?.assinatura_nome || proposta.assinatura_nome}</strong></div>}
                {(fullData?.assinatura_cargo || proposta.assinatura_cargo) && <div><span style={{ color: "#888" }}>Cargo:</span> <strong style={{ color: "#222" }}>{fullData?.assinatura_cargo || proposta.assinatura_cargo}</strong></div>}
                {(fullData?.assinatura_cpf || proposta.assinatura_cpf) && <div><span style={{ color: "#888" }}>CPF:</span> <strong style={{ color: "#222" }}>{fullData?.assinatura_cpf || proposta.assinatura_cpf}</strong></div>}
                {(fullData?.assinatura_ip || proposta.assinatura_ip) && <div><span style={{ color: "#888" }}>IP de origem:</span> <strong style={{ color: "#222" }}>{fullData?.assinatura_ip || proposta.assinatura_ip}</strong></div>}
              </div>
              {fullData?.assinatura_img && (
                <div style={{ textAlign: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#888", marginBottom: 6, letterSpacing: 1, textTransform: "uppercase" }}>Assinatura Digital</div>
                  <img src={fullData.assinatura_img} alt="Assinatura" style={{ maxWidth: 260, border: "1px solid #c3e6d5", borderRadius: 6, background: "#fff", padding: 8 }} />
                </div>
              )}
              {!fullData?.assinatura_img && (fullData?.tem_assinatura || proposta.tem_assinatura) && (
                <div style={{ textAlign: "center", marginBottom: 14, fontSize: 12, color: "#888" }}>Carregando assinatura...</div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={() => window.open(`https://contrato.controlacacambas.com.br/${proposta.token_publico}`, "_blank")} style={{ background: "#1a8a5c", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>📄 Abrir Contrato Completo</button>
                <button onClick={() => { const w = window.open(`https://contrato.controlacacambas.com.br/${proposta.token_publico}`, "_blank"); setTimeout(() => { w && w.print(); }, 1500); }} style={{ background: "#555", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>🖨 Baixar PDF</button>
              </div>
            </div>
          )}

          {/* Vincular empresa (opcional) */}
          <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 12 }}>🏢 Vincular empresa (opcional)</div>
            <select style={inputStyle} value={form.tenant_id || ""} onChange={e => handleVincularTenant(e.target.value)}>
              <option value="">— Sem vínculo —</option>
              {tenantsList.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
            <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>Ao selecionar, os dados do cliente são pré-preenchidos com os dados da empresa.</div>
          </div>

          {/* Cliente */}
          <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 12 }}>👤 Dados do Cliente</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={labelStyle}>Nome *</label><input style={inputStyle} value={form.cliente_nome} onChange={e => upd("cliente_nome", e.target.value)} /></div>
              <div><label style={labelStyle}>Empresa</label><input style={inputStyle} value={form.cliente_empresa} onChange={e => upd("cliente_empresa", e.target.value)} /></div>
              <div><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={form.cliente_email} onChange={e => upd("cliente_email", e.target.value)} /></div>
              <div><label style={labelStyle}>Telefone</label><input style={inputStyle} value={form.cliente_telefone} onChange={e => upd("cliente_telefone", e.target.value)} /></div>
              <div><label style={labelStyle}>CPF/CNPJ</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={form.cliente_cpf_cnpj} onChange={e => upd("cliente_cpf_cnpj", e.target.value)} placeholder="00.000.000/0000-00" />
                  <button type="button" onClick={async () => { try { const d = await buscarCNPJ(form.cliente_cpf_cnpj); upd("cliente_nome", d.razao_social || d.nome_fantasia); upd("cliente_empresa", d.nome_fantasia || d.razao_social); upd("cliente_cidade", d.cidade); upd("cliente_uf", d.uf); if (d.telefone) upd("cliente_telefone", d.telefone); if (d.email) upd("cliente_email", d.email); addToast("CNPJ", d.razao_social, "✅", "#2ecc71"); } catch(e) { addToast("Erro", e.message, "❌", "#e74c3c"); } }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, padding: "0 12px", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>🔍</button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Cidade</label><input style={inputStyle} value={form.cliente_cidade} onChange={e => upd("cliente_cidade", e.target.value)} /></div>
                <div style={{ width: 60 }}><label style={labelStyle}>UF</label><input style={inputStyle} maxLength={2} value={form.cliente_uf} onChange={e => upd("cliente_uf", e.target.value.toUpperCase())} /></div>
              </div>
            </div>
          </div>

          {/* Plano e Valores */}
          <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 12 }}>💰 Plano e Valores</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Plano</label>
                <select style={inputStyle} value={form.plano} onChange={e => {
                  const v = e.target.value;
                  upd("plano", v);
                  if (v === "Básico") upd("valor_original", 120);
                  else if (v === "Controla") upd("valor_original", 200);
                  else if (v === "Controla Plus") upd("valor_original", 300);
                }}>
                  <option value="Básico">Básico — R$ 120</option>
                  <option value="Controla">Controla — R$ 200</option>
                  <option value="Controla Plus">Controla Plus — R$ 300</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Ciclo</label>
                <select style={inputStyle} value={form.ciclo} onChange={e => upd("ciclo", e.target.value)}>
                  <option value="mensal">Mensal</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
              <div><label style={labelStyle}>Valor Original</label><input style={inputStyle} type="number" step="0.01" value={form.valor_original} onChange={e => upd("valor_original", e.target.value)} /></div>
              <div><label style={labelStyle}>Desconto (%)</label><input style={inputStyle} type="number" step="1" min="0" max="100" value={form.desconto_percentual} onChange={e => upd("desconto_percentual", e.target.value)} /></div>
            </div>
            {/* Resumo */}
            <div style={{ marginTop: 14, background: "#fff", borderRadius: 10, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e7eb" }}>
              <div>
                <span style={{ fontSize: 12, color: "#888" }}>Valor original: </span><span style={{ fontWeight: 700, color: "#555" }}>{fmt(form.valor_original)}{cicloLabel[form.ciclo]}</span>
                {form.desconto_percentual > 0 && <span style={{ marginLeft: 10, fontSize: 12, color: "#2ecc71", fontWeight: 700 }}>(-{form.desconto_percentual}% = -{fmt(descontoValor)})</span>}
              </div>
              <div>
                <span style={{ fontSize: 12, color: "#888" }}>Valor final: </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: "#1aae9e" }}>{fmt(valorFinal)}</span>
                <span style={{ fontSize: 12, color: "#888" }}>{cicloLabel[form.ciclo]}</span>
              </div>
            </div>
          </div>

          {/* Módulos / Itens */}
          <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f" }}>🧩 Módulos e Itens</div>
              <button onClick={addItem} style={{ background: "#1aae9e22", color: "#1aae9e", border: "none", borderRadius: 8, padding: "5px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>+ Adicionar Item</button>
            </div>
            {form.itens.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#bbb", fontSize: 13 }}>Nenhum item adicionado. Clique em "+ Adicionar Item" para incluir módulos, funcionalidades ou benefícios.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {form.itens.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 8, padding: "8px 12px", border: "1px solid #e5e7eb" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input type="checkbox" checked={item.incluso !== false} onChange={e => updItem(i, "incluso", e.target.checked)} />
                      <span style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{item.incluso !== false ? "Incluso" : "Opcional"}</span>
                    </label>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder="Ex: Módulo de Caçambas, App Motorista, Relatórios..." value={item.nome || ""} onChange={e => updItem(i, "nome", e.target.value)} />
                    <button onClick={() => removeItem(i)} style={{ background: "none", border: "none", color: "#e74c3c", fontSize: 16, cursor: "pointer" }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Observações e Condições */}
          <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4f", marginBottom: 12 }}>📝 Observações e Condições</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Observações</label><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={form.observacoes} onChange={e => upd("observacoes", e.target.value)} placeholder="Observações internas ou para o cliente..." /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Condições</label><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={form.condicoes} onChange={e => upd("condicoes", e.target.value)} /></div>
              <div><label style={labelStyle}>Validade (dias)</label><input style={inputStyle} type="number" value={form.validade_dias} onChange={e => upd("validade_dias", e.target.value)} /></div>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => upd("status", e.target.value)}>
                  <option value="rascunho">Rascunho</option>
                  <option value="enviada">Enviada</option>
                  <option value="aceita">Aceita</option>
                  <option value="recusada">Recusada</option>
                </select>
              </div>
            </div>
          </div>

          {/* Link público */}
          {isEdit && proposta.token_publico && (
            <div style={{ background: "#f0faf8", borderRadius: 12, padding: 14, border: "1px solid #1aae9e33", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1aae9e", marginBottom: 2 }}>LINK DO CONTRATO</div>
                <div style={{ fontSize: 12, color: "#555", wordBreak: "break-all" }}>https://contrato.controlacacambas.com.br/{proposta.token_publico}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { navigator.clipboard.writeText(`https://contrato.controlacacambas.com.br/${proposta.token_publico}`); addToast("Copiado", "Link copiado!", "🔗", "#1aae9e"); }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>📋 Copiar</button>
                <button onClick={() => window.open(`https://contrato.controlacacambas.com.br/${proposta.token_publico}`, "_blank")} style={{ background: "#555", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>🔗 Abrir</button>
              </div>
            </div>
          )}

        </div>

        {/* Footer / Ações */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 10, position: "sticky", bottom: 0, background: "#fff", borderRadius: "0 0 16px 16px" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#f5f5f5", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Cancelar</button>
          <button onClick={() => handleSave("rascunho")} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#555", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Salvando..." : "💾 Salvar Rascunho"}
          </button>
          <button onClick={() => handleSave("enviada")} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #1aae9e, #0d8a7e)", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Salvando..." : "🚀 Salvar e Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DASHBOARD FINANCEIRO
   ══════════════════════════════════════════════════════════════ */
function FinanceiroPage({ addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch("/api/admin/financeiro");
      setData(d);
    } catch (err) { addToast("Erro", err.message, "❌", "#e74c3c"); }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const fmt = v => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const fmtP = v => parseFloat(v || 0).toFixed(1) + "%";
  const fmtDate = d => d ? new Date(d).toLocaleDateString("pt-BR") : "—";
  const mesLabel = m => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    return meses[parseInt(mo) - 1] + "/" + y.slice(2);
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "#aaa" }}>Carregando dados financeiros...</div>;
  if (!data) return <div style={{ padding: 60, textAlign: "center", color: "#aaa" }}>Erro ao carregar dados</div>;

  // Variação MRR
  const receitaVar = data.receitaMesAnterior > 0
    ? ((data.receitaMesAtual - data.receitaMesAnterior) / data.receitaMesAnterior * 100).toFixed(1)
    : 0;

  const cardS = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 };
  const labelS = { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 };
  const bigS = { fontSize: 28, fontWeight: 800, lineHeight: 1.2 };
  const subS = { fontSize: 12, color: "#888", marginTop: 4 };

  // Chart bars helper
  const maxVal = (arr, key) => Math.max(...arr.map(a => parseFloat(a[key] || 0)), 1);
  const Bar = ({ value, max, color, label, sub }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <div style={{ width: 70, fontSize: 11, color: "#888", textAlign: "right", flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: "#f5f5f5", borderRadius: 4, height: 24, position: "relative", overflow: "hidden" }}>
        <div style={{ width: Math.max((value / max) * 100, 2) + "%", height: "100%", background: color, borderRadius: 4, transition: "width .5s ease" }} />
        <span style={{ position: "absolute", right: 8, top: 3, fontSize: 11, fontWeight: 700, color: value > max * 0.5 ? "#fff" : "#555" }}>{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e2d4f" }}>Dashboard Financeiro</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>Visão geral da saúde financeira do negócio</p>
        </div>
        <button onClick={load} style={{ background: "#f5f5f5", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", color: "#555", fontWeight: 600 }}>↻ Atualizar</button>
      </div>

      {/* ── KPIs principais ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 24 }}>
        <div style={cardS}>
          <div style={labelS}>MRR Atual</div>
          <div style={{ ...bigS, color: "#1aae9e" }}>{fmt(data.mrr)}</div>
          <div style={subS}>Receita recorrente mensal</div>
        </div>
        <div style={cardS}>
          <div style={labelS}>Receita do Mês</div>
          <div style={{ ...bigS, color: "#2ecc71" }}>{fmt(data.receitaMesAtual)}</div>
          <div style={{ ...subS, color: parseFloat(receitaVar) >= 0 ? "#2ecc71" : "#e74c3c" }}>
            {parseFloat(receitaVar) >= 0 ? "▲" : "▼"} {Math.abs(receitaVar)}% vs mês anterior
          </div>
        </div>
        <div style={cardS}>
          <div style={labelS}>Inadimplência</div>
          <div style={{ ...bigS, color: data.totalInadimplente > 0 ? "#e74c3c" : "#2ecc71" }}>{fmt(data.totalInadimplente)}</div>
          <div style={subS}>{data.inadimplencia?.length || 0} boleto(s) vencido(s)</div>
        </div>
        <div style={cardS}>
          <div style={labelS}>Churn Rate</div>
          <div style={{ ...bigS, color: data.churnRate > 5 ? "#e74c3c" : data.churnRate > 2 ? "#f39c12" : "#2ecc71" }}>{fmtP(data.churnRate)}</div>
          <div style={subS}>{data.canceladosMes} cancelamento(s) no mês</div>
        </div>
        <div style={cardS}>
          <div style={labelS}>Ticket Médio</div>
          <div style={{ ...bigS, color: "#9b59b6" }}>{fmt(data.ticketMedio)}</div>
          <div style={subS}>{data.totalAtivos} ativo(s) · {data.totalTrial} trial(s)</div>
        </div>
      </div>

      {/* ── Linha 2: Gráficos ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>

        {/* Evolução da Receita */}
        <div style={{ ...cardS, padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>Evolução da Receita (12 meses)</div>
          {data.mrrHistorico?.length > 0 ? (() => {
            const mx = maxVal(data.mrrHistorico, "receita");
            return data.mrrHistorico.map((m, i) => (
              <Bar key={i} value={parseFloat(m.receita)} max={mx} color="#1aae9e" label={mesLabel(m.mes)} sub={fmt(m.receita)} />
            ));
          })() : <div style={{ color: "#ccc", fontSize: 13, textAlign: "center", padding: 30 }}>Sem dados</div>}
        </div>

        {/* Evolução de Assinantes + Churn */}
        <div style={{ ...cardS, padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>Novos Assinantes vs Churn (12 meses)</div>
          {data.evolucaoAssinantes?.length > 0 ? (() => {
            const churnMap = {};
            (data.churnMensal || []).forEach(c => { churnMap[c.mes] = c.cancelados; });
            const mxN = Math.max(...data.evolucaoAssinantes.map(a => a.novos), ...Object.values(churnMap), 1);
            return data.evolucaoAssinantes.map((m, i) => {
              const canc = churnMap[m.mes] || 0;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <div style={{ width: 55, fontSize: 11, color: "#888", textAlign: "right", flexShrink: 0 }}>{mesLabel(m.mes)}</div>
                  <div style={{ flex: 1, display: "flex", gap: 3 }}>
                    <div style={{ width: Math.max((m.novos / mxN) * 100, 2) + "%", height: 20, background: "#2ecc71", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 700, minWidth: 20 }}>+{m.novos}</div>
                    {canc > 0 && <div style={{ width: Math.max((canc / mxN) * 100, 2) + "%", height: 20, background: "#e74c3c", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 700, minWidth: 20 }}>-{canc}</div>}
                  </div>
                </div>
              );
            });
          })() : <div style={{ color: "#ccc", fontSize: 13, textAlign: "center", padding: 30 }}>Sem dados</div>}
          <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: "#888" }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#2ecc71", borderRadius: 2, marginRight: 4 }}></span>Novos</span>
            <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#e74c3c", borderRadius: 2, marginRight: 4 }}></span>Cancelamentos</span>
          </div>
        </div>
      </div>

      {/* ── Linha 3: Planos + Previsão ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>

        {/* Distribuição por Plano */}
        <div style={{ ...cardS, padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>Distribuição por Plano</div>
          {(data.porPlano || []).map((p, i) => {
            const cores = { basico: "#3498db", base: "#3498db", controla: "#1aae9e", operacao: "#1aae9e", controla_plus: "#9b59b6", escala: "#9b59b6" };
            const labels = { basico: "Básico", base: "Básico", controla: "Controla", operacao: "Controla", controla_plus: "Controla Plus", escala: "Controla Plus" };
            const cor = cores[p.plano] || "#888";
            const totalAll = (data.porPlano || []).reduce((s, x) => s + x.total, 0);
            const pct = totalAll > 0 ? (p.total / totalAll * 100).toFixed(0) : 0;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < data.porPlano.length - 1 ? "1px solid #f0f2f5" : "none" }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: cor, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{labels[p.plano] || p.plano}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{p.total} empresa(s) · {fmt(p.receita)}/mês</div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: cor }}>{pct}%</div>
              </div>
            );
          })}
          <div style={{ marginTop: 14, padding: 12, background: "#f8f9fa", borderRadius: 8, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span style={{ color: "#888" }}>Taxa de conversão (trial → ativo)</span>
            <span style={{ fontWeight: 700, color: data.taxaConversao > 50 ? "#2ecc71" : "#f39c12" }}>{fmtP(data.taxaConversao)}</span>
          </div>
        </div>

        {/* Previsão de Receita */}
        <div style={{ ...cardS, padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", marginBottom: 16 }}>Previsão de Receita</div>
          {(data.previsao || []).length > 0 ? (() => {
            const mx = maxVal(data.previsao, "previsto");
            return data.previsao.map((p, i) => (
              <Bar key={i} value={parseFloat(p.previsto)} max={mx} color="#3498db" label={mesLabel(p.mes)} sub={fmt(p.previsto)} />
            ));
          })() : <div style={{ color: "#ccc", fontSize: 13, textAlign: "center", padding: 30 }}>Nenhum boleto futuro pendente</div>}
          <div style={{ marginTop: 14, padding: 12, background: "#f8f9fa", borderRadius: 8, fontSize: 12, color: "#888", textAlign: "center" }}>
            Baseado em boletos pendentes e emitidos
          </div>
        </div>
      </div>

      {/* ── Tabela de Inadimplência ── */}
      {data.inadimplencia?.length > 0 && (
        <div style={{ ...cardS, padding: 0, marginBottom: 24, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#e74c3c" }}>⚠ Inadimplentes ({data.inadimplencia.length})</div>
            <div style={{ fontSize: 12, color: "#888" }}>Total: <strong style={{ color: "#e74c3c" }}>{fmt(data.totalInadimplente)}</strong></div>
          </div>
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Empresa</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Plano</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Ref.</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Venceu</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Valor</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" }}>Atraso</th>
              </tr>
            </thead>
            <tbody>
              {data.inadimplencia.map((item, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td className="mob-title" style={{ padding: "10px 14px" }}>
                    <div style={{ fontWeight: 600, color: "#222" }}>{item.nome}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>{item.email}</div>
                  </td>
                  <td data-label="Plano" style={{ padding: "10px 14px", fontSize: 12, color: "#555" }}>{planoLabels[item.plano] || item.plano}</td>
                  <td data-label="Ref." style={{ padding: "10px 14px", fontSize: 12, color: "#555" }}>{item.referencia}</td>
                  <td data-label="Venceu" style={{ padding: "10px 14px", fontSize: 12, color: "#555" }}>{fmtDate(item.vencimento)}</td>
                  <td data-label="Valor" style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#e74c3c" }}>{fmt(item.valor)}</td>
                  <td data-label="Atraso" style={{ padding: "10px 14px", textAlign: "right" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: item.dias_atraso > 30 ? "#e74c3c18" : item.dias_atraso > 15 ? "#f39c1218" : "#f5f5f5", color: item.dias_atraso > 30 ? "#e74c3c" : item.dias_atraso > 15 ? "#f39c12" : "#888" }}>
                      {item.dias_atraso}d
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ModulosPage({ addToast }) {
  const [modulos, setModulos] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        apiFetch("/api/modulos/admin/list"),
        apiFetch("/api/modulos/admin/tenants"),
      ]);
      setModulos(m || []);
      setTenants(t || []);
    } catch (err) {
      console.error("Erro carregar módulos:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = tenants.filter(t => {
    if (filtroStatus === "com_modulo" && (!t.modulos_ativos || t.modulos_ativos.length === 0)) return false;
    if (filtroStatus === "sem_modulo" && t.modulos_ativos && t.modulos_ativos.length > 0) return false;
    if (busca && !t.nome.toLowerCase().includes(busca.toLowerCase()) && !String(t.id).includes(busca)) return false;
    return true;
  });

  const handleVincular = async (tenantId, moduloSlug) => {
    setActionLoading(true);
    try {
      await apiFetch("/api/modulos/admin/vincular", { method: "POST", body: { tenant_id: tenantId, modulo_slug: moduloSlug } });
      addToast("Módulo vinculado!", "Módulo ativado para o tenant", "✅", "#22c55e");
      load();
    } catch (err) {
      addToast("Erro", err.message, "❌", "#ef4444");
    }
    setActionLoading(false);
    setConfirmModal(null);
  };

  const handleDesvincular = async (tenantId, moduloSlug) => {
    setActionLoading(true);
    try {
      await apiFetch("/api/modulos/admin/desvincular", { method: "POST", body: { tenant_id: tenantId, modulo_slug: moduloSlug } });
      addToast("Módulo desvinculado!", "Módulo desativado para o tenant", "🔴", "#f59e0b");
      load();
    } catch (err) {
      addToast("Erro", err.message, "❌", "#ef4444");
    }
    setActionLoading(false);
    setConfirmModal(null);
  };

  const cardStyle = { background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,.06)", marginBottom: 20 };

  return (
    <div style={{ animation: "fadeIn .3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1e2330", margin: 0 }}>🧩 Módulos Adicionais</h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>Gerencie módulos adicionais por tenant</p>
        </div>
        <button onClick={load} style={{ padding: "8px 16px", background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>🔄 Recarregar</button>
      </div>

      {/* Módulos Disponíveis */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: "#333" }}>Módulos Disponíveis</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {modulos.map(m => (
            <div key={m.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: m.ativo ? "#f0fdfa" : "#fef2f2", borderLeft: `4px solid ${m.ativo ? "#1aae9e" : "#ef4444"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>{m.icone || "📦"}</span>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{m.nome}</span>
              </div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>{m.descricao || ""}</div>
              <div style={{ fontSize: 11, color: m.ativo ? "#059669" : "#dc2626", fontWeight: 600 }}>{m.ativo ? "✅ Disponível" : "❌ Desativado"}</div>
              <div style={{ fontSize: 10, color: "#aaa", marginTop: 4 }}>Slug: {m.slug}</div>
            </div>
          ))}
          {modulos.length === 0 && !loading && <div style={{ color: "#aaa", fontSize: 13 }}>Nenhum módulo cadastrado</div>}
        </div>
      </div>

      {/* Filtros */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: "#333" }}>Tenants e Vínculos</h2>
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar tenant por nome ou ID..." style={{ flex: 1, minWidth: 200, padding: "10px 14px", border: "1px solid #ddd", borderRadius: 8, fontSize: 13 }} />
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ padding: "10px 14px", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, minWidth: 160 }}>
            <option value="todos">Todos os tenants</option>
            <option value="com_modulo">Com módulo ativo</option>
            <option value="sem_modulo">Sem módulo</option>
          </select>
        </div>

        {/* Tabela de Tenants */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>Carregando...</div>
        ) : (
          <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 600, color: "#555" }}>ID</th>
                <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 600, color: "#555" }}>Tenant</th>
                <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 600, color: "#555" }}>Status</th>
                <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 600, color: "#555" }}>Plano</th>
                <th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 600, color: "#555" }}>Módulos Ativos</th>
                <th style={{ textAlign: "center", padding: "10px 8px", fontWeight: 600, color: "#555" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#aaa" }}>Nenhum tenant encontrado</td></tr>
              )}
              {filtered.map(t => {
                const hasServicos = t.modulos_ativos && t.modulos_ativos.includes("servicos_liquidos");
                return (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td data-label="ID" style={{ padding: "12px 8px", fontWeight: 600, color: "#1aae9e" }}>#{t.id}</td>
                    <td className="mob-title" style={{ padding: "12px 8px" }}>
                      <div style={{ fontWeight: 600 }}>{t.nome}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{t.email || ""}</div>
                    </td>
                    <td data-label="Status" style={{ padding: "12px 8px" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: "#fff", background: statusColors[t.status] || "#888" }}>
                        {statusLabels[t.status] || t.status}
                      </span>
                    </td>
                    <td data-label="Plano" style={{ padding: "12px 8px", fontSize: 12 }}>{planoLabels[t.plano] || t.plano || "—"}</td>
                    <td data-label="Módulos Ativos" style={{ padding: "12px 8px" }}>
                      {t.modulos_nomes && t.modulos_nomes.length > 0 ? (
                        t.modulos_nomes.map((mn, i) => (
                          <span key={i} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: "#dbeafe", color: "#1e40af", marginRight: 4, marginBottom: 2 }}>
                            💧 {mn}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: "#aaa", fontSize: 11 }}>Nenhum</span>
                      )}
                    </td>
                    <td className="mob-actions" style={{ padding: "12px 8px", textAlign: "center" }}>
                      {modulos.map(mod => (
                        <div key={mod.id} style={{ marginBottom: 4 }}>
                          {t.modulos_ativos && t.modulos_ativos.includes(mod.slug) ? (
                            <button
                              onClick={() => setConfirmModal({ action: "desvincular", tenantId: t.id, tenantNome: t.nome, moduloSlug: mod.slug, moduloNome: mod.nome })}
                              disabled={actionLoading}
                              style={{ padding: "5px 12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                            >
                              Desativar {mod.icone}
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirmModal({ action: "vincular", tenantId: t.id, tenantNome: t.nome, moduloSlug: mod.slug, moduloNome: mod.nome })}
                              disabled={actionLoading}
                              style={{ padding: "5px 12px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                            >
                              Ativar {mod.icone}
                            </button>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Confirmação */}
      {confirmModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setConfirmModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 440, width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,.2)", animation: "slideIn .2s ease" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#1e2330" }}>
              {confirmModal.action === "vincular" ? "✅ Ativar Módulo" : "🔴 Desativar Módulo"}
            </h3>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6, marginBottom: 20 }}>
              {confirmModal.action === "vincular"
                ? `Tem certeza que deseja ATIVAR o módulo "${confirmModal.moduloNome}" para o tenant "${confirmModal.tenantNome}" (ID #${confirmModal.tenantId})?`
                : `Tem certeza que deseja DESATIVAR o módulo "${confirmModal.moduloNome}" para o tenant "${confirmModal.tenantNome}" (ID #${confirmModal.tenantId})? Os dados não serão excluídos.`
              }
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmModal(null)} style={{ padding: "10px 20px", border: "1px solid #ddd", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
              <button
                onClick={() => confirmModal.action === "vincular" ? handleVincular(confirmModal.tenantId, confirmModal.moduloSlug) : handleDesvincular(confirmModal.tenantId, confirmModal.moduloSlug)}
                disabled={actionLoading}
                style={{
                  padding: "10px 20px",
                  background: confirmModal.action === "vincular" ? "#22c55e" : "#ef4444",
                  color: "#fff", border: "none", borderRadius: 8, cursor: actionLoading ? "wait" : "pointer", fontWeight: 700, fontSize: 13
                }}
              >
                {actionLoading ? "Processando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN APP
   ══════════════════════════════════════════════ */

export default function App() {
  const [admin, setAdmin] = useState(getSession);
  const [page, setPage] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((title, sub, icon, color) => {
    const id = Date.now();
    setToasts(t => [...t, { id, title, sub, icon, color }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        apiFetch("/api/admin/stats"),
        apiFetch("/api/admin/tenants-full"),
      ]);
      setStats(s);
      setTenants(t);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (admin) loadData();
  }, [admin, loadData]);

  const handleLogout = () => { clearSession(); setAdmin(null); };

  if (!admin) return <><GlobalStyles /><LoginPage onLogin={setAdmin} /></>;

  return (
    <>
      <GlobalStyles />
      <ToastContainer toasts={toasts} />
      <button className="adm-burger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu" style={{ display: "none", position: "fixed", top: 12, left: 12, zIndex: 9000, width: 44, height: 44, borderRadius: 12, background: "#1e2330", color: "#fff", border: "none", fontSize: 20, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.25)", alignItems: "center", justifyContent: "center" }}>☰</button>
      {menuOpen && <div className="adm-overlay" onClick={() => setMenuOpen(false)} style={{ display: "none" }} />}
      <Sidebar page={page} setPage={(id) => { setPage(id); setMenuOpen(false); }} admin={admin} onLogout={handleLogout} mobileOpen={menuOpen} />
      <div className="adm-main" style={{ marginLeft: 214, padding: "30px 36px", minHeight: "100vh" }}>
        {page === "dashboard" && <Dashboard stats={stats} loading={loading} />}
        {page === "reunioes" && <ReunioesPage addToast={addToast} />}
        {page === "empresas" && <EmpresasPage tenants={tenants} loading={loading} onReload={loadData} addToast={addToast} />}
        {page === "usuarios" && <UsuariosPage addToast={addToast} />}
        {page === "contratos" && <ContratosPage addToast={addToast} />}
        {page === "assinaturas" && <AssinaturasPage addToast={addToast} />}
        {page === "calendario" && <CalendarioPage addToast={addToast} />}
        {page === "propostas" && <PropostasPage addToast={addToast} />}
        {page === "financeiro" && <FinanceiroPage addToast={addToast} />}
        {page === "mapa" && <MapaEmpresas addToast={addToast} />}
        {page === "monitoramento" && <MonitoramentoPage addToast={addToast} />}
        {page === "modulos" && <ModulosPage addToast={addToast} />}
        {page === "ia" && <IAPage addToast={addToast} />}
        {page === "emails" && <EmailsPage addToast={addToast} />}
      </div>
    </>
  );
}
